import React, { useState, useRef, useCallback, useEffect, useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';
import { useDrop } from 'react-dnd';
import { Box, Typography, Slider, CircularProgress, Tooltip, IconButton, ButtonBase } from '@mui/material';
import StraightenIcon from '@mui/icons-material/Straighten';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { getSeriesCount, getSeriesGeometry, presentationStateApi } from '../api';
import { normalizePresentationState, serializePresentationState, presentationStateSyncKey } from '../presentationState/presentationStateSerializer';
import { loadSeriesPresentationState } from './studyOpenBundle';
import { createPresentationStateSaveCoordinator } from '../presentationState/presentationStateSaveCoordinator';
import { createPresentationRestoreCoordinator, getPresentationRestoreKey } from '../presentationState/presentationRestoreCoordinator';
import { visibleAnnotationsForSlice, visibleMeasurementsForSlice, visibleRoisForSlice } from '../presentationState/visiblePresentationState';
import { finalizeRoiForSlice } from '../presentationState/roiState';
import { chooseReportContextKind, selectReportContextSubjects } from '../reporting/reportContextSelection';
import { DEFAULT_OVERLAY_CONFIG, measurementLabelFontSize, normalizeOverlayConfig, overlayFieldVisible, overlayToolRailSafeInsets } from '../overlayConfig';
import { DEFAULT_ANNOTATION_APPEARANCE, normalizeAnnotationAppearance, cssFontFamilyForAnnotation } from '../annotationAppearance';
import { angleMeasurementLabelLayout, formatAngleMeasurementLabel, linearMeasurementLabelLayout } from '../measurementLabelLayout';
import { DEFAULT_TOOL_SHORTCUTS } from './viewerKeyboardShortcuts';
import { VIEWPORT_EDITABLE_KEY_GUARD_MARKER, isViewportEditableTarget } from './viewportKeyboardGuards';
import { WLContextMenu } from './WLPresets';
import { selectToolsForPlacement } from '../viewerToolLayoutPreferences';
import {
  VIEWPORT_BROWSER_WARMUP_SHARED_BUDGET_MARKER,
  createViewportBrowserWarmupCandidates,
  shouldOwnViewportBrowserWarmup,
} from './viewportBrowserWarmup';
import { createDeferredSeriesGeometryCoordinator, shouldDeferSeriesGeometry } from './viewportDeferredSeriesGeometry';
import { VIEWPORT_GEOMETRY_CINE_SUPPRESSION_MARKER, createViewportGeometryBroadcastCoordinator } from './viewportGeometryBroadcastCoordinator';
import { VIEWPORT_DIRECTION_REVERSAL_CANCEL_MARKER, VIEWPORT_LATEST_WINS_REQUEST_MARKER, createViewportInteractiveRequestCoordinator, isExpectedViewportImageSource } from './viewportInteractiveRequestCoordinator';
import { VIEWPORT_FOLLOWER_FRAME_COORDINATOR_MARKER, createViewportFollowerFrameCoordinator } from './viewportFollowerFrameCoordinator';
import { createLinkedFollowerPaintAuthority } from './viewportLinkedFollowerPaintAuthority';
import { cancelActiveViewportImageSettlement, displayDecodedViewportFrame, settleCompleteActiveViewportImage } from './viewportDirectDisplay';
import { clearAuthenticatedRenderedSliceDom, normalizeLiveSlice, publishAcceptedVisibleFrameDom, publishAuthenticatedRenderedSliceDom, publishLiveSliceDom, publishVisibleFrameDom, readCanonicalUnsignedDomAttribute, transferAcceptedVisibleFrameDom } from './viewportLiveSlice';
import { preferredInitialSlice, resolveDisplayFrameCount } from './seriesFrameCount';
import { createSeriesLoadGenerationCoordinator } from './seriesLoadGeneration';
import { computeImgRect, getCanvasLogicalSize } from './viewportCoordinate';
import { clampFloatingToolRailPosition, defaultFloatingToolRailPosition, floatingToolRailSafeBandAvailable } from './viewportToolRailLayout';
import { ViewportToolRailRenderBoundary } from './viewportToolRailRenderBoundary';
import { applyMarkupAppearancePatch, hitTestViewportMarkupAt, moveMeasurementEndpointTo, moveViewportMarkupBy, prepareViewportMarkupForDrag, resizeRoiRadiusTo, restoreMarkupAppearanceSnapshot } from './annotationSelection';
import ClinicalMarkupPropertiesPopover from './ClinicalMarkupPropertiesPopover';
import { VIEWPORT_REFERENCE_LINE_MARKER, VIEWPORT_TRIANGULATION_DRAG_MARKER, VIEWPORT_TRIANGULATION_MARKER, createGeometrySnapshot, createTriangulationPoint, referenceLineOverlayProjection, shouldHandleTriangulationMiddleClick, shouldClearTriangulationOnMiddleClick, shouldShowTriangulationPoint, shouldShowTriangulationSourceMarker, triangulationOverlayProjection } from './viewportTriangulation';
import { VIEWPORT_WL_EFFECTIVE_DEFAULT_MARKER, describeWindowLevelOverlay, formatWindowLevelDraft, normalizeViewportWindowLevel, resolveEffectiveDefaultWindowLevel } from './viewportWindowLevel';
import { VIEWPORT_WINDOW_LEVEL_DRAG_MARKER, createWindowLevelDragCoordinator } from './windowLevelDragCoordinator';
import { PET_SLICE_THICKNESS_HELP_MARKER, PET_SLICE_THICKNESS_HELP_TEXT, PET_SLICE_THICKNESS_MARKER, PET_SLICE_THICKNESS_OPTIONS_MM, buildPetSliceThicknessRenderUrl, isPetSeries, nativeSliceThicknessMm } from './petSliceThickness';
import { VIEWPORT_DOUBLE_CLICK_MAXIMIZE_MARKER } from './viewportMaximize';
import { beginViewportLayoutTransition } from '../viewportLayoutTransition';
import { VIEWPORT_WHEEL_DISTANCE_SCROLL_MARKER, VIEWPORT_WHEEL_PIXELS_PER_SLICE, applyViewportWheelBurstDamping, consumeViewportWheelSteps, normalizeViewportWheelDelta } from './viewportScrollWheel';
import {
  createViewportBurstProjectionPlan,
} from './viewportBurstProjection';
import { createViewportWheelInputCausalityTracker } from './viewportWheelInputCausality';
import { recordViewportRender } from './viewportRenderProbe';
import { emptyTriangulationDragSnapshot, emptyTriangulationDragSubscribe } from './viewportTriangulationDragChannel';
import { createViewportTriangulationFollowerCadence } from './viewportTriangulationFollowerCadence';
import { emptyReferenceGeometrySnapshot, emptyReferenceGeometrySubscribe } from './viewportReferenceGeometryChannel';
import { VIEWPORT_REQUEST_ORIGIN, isLocalLinkedRequest } from './viewportPublicationPolicy';
import { nextFollowerRetryDelayMs, ownsCurrentFollowerCommandGeneration } from './viewportFollowerRequestAuthority';
import { nextFirstViewportImageRetryDelayMs } from './viewportFirstImageRetry';
import { applyRoiStatsIfOwned } from './roiStatsOwnership';
import {
  acceptViewportCineRenderPublication,
  acceptViewportWindowLevelPaint,
  acceptViewportWindowLevelRenderPublication,
  beginViewportCineInteraction,
  beginViewportEvidenceDecodedCacheHit,
  beginViewportEvidenceNetworkRequest,
  beginViewportWindowLevelInteraction,
  cancelViewportCineInteraction,
  cancelViewportWindowLevelInteraction,
  clearViewportCineWheelActivityForFrame,
  consumeViewportCineInputForCoordinator,
  consumeViewportWindowLevelInputForCoordinator,
  createViewportCineBrowserClock,
  createViewportCineRenderBinding,
  createViewportWindowLevelBrowserClock,
  failViewportWindowLevelInteraction,
  installViewportEvidenceBrowserBenchmarkBridge,
  isViewportEvidenceCinePhaseActive,
  isViewportEvidenceNetworkBridgeActive,
  isViewportCineInteractionActive,
  observeViewportCineInput,
  observeViewportCinePaint,
  observeViewportCineWheelActivity,
  observeViewportCineWheelActivityForFrame,
  observeViewportCineZeroDelta,
  recordViewportCineWheelEffect,
  reserveViewportCinePendingRenderOrdinal,
  observeViewportWindowLevelInput,
  releaseViewportWindowLevelInteraction,
  settleViewportWindowLevelInteraction,
} from './viewportEvidenceSessionBridge';
import { createCineEvidencePreloadContext } from './cineResourceGovernor';
import { linkedIntentLeadFrameLimit } from './viewportLinkedIntentLead';
import axios from 'axios';

const EMPTY_REFERENCE_FRACTIONS = Object.freeze([]);

export const replaceViewportNativeWheelBinding = (bindingRef, node, handler) => {
  const prior = bindingRef.current;
  if (prior?.node && prior?.handler) prior.node.removeEventListener('wheel', prior.handler);
  if (node && typeof handler === 'function') {
    node.addEventListener('wheel', handler, { passive: false });
    bindingRef.current = { node, handler };
  } else {
    bindingRef.current = null;
  }
};

installViewportEvidenceBrowserBenchmarkBridge();

const ITEM_TYPE = 'SERIES';
const TOOL_RAIL_PROFILED_SHORTCUT_MARKER = 'KAIROS_TOOL_RAIL_PROFILED_SHORTCUTS';
const VIEWPORT_TOOL_RAIL_DOCK_TOGGLE_MARKER = 'KAIROS_QOL_VIEWPORT_TOOL_RAIL_DOCK_TOGGLE';
const VIEWPORT_TOOL_RAIL_BELOW_ATTRIBUTES_MARKER = 'KAIROS_QOL_VIEWPORT_TOOL_RAIL_BELOW_ATTRIBUTES';
const VIEWPORT_TOOL_RAIL_DOCKED_DEFAULT_MARKER = 'KAIROS_QOL_VIEWPORT_TOOL_RAIL_DOCKED_DEFAULT';
const VIEWPORT_ACTIVE_SENTINEL_TOP_PLACEMENT_MARKER = 'KAIROS_ACTIVE_VIEWPORT_SENTINEL_TOP_CENTER_NEAR_SERIES';
const VIEWPORT_FULL_RESTORE_RIGHT_PLACEMENT_MARKER = 'KAIROS_QOL3_FULL_VIEWPORT_RESTORE_RIGHT_CENTER_GAP';
const VIEWPORT_SCROLL_IDLE_PRELOAD_MARKER = 'KAIROS_VIEWPORT_SCROLL_IDLE_PRELOAD';
const VIEWPORT_INTERACTIVE_PROJECTED_PRELOAD_MARKER = 'KAIROS_VIEWPORT_INTERACTIVE_PROJECTED_PRELOAD';
const VIEWPORT_SCRUB_LATEST_ONLY_MARKER = 'KAIROS_VIEWPORT_SCRUB_LATEST_ONLY';
const VIEWPORT_ACTIVE_SERIES_BROWSER_CACHE_MARKER = 'KAIROS_VIEWPORT_ACTIVE_SERIES_BROWSER_CACHE';
const VIEWPORT_SCROLL_IDLE_PRELOAD_DELAY_MS = 150;
const VIEWPORT_RECENT_PROJECTED_PRELOAD_SUPPRESS_MS = 300;
const VIEWPORT_BURST_PROJECTED_PRELOAD_TTL_MS = 2000;
const VIEWPORT_BURST_PROJECTED_MIN_STEPS = 64;
const VIEWPORT_SCRUB_COLD_DISPLAY_INTERVAL_MS = 20;
const VIEWPORT_SCRUB_STATE_INTERVAL_MS = 20;
const VIEWPORT_FOLLOWER_IMAGE_SETTLEMENT_TIMEOUT_MS = 1000;

const measurementCalculationBackdrop = 'rgba(0,0,0,0.38)';
const VIEWPORT_DOCKED_TOOL_RAIL_TOP = 112;
const VIEWPORT_DOCKED_TOOL_RAIL_BOTTOM_GUTTER = 16;
const VIEWPORT_FLOATING_TOOL_RAIL_GUTTER = 8;
const VIEWPORT_FLOATING_TOOL_RAIL_TOP_INSET = 112;
const VIEWPORT_FLOATING_TOOL_RAIL_BOTTOM_INSET = 48;
const VIEWPORT_WINDOW_LEVEL_INLINE_EDIT_MARKER = 'KAIROS_QOL_VIEWPORT_WL_INLINE_OVERLAY_ATTRIBUTE';

const resolveWindowLevelEvidenceTestInjection = candidate => {
  if (process.env.NODE_ENV !== 'test') return null;
  try {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)
        || !Object.isFrozen(candidate)) return null;
    const keys = Reflect.ownKeys(candidate);
    if (keys.length !== 2 || !keys.includes('clock') || !keys.includes('eventForNativeEvent')) return null;
    const clockDescriptor = Object.getOwnPropertyDescriptor(candidate, 'clock');
    const eventDescriptor = Object.getOwnPropertyDescriptor(candidate, 'eventForNativeEvent');
    if (!clockDescriptor || !Object.prototype.hasOwnProperty.call(clockDescriptor, 'value')
        || !eventDescriptor || !Object.prototype.hasOwnProperty.call(eventDescriptor, 'value')
        || typeof eventDescriptor.value !== 'function') return null;
    return Object.freeze({
      clock: clockDescriptor.value,
      eventForNativeEvent: eventDescriptor.value,
    });
  } catch (_) {
    return null;
  }
};

const isLinkedAuthorityRequest = requestOrigin => (
  isLocalLinkedRequest(requestOrigin)
  || requestOrigin === VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER
  || requestOrigin === VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER_RETRY
);


const formatToolRailShortcut = (key) => String(key || '').toUpperCase();

const ToolRailHotkeyBadge = ({ hotkey, active, color = '#6eefff' }) => (
  <Typography
    component="span"
    className="toolRailHotkeyBadge"
    aria-hidden="true"
    sx={{
      position: 'absolute',
      right: -4,
      top: -4,
      minWidth: 12,
      height: 12,
      lineHeight: '12px',
      borderRadius: '6px',
      px: 0.25,
      textAlign: 'center',
      fontSize: 8,
      fontWeight: 800,
      letterSpacing: '0.02em',
      fontFamily: 'monospace',
      color: active ? '#00141a' : 'rgba(205,245,255,0.78)',
      background: active ? color : 'rgba(6,18,28,0.88)',
      border: active ? '1px solid rgba(255,255,255,0.72)' : '1px solid rgba(110,239,255,0.24)',
      boxShadow: active ? `0 0 8px ${color}` : '0 2px 6px rgba(0,0,0,0.32)',
      pointerEvents: 'none',
    }}
  >
    {hotkey}
  </Typography>
);

const ToolRailSectionHeader = ({ id, label, color, expanded, onToggle, mt = 0, statusLabel = '' }) => {
  const announceStatus = statusLabel && !/^\d+$/.test(statusLabel) ? statusLabel : '';
  return (
  <Box
    component="button"
    type="button"
    className="toolRailSectionToggle"
    data-testid={`tool-rail-section-toggle-${id}`}
    data-tool-rail-collapsed-state="KAIROS_TOOL_RAIL_COLLAPSED_STATE"
    aria-label={announceStatus ? `${label} tools; ${announceStatus}` : `${label} tools`}
    aria-expanded={expanded}
    aria-controls={`tool-rail-section-${id}`}
    onClick={() => onToggle(id)}
    sx={{
      width: 80,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 0.4,
      color,
      bgcolor: 'transparent',
      border: 0,
      borderLeft: `2px solid ${color}`,
      borderRadius: '3px',
      mt,
      px: 0.25,
      pl: 0.45,
      py: 0.15,
      cursor: 'pointer',
      fontFamily: 'monospace',
      '&:hover': { bgcolor: 'rgba(110,239,255,0.08)' },
      '&:focus-visible': { outline: `1px solid ${color}`, outlineOffset: 1 },
    }}
  >
    <Typography component="span" sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', fontFamily: 'monospace', lineHeight: 1.1 }}>
      {label}
    </Typography>
    {statusLabel ? (
      <Typography
        component="span"
        aria-hidden="true"
        className="toolRailSectionStatusBadge"
        sx={{
          ml: 'auto', px: 0.35, py: 0.05, borderRadius: '999px',
          color: '#001418', bgcolor: color, fontSize: 6.5, fontFamily: 'monospace', fontWeight: 900,
          letterSpacing: 0.4, maxWidth: 28, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {statusLabel}
      </Typography>
    ) : null}
    <Typography component="span" aria-hidden="true" sx={{ fontSize: 8, fontWeight: 900, lineHeight: 1, opacity: 0.86 }}>
      {expanded ? '−' : '+'}
    </Typography>
  </Box>
  );
};

const ToolRailSectionBody = ({ id, expanded, children }) => (
  <Box
    id={`tool-rail-section-${id}`}
    className="toolRailSectionBody"
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      alignItems: 'center',
      justifyItems: 'center',
      columnGap: '6px',
      rowGap: '4px',
      width: 80,
      maxHeight: expanded ? 520 : 0,
      opacity: expanded ? 1 : 0,
      overflow: 'hidden',
      pointerEvents: expanded ? 'auto' : 'none',
      transition: 'max-height 150ms ease, opacity 120ms ease',
      '& > :not(button)': { gridColumn: '1 / -1' },
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    }}
  >
    {children}
  </Box>
);

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const ANNOTATION_COLORS = ['#ff9800', '#00e5ff', '#ffeb3b', '#4caf50', '#e040fb', '#ff5252', '#ffffff'];


const annotationControlButtonSx = {
  minWidth: 0,
  minHeight: 0,
  p: 0,
  borderRadius: '3px',
  lineHeight: 1,
  fontFamily: 'monospace',
  '&:focus-visible': { outline: '1px solid #6eefff', outlineOffset: 1 },
};

const AnnotationAppearanceButton = ({ label, pressed, onClick, children, sx = {}, ...props }) => (
  <ButtonBase
    type="button"
    aria-label={label}
    aria-pressed={typeof pressed === 'boolean' ? pressed : undefined}
    onClick={onClick}
    className="annotationAppearanceButton profile-backed-annotation-button"
    sx={{ ...annotationControlButtonSx, ...sx }}
    {...props}
  >
    {children}
  </ButtonBase>
);

const rectBoundsFromPoints = (pts) => {
  const [a, b] = pts || [];
  return {
    left: Math.min(a?.x ?? 0, b?.x ?? 0),
    right: Math.max(a?.x ?? 0, b?.x ?? 0),
    top: Math.min(a?.y ?? 0, b?.y ?? 0),
    bottom: Math.max(a?.y ?? 0, b?.y ?? 0),
  };
};

const boundsToPoints = ({ left, right, top, bottom }) => [
  { x: clamp01(left), y: clamp01(top) },
  { x: clamp01(right), y: clamp01(bottom) },
];

const getAnnotationHandlePoints = (ann) => {
  if (!ann) return [];
  if (ann.type === 'text' || ann.type === 'level') {
    return ann.position ? [{ key: 'position', pt: ann.position, cursor: 'move' }] : [];
  }
  if (ann.type === 'arrow' && ann.points?.a && ann.points?.b) {
    return [
      { key: 'a', pt: ann.points.a, cursor: 'grab' },
      { key: 'b', pt: ann.points.b, cursor: 'grab' },
    ];
  }
  if ((ann.type === 'angle' || ann.type === 'cobb') && Array.isArray(ann.points)) {
    return ann.points.map((pt, i) => ({ key: `p${i}`, pt, cursor: 'grab' }));
  }
  if ((ann.type === 'rect' || ann.type === 'ellipse') && Array.isArray(ann.points) && ann.points.length >= 2) {
    const { left, right, top, bottom } = rectBoundsFromPoints(ann.points);
    const cx = (left + right) / 2;
    const cy = (top + bottom) / 2;
    return [
      { key: 'nw', pt: { x: left, y: top }, cursor: 'nwse-resize' },
      { key: 'n', pt: { x: cx, y: top }, cursor: 'ns-resize' },
      { key: 'ne', pt: { x: right, y: top }, cursor: 'nesw-resize' },
      { key: 'e', pt: { x: right, y: cy }, cursor: 'ew-resize' },
      { key: 'se', pt: { x: right, y: bottom }, cursor: 'nwse-resize' },
      { key: 's', pt: { x: cx, y: bottom }, cursor: 'ns-resize' },
      { key: 'sw', pt: { x: left, y: bottom }, cursor: 'nesw-resize' },
      { key: 'w', pt: { x: left, y: cy }, cursor: 'ew-resize' },
    ];
  }
  return [];
};

const resizeAnnotationHandle = (ann, handleKey, pt) => {
  const nextPt = { x: clamp01(pt.x), y: clamp01(pt.y) };
  if (handleKey === 'position' && ann.position) return { ...ann, position: nextPt };
  if (ann.type === 'arrow' && ann.points?.a && ann.points?.b && (handleKey === 'a' || handleKey === 'b')) {
    return { ...ann, points: { ...ann.points, [handleKey]: nextPt } };
  }
  if ((ann.type === 'angle' || ann.type === 'cobb') && Array.isArray(ann.points) && /^p\d+$/.test(handleKey)) {
    const idx = Number(handleKey.slice(1));
    return { ...ann, points: ann.points.map((p, i) => (i === idx ? nextPt : p)) };
  }
  if ((ann.type === 'rect' || ann.type === 'ellipse') && Array.isArray(ann.points) && ann.points.length >= 2) {
    const bounds = rectBoundsFromPoints(ann.points);
    if (handleKey.includes('n')) bounds.top = Math.min(nextPt.y, bounds.bottom);
    if (handleKey.includes('s')) bounds.bottom = Math.max(nextPt.y, bounds.top);
    if (handleKey.includes('w')) bounds.left = Math.min(nextPt.x, bounds.right);
    if (handleKey.includes('e')) bounds.right = Math.max(nextPt.x, bounds.left);
    return { ...ann, points: boundsToPoints(bounds) };
  }
  return ann;
};


const cobbAngle = (pts) => {
  if (!pts || pts.length < 4) return null;
  const [a, b, c, d] = pts;
  const ang1 = Math.atan2(b.y - a.y, b.x - a.x);
  const ang2 = Math.atan2(d.y - c.y, d.x - c.x);
  let deg = Math.abs((ang2 - ang1) * 180 / Math.PI);
  while (deg > 180) deg -= 180;
  return deg > 90 ? 180 - deg : deg;
};

const clearAnnotationCanvas = (canvas) => {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const { width: logicalW, height: logicalH } = getCanvasLogicalSize(canvas);
  ctx.clearRect(0, 0, logicalW, logicalH);
};

const ViewportSliceSlider = React.memo(React.forwardRef(({
  value,
  maxSlice,
  disabled,
  onLiveChange,
  onCommit,
}, ref) => {
  const [liveValue, setLiveValue] = useState(() => normalizeLiveSlice(value, maxSlice));

  useEffect(() => {
    setLiveValue(normalizeLiveSlice(value, maxSlice));
  }, [value, maxSlice]);

  React.useImperativeHandle(ref, () => ({
    setLiveSlice: next => setLiveValue(normalizeLiveSlice(next, maxSlice)),
  }), [maxSlice]);

  return (
    <Slider
      value={liveValue}
      min={0}
      max={maxSlice}
      step={1}
      disabled={disabled}
      onChange={(_, next) => {
        const clamped = normalizeLiveSlice(Array.isArray(next) ? next[0] : next, maxSlice);
        setLiveValue(clamped);
        onLiveChange?.(clamped);
      }}
      onChangeCommitted={(_, next) => {
        const clamped = normalizeLiveSlice(Array.isArray(next) ? next[0] : next, maxSlice);
        setLiveValue(clamped);
        onCommit?.(clamped);
      }}
      size="small"
      sx={{
        color: '#00bcd4', height: 2, padding: '5px 0',
        '& .MuiSlider-thumb': { width: 8, height: 8, '&:hover': { boxShadow: '0 0 0 6px rgba(0,188,212,0.2)' } },
        '& .MuiSlider-rail': { bgcolor: '#222' },
      }}
    />
  );
}));
ViewportSliceSlider.displayName = 'ViewportSliceSlider';

// Reads the latest triangulation drag frame from the imperative channel with
// the durable React anchor as fallback. Channel payloads are always at least
// as new as committed state (every commit also publishes), so precedence is
// channel-first without a version race.
const useTriangulationDragPoint = (channel, anchorPoint) => {
  const subscribe = channel && typeof channel.subscribe === 'function' ? channel.subscribe : emptyTriangulationDragSubscribe;
  const getSnapshot = channel && typeof channel.getSnapshot === 'function' ? channel.getSnapshot : emptyTriangulationDragSnapshot;
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  return snapshot?.payload?.point || anchorPoint || null;
};

// Follower crosshair, isolated so per-frame drag repaints touch only this tiny
// SVG group instead of the whole Viewport tree. Markup and projection semantics
// are identical to the historical inline render.
export const TriangulationCrosshairLayer = React.memo(({ channel, viewportIndex, series, overlayImgRect, overlayW, overlayH, currentSliceGeometry, naturalSize, anchorPoint }) => {
  const point = useTriangulationDragPoint(channel, anchorPoint);
  const activeTriangulationPoint = shouldShowTriangulationPoint(point, viewportIndex, series) ? point : null;
  const triangulationProjection = activeTriangulationPoint && overlayImgRect ? triangulationOverlayProjection(activeTriangulationPoint, overlayImgRect, {
    targetGeometry: currentSliceGeometry,
    targetDimensions: naturalSize,
  }) : null;
  const triangulationScreenPoint = triangulationProjection ? { x: triangulationProjection.x, y: triangulationProjection.y } : null;
  if (!triangulationScreenPoint) return null;
  return (
    <g
      data-kairos-marker={VIEWPORT_TRIANGULATION_MARKER}
      data-testid="viewport-triangulation-crosshair"
      data-triangulation-mode={triangulationProjection?.mode || 'normalized-fallback'}
      aria-label={`Triangulation from viewport ${activeTriangulationPoint.sourceViewportIndex + 1} (${triangulationProjection?.mode === 'dicom-geometry' ? 'DICOM geometry' : 'normalized fallback'})`}
    >
      <line x1="0" y1={triangulationScreenPoint.y} x2={overlayW} y2={triangulationScreenPoint.y} stroke="rgba(0,0,0,0.72)" strokeWidth="2" />
      <line x1={triangulationScreenPoint.x} y1="0" x2={triangulationScreenPoint.x} y2={overlayH} stroke="rgba(0,0,0,0.72)" strokeWidth="2" />
      <line x1="0" y1={triangulationScreenPoint.y} x2={overlayW} y2={triangulationScreenPoint.y} stroke="#ffeb3b" strokeWidth="0.9" strokeDasharray="5 5" opacity="0.9" />
      <line x1={triangulationScreenPoint.x} y1="0" x2={triangulationScreenPoint.x} y2={overlayH} stroke="#ffeb3b" strokeWidth="0.9" strokeDasharray="5 5" opacity="0.9" />
      <circle cx={triangulationScreenPoint.x} cy={triangulationScreenPoint.y} r="6" fill="none" stroke="rgba(0,0,0,0.78)" strokeWidth="2" />
      <circle cx={triangulationScreenPoint.x} cy={triangulationScreenPoint.y} r="4" fill="none" stroke="#ffeb3b" strokeWidth="1.1" />
      <text x={Math.min(triangulationScreenPoint.x + 8, overlayW - 74)} y={Math.max(triangulationScreenPoint.y - 8, 12)} fontSize="8.5" fontFamily="monospace" fill="#ffeb3b" stroke="rgba(0,0,0,0.82)" strokeWidth="1.4" paintOrder="stroke">TRIANG VP{activeTriangulationPoint.sourceViewportIndex + 1}</text>
      <text x={Math.min(triangulationScreenPoint.x + 8, overlayW - 54)} y={Math.min(Math.max(triangulationScreenPoint.y + 10, 22), overlayH - 4)} fontSize="7" fontFamily="monospace" fill="rgba(255,235,59,0.72)" stroke="rgba(0,0,0,0.72)" strokeWidth="1.1" paintOrder="stroke">{triangulationProjection?.mode === 'dicom-geometry' ? 'GEOM' : 'FALLBACK'}</text>
    </g>
  );
});
TriangulationCrosshairLayer.displayName = 'TriangulationCrosshairLayer';

// Source-viewport drag marker, isolated for the same reason: while the user
// drags, the marker follows the pointer through the channel without a full
// Viewport render per frame. Pointer handlers stay owned by Viewport.
export const TriangulationSourceMarkerLayer = React.memo(({ channel, viewportIndex, series, overlayImgRect, overlayW, currentSliceGeometry, naturalSize, anchorPoint, viewportFrozen, hasAcceptedClinicalSlice, onPointerDown, onAuxClick, onPointerMove, onPointerUp, onPointerCancel }) => {
  const point = useTriangulationDragPoint(channel, anchorPoint);
  const sourceTriangulationPoint = shouldShowTriangulationSourceMarker(point, viewportIndex, series) ? point : null;
  const sourceTriangulationScreenPoint = sourceTriangulationPoint && overlayImgRect
    ? triangulationOverlayProjection(sourceTriangulationPoint, overlayImgRect, { targetGeometry: currentSliceGeometry, targetDimensions: naturalSize })
    : null;
  if (!sourceTriangulationScreenPoint) return null;
  return (
    <Box
      data-kairos-marker={VIEWPORT_TRIANGULATION_DRAG_MARKER}
      data-testid="viewport-triangulation-source-marker"
      data-triangulation-mode={sourceTriangulationScreenPoint?.mode || 'normalized-fallback'}
      data-triangulation-cx={Math.round(sourceTriangulationScreenPoint.x)}
      data-triangulation-cy={Math.round(sourceTriangulationScreenPoint.y)}
      aria-label={`Drag triangulation source marker for viewport ${viewportIndex + 1}`}
      onPointerDown={onPointerDown}
      onAuxClick={onAuxClick}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      sx={{
        position: 'absolute',
        left: `${sourceTriangulationScreenPoint.x}px`,
        top: `${sourceTriangulationScreenPoint.y}px`,
        width: 22,
        height: 22,
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        zIndex: 121,
        pointerEvents: viewportFrozen || !hasAcceptedClinicalSlice ? 'none' : 'auto',
        cursor: viewportFrozen ? 'not-allowed' : 'grab',
        touchAction: 'none',
        bgcolor: 'rgba(255,235,59,0.12)',
        border: '2px dashed #ffeb3b',
        boxShadow: '0 0 0 2px rgba(0,0,0,0.72), 0 0 12px rgba(255,235,59,0.25)',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 5,
          height: 5,
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          bgcolor: '#ffeb3b',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.78)',
        },
      }}
    />
  );
});
TriangulationSourceMarkerLayer.displayName = 'TriangulationSourceMarkerLayer';

// Reference geometry is a durable external snapshot because follower slices may
// change much faster than the rest of the diagnostic viewport. Only this small
// SVG layer subscribes; follower geometry must never re-render the source image,
// tools, annotations, or decode/paint state.
const ReferenceLineLayer = React.memo(({
  channel,
  enabled,
  viewportIndex,
  studyInstanceUID,
  currentSliceGeometry,
  naturalSize,
  overlayImgRect,
  overlayW,
  overlayH,
}) => {
  const subscribe = enabled && channel?.subscribe
    ? channel.subscribe
    : emptyReferenceGeometrySubscribe;
  const getSnapshot = enabled && channel?.getSnapshot
    ? channel.getSnapshot
    : emptyReferenceGeometrySnapshot;
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!enabled || !currentSliceGeometry || !overlayImgRect) return null;

  const referenceLineProjections = Object.values(snapshot.geometry || {})
    .filter(ref => ref?.viewportIndex !== viewportIndex && ref?.studyInstanceUID === studyInstanceUID)
    .map(ref => ({
      ref,
      projection: referenceLineOverlayProjection(ref.geometry, currentSliceGeometry, overlayImgRect, {
        sourceDimensions: ref.dimensions,
        targetDimensions: naturalSize,
        label: `V${(ref.viewportIndex ?? 0) + 1}`,
      }),
    }))
    .filter(item => item.projection);

  return <>
    {referenceLineProjections.map(({ ref, projection }) => (
      <g
        key={`reference-line-${ref.viewportIndex}-${ref.seriesInstanceUID}-${ref.slice}`}
        data-kairos-marker={VIEWPORT_REFERENCE_LINE_MARKER}
        data-testid="viewport-reference-line"
        data-reference-line-mode={projection.mode}
        data-reference-source-viewport={ref.viewportIndex}
        aria-label={`Reference line from viewport ${(ref.viewportIndex ?? 0) + 1} (${projection.mode === 'dicom-geometry' ? 'DICOM geometry' : 'hidden fallback'})`}
      >
        <line x1={projection.x1} y1={projection.y1} x2={projection.x2} y2={projection.y2} stroke="rgba(0,0,0,0.76)" strokeWidth="2" />
        <line x1={projection.x1} y1={projection.y1} x2={projection.x2} y2={projection.y2} stroke="#ff9800" strokeWidth="1" strokeDasharray="7 4" opacity="0.92" />
        <text x={Math.max(4, Math.min(projection.x1 + 6, overlayW - 42))} y={Math.max(12, Math.min(projection.y1 - 4, overlayH - 6))} fontSize="8" fontFamily="monospace" fill="#ffb74d" stroke="rgba(0,0,0,0.82)" strokeWidth="1.2" paintOrder="stroke">REF {projection.label}</text>
      </g>
    ))}
  </>;
});
ReferenceLineLayer.displayName = 'ReferenceLineLayer';

const sameRoiStatsRequestOwner = (left, right) => Boolean(left && right
  && left.seriesLoadGeneration === right.seriesLoadGeneration
  && left.studyInstanceUID === right.studyInstanceUID
  && left.seriesInstanceUID === right.seriesInstanceUID
  && left.slice === right.slice
  && left.roiId === right.roiId
  && left.roiSubjectToken === right.roiSubjectToken);

const Viewport = ({ index, onDrop, initialSeries = null, toolMode = 'scroll', initialWL = null, slotLabel = '', onSliceChange = null, onLinkedSliceRequest = null, linkedScrollFraction = null, linkedScrollCommandId = null, linkedScrollCommandChannel = null, linkedScrollEnabled = false, linkedAuthorityEpoch = 0, referenceFractions = EMPTY_REFERENCE_FRACTIONS, onToolComplete = null, onSeriesLoad = null, onWindowLevelChange = null, onViewportFocus = null, onReportContextChange = null, syncCommand = null, overlayConfig = DEFAULT_OVERLAY_CONFIG, annotationAppearance = DEFAULT_ANNOTATION_APPEARANCE, onAnnotationAppearanceChange = null, studyInfo = null, comparisonContext = null, isActiveViewport = false, keyboardToolShortcuts = DEFAULT_TOOL_SHORTCUTS, toolLayoutPrefs = null, onViewportZoomChange = null, onViewportFreezeChange = null, toolCompletionBehavior = 'scroll', escapeReturnsToScroll = true, triangulationPoint = null, onTriangulationPoint = null, triangulationDragChannel = null, onViewportGeometryChange = null, referenceGeometryChannel = null, showRefLines = false, studyInstanceUID = null, keyObjects = [], onMarkKeyObject = null, isViewportMaximized = false, viewportLayoutMode = 'grid', onViewportDoubleClick = null, cineResourceGovernor = null, viewportInstanceEpoch = 0, cacheEpoch = null, onInteractionPerformanceEvent = null, windowLevelEvidenceTestInjection = null, cineWheelEvidenceTestInjection = null }) => {
  recordViewportRender(index);
  // Accepted-visible publication is independent of LINK; requested transport is not.
  const effectiveOnLinkedSliceRequest = linkedScrollEnabled ? onLinkedSliceRequest : null;
  const effectiveLinkedScrollFraction = linkedScrollEnabled ? linkedScrollFraction : null;
  const [series, setSeries] = useState(null);
  const [slice, setSlice] = useState(null);
  const [maxSlice, setMaxSlice] = useState(99);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1.0);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [canvasCssSize, setCanvasCssSize] = useState({ width: 0, height: 0 });
  const [, setOverlayLayoutVersion] = useState(0);
  const [imgError, setImgError] = useState(false);
  const [windowLevel, setWindowLevel] = useState({ ww: 0, wc: 0 });
  const [wlDraft, setWlDraft] = useState({ ww: '0', wc: '0' });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef(null); // { mode: 'scroll'|'wl', ... }
  const roiStartRef = useRef(null);
  const triangulationDragStateRef = useRef(null);
  const triangulationDragFrameRef = useRef(null);
  const triangulationDragPointerIdRef = useRef(null);
  const triangulationDragCaptureTargetRef = useRef(null);
  const triangulationDragGestureRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const imgSrcRef = useRef(null);
  const acceptedImgSrcRef = useRef(null);
  const acceptedVisibleSurfaceRef = useRef(null);
  const pendingVisibleDomSettlementRef = useRef(null);
  const directDomSettlementCancelRef = useRef(null);
  const viewportMountedRef = useRef(false);
  const pendingEvidenceOperationRef = useRef(null);
  const acceptedEvidenceOperationRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y } for context menu
  const [pixelSpacing, setPixelSpacing] = useState(null);
  const [seriesGeometry, setSeriesGeometry] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [pendingMeasure, setPendingMeasure] = useState(null); // { a: {x,y} } or null
  const [rois, setRois] = useState([]); // { id, cx, cy, rx, ry, stats }
  const [pendingRoi, setPendingRoi] = useState(null); // { cx, cy, rx, ry } during drag
  const [annotations, setAnnotations] = useState([]); // future persistence shape: { id, type, slice, points/position, text, color, lineWidth, fontSize }
  const [selectedMarkup, setSelectedMarkup] = useState(null); // { kind: annotation|measurement|roi, id }
  const selectedAnnotationId = selectedMarkup?.kind === 'annotation' ? selectedMarkup.id : null;
  const [hoveredAnnotationId, setHoveredAnnotationId] = useState(null);
  const [annotationCommitRevision, setAnnotationCommitRevision] = useState(0);
  const roiStatsSavePendingRef = useRef(null);
  const [annotationStyle, setAnnotationStyle] = useState(() => normalizeAnnotationAppearance(annotationAppearance));
  const [pendingArrow, setPendingArrow] = useState(null); // { a, b } during two-click arrow placement
  const [pendingFreehand, setPendingFreehand] = useState(null); // normalized points during drag
  const [pendingRect, setPendingRect] = useState(null); // { a, b } during drag
  const [pendingEllipse, setPendingEllipse] = useState(null); // { a, b } during drag
  const [pendingAngle, setPendingAngle] = useState([]); // 3-click angle points
  const [pendingCobb, setPendingCobb] = useState([]); // 4-click Cobb angle points
  const [textPopover, setTextPopover] = useState(null); // { x, y, pt, mode } screen + normalized image coords
  const [localTool, setLocalTool] = useState(null); // null = use parent toolMode
  const [expandedToolRailSections, setExpandedToolRailSections] = useState({ roi: true, measure: true, annotate: true, draw: true, status: true });
  const [toolRailDocked, setToolRailDocked] = useState(true);
  const [toolRailPosition, setToolRailPosition] = useState(null);
  const [toolRailDragging, setToolRailDragging] = useState(false);
  React.useEffect(() => { if (toolLayoutPrefs?.dock?.docked !== undefined) setToolRailDocked(toolLayoutPrefs?.dock?.docked !== false); }, [toolLayoutPrefs?.dock?.docked]);
  const dockToolIds = React.useMemo(() => new Set(selectToolsForPlacement(toolLayoutPrefs, 'dock').map(tool => tool.id)), [toolLayoutPrefs]);
  const contextTools = React.useMemo(() => selectToolsForPlacement(toolLayoutPrefs, 'context'), [toolLayoutPrefs]);
  const contextToolIds = React.useMemo(() => new Set(contextTools.map(tool => tool.id)), [contextTools]);
  const contextMenuEnabled = toolLayoutPrefs?.context?.enabled !== false && contextToolIds.size > 0;
  const showDockTool = React.useCallback((toolId) => dockToolIds.has(toolId), [dockToolIds]);
  const profiledToolShortcuts = React.useMemo(
    () => ({ ...DEFAULT_TOOL_SHORTCUTS, ...(keyboardToolShortcuts || {}) }),
    [keyboardToolShortcuts]
  );
  const profiledToolShortcut = React.useCallback(
    (tool) => formatToolRailShortcut(profiledToolShortcuts[tool] || DEFAULT_TOOL_SHORTCUTS[tool] || ''),
    [profiledToolShortcuts]
  );
  const [naturalSize, setNaturalSize] = useState(null); // { width, height } — actual DICOM image dimensions
  const [propertyPopover, setPropertyPopover] = useState(null); // { anchor, target: { kind, item } }
  const propertyEditSessionRef = useRef(null); // { target, original, dirty }
  const [presentationVersion, setPresentationVersion] = useState(0);
  const [presentationSaveStatus, setPresentationSaveStatus] = useState('');
  const [presentationSaveError, setPresentationSaveError] = useState('');
  const presentationSaveAlert = presentationSaveError || '';
  const [viewportFrozen, setViewportFrozen] = useState(false);
  const freezeAuthorityEpochRef = useRef(0);
  // Render-time mirrors so the imperative triangulation drag subscriber can
  // apply freeze/clamp semantics identical to the React effect path without
  // re-subscribing per state change.
  const viewportFrozenRef = useRef(viewportFrozen);
  viewportFrozenRef.current = viewportFrozen;
  const toggleViewportFrozen = useCallback(() => {
    freezeAuthorityEpochRef.current += 1;
    // Keep the mirror exact through the click itself (not just the next
    // commit) so an imperative drag frame arriving between toggle and
    // re-render is judged against the same freeze state as the epoch it
    // would carry.
    viewportFrozenRef.current = !viewportFrozenRef.current;
    setViewportFrozen(prev => !prev);
  }, []);
  const [petSliceThicknessMm, setPetSliceThicknessMm] = useState(1);

  const boxRef = useRef(null);
  const wheelBindingRef = useRef(null);
  const interactionPerformanceOrdinalRef = useRef(0);
  const canvasRef = useRef(null);
  const scrubPreviewCanvasRef = useRef(null);
  const drawScrubPreviewImageRef = useRef(null);
  const imgRef = useRef(null);
  const viewportRootRef = useRef(null);
  const toolRailRef = useRef(null);
  const toolRailDragRef = useRef(null);
  const sliceSliderRef = useRef(null);
  const publishScrubSliceStateRef = useRef(null);
  const publishVisibleSliceStateRef = useRef(null);
  const deferredSeriesGeometryCoordinatorRef = useRef(null);
  if (!deferredSeriesGeometryCoordinatorRef.current) {
    deferredSeriesGeometryCoordinatorRef.current = createDeferredSeriesGeometryCoordinator();
  }
  useEffect(() => () => {
    deferredSeriesGeometryCoordinatorRef.current.cancel();
  }, []);

  const openWindowLevelContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (viewportFrozen) return;
    setCtxMenu({ x: e.clientX, y: e.clientY });
  }, [viewportFrozen]);
  const sliceRef = useRef(null); // accepted, clinically visible slice only; null until first accepted pixels
  const requestedSliceRef = useRef(null); // latest coalesced/requested target only
  const seriesRef = useRef(null);
  const sliceIntentAuthorityRef = useRef(Object.freeze({
    generation: 0,
    studyInstanceUID: null,
    seriesInstanceUID: null,
    slice: null,
  }));
  const claimSliceIntentAuthority = useCallback((target, targetSlice) => {
    if (!target?.studyInstanceUID || !target?.seriesInstanceUID
        || !Number.isSafeInteger(targetSlice) || targetSlice < 0) {
      return sliceIntentAuthorityRef.current;
    }
    const normalizedSlice = targetSlice;
    const current = sliceIntentAuthorityRef.current;
    if (current.studyInstanceUID === target.studyInstanceUID
        && current.seriesInstanceUID === target.seriesInstanceUID
        && current.slice === normalizedSlice) return current;
    const next = Object.freeze({
      generation: current.generation + 1,
      studyInstanceUID: target.studyInstanceUID,
      seriesInstanceUID: target.seriesInstanceUID,
      slice: normalizedSlice,
    });
    sliceIntentAuthorityRef.current = next;
    return next;
  }, []);
  const wlRef = useRef({ ww: 0, wc: 0, source: 'auto' });
  const windowLevelSourceRef = useRef('auto');
  const scrollPendingRef = useRef(0);
  const scrollPendingRapidBurstRef = useRef(false);
  const wheelInputCausalityRef = useRef(createViewportWheelInputCausalityTracker());
  const localLinkedCommandCausalityRef = useRef(null);
  const scrollFrameRef = useRef(null);
  const lastWheelEventAtRef = useRef(0);
  const cineWheelEventRef = useRef(null);
  const cineWheelActivityRef = useRef(null);
  const cineWheelInteractionRef = useRef(null);
  const cineWheelRenderRequestRef = useRef(null);
  const cineWheelPendingRenderOrdinalRef = useRef(null);
  const scrollDirectionRef = useRef(0); // +1 forward, -1 backward, 0 unknown
  const scrollIdlePreloadTimerRef = useRef(null);
  const scrubFrameRef = useRef(null);
  const scrubThrottleTimerRef = useRef(null);
  const scrubPendingSliceRef = useRef(null);
  const scrubActiveRef = useRef(false);
  const scrubDisplayPendingRef = useRef(false);
  const scrubLastDisplayAtRef = useRef(0);
  const scrubLastStateAtRef = useRef(0);
  const lastLinkedPublishedSliceRef = useRef(null);
  const linkedFollowerRetryTimerRef = useRef(null);
  const linkedFollowerRetryRef = useRef({ key: '', attempts: 0 });
  const lastLinkedScrollCommandIdRef = useRef(null);
  const lastLinkedAcceptedCommandIdRef = useRef(null);
  const latestLinkedIntentRef = useRef(null);
  const lastLinkedScrollAppliedRef = useRef(null);
  const linkedAuthorityEpochRef = useRef(linkedAuthorityEpoch);
  linkedAuthorityEpochRef.current = linkedAuthorityEpoch;
  const activeLinkedAuthorityRequestRef = useRef(null);
  const loadSliceRef = useRef(null);
  const firstSurfaceRecoveryRef = useRef(null);
  const linkedScrollFrameRef = useRef(null); // rAF-coalesced linked scroll image load
  const triangulationFollowerDispatchRef = useRef(null);
  const triangulationFollowerCadenceRef = useRef(null);
  if (!triangulationFollowerCadenceRef.current) {
    triangulationFollowerCadenceRef.current = createViewportTriangulationFollowerCadence({
      dispatch: command => triangulationFollowerDispatchRef.current?.(command),
    });
  }
  const followerFrameCoordinatorRef = useRef(null);
  const linkedFollowerPaintAuthorityRef = useRef(null);
  const dispatchFollowerCommandRef = useRef(null);
  const followerCommandGenerationRef = useRef(0);
  // Detached LINK transport ownership outlives coordinator-lane release. A
  // superseded Image may remain alive until the next beginVisible() aborts it.
  const activeLinkedFollowerTransportRef = useRef(null);
  const activeLinkedIntentTakeoverRef = useRef(null);
  const interactiveRequestCoordinatorRef = useRef(null);
  const cineResourceGovernorRef = useRef(cineResourceGovernor);
  cineResourceGovernorRef.current = cineResourceGovernor;
  const viewportIndexRef = useRef(index);
  viewportIndexRef.current = index;
  const isActiveViewportRef = useRef(isActiveViewport);
  isActiveViewportRef.current = isActiveViewport;
  const cacheEpochRef = useRef(cacheEpoch);
  cacheEpochRef.current = cacheEpoch;
  const evidenceWorkloadRef = useRef(null);
  const speculativeClaimAuthorityRef = useRef({ adjacent: null, 'full-stack': null, 'linked-intent': null });
  const seriesLoadGenerationRef = useRef(null);
  const currentSeriesLoadGenerationRef = useRef(null);
  const viewportGeometryBroadcastCoordinatorRef = useRef(null);
  const onViewportGeometryChangeRef = useRef(onViewportGeometryChange);
  onViewportGeometryChangeRef.current = onViewportGeometryChange;
  const windowLevelDragCoordinatorRef = useRef(null);
  const windowLevelEvidenceClockRef = useRef(null);
  const windowLevelEvidenceInteractionRef = useRef(null);
  if (!interactiveRequestCoordinatorRef.current) {
    interactiveRequestCoordinatorRef.current = createViewportInteractiveRequestCoordinator({
      onAbortVisibleImage: image => cineResourceGovernorRef.current?.releaseDetachedImage(image),
    });
  }
  if (!followerFrameCoordinatorRef.current) {
    followerFrameCoordinatorRef.current = createViewportFollowerFrameCoordinator({
      requestFrame: callback => {
        const frameId = window.requestAnimationFrame?.(callback);
        return frameId == null
          ? { kind: 'timeout', id: window.setTimeout(callback, 0) }
          : { kind: 'animation-frame', id: frameId };
      },
      cancelFrame: frameHandle => {
        if (frameHandle?.kind === 'animation-frame') window.cancelAnimationFrame?.(frameHandle.id);
        if (frameHandle?.kind === 'timeout') window.clearTimeout(frameHandle.id);
      },
      onDispatch: (command, complete) => {
        if (typeof dispatchFollowerCommandRef.current !== 'function') {
          complete();
          return;
        }
        dispatchFollowerCommandRef.current(command, complete);
      },
    });
  }
  if (!linkedFollowerPaintAuthorityRef.current) {
    linkedFollowerPaintAuthorityRef.current = createLinkedFollowerPaintAuthority({
      requestFrame: callback => {
        const frameId = window.requestAnimationFrame?.(callback);
        return frameId == null
          ? { kind: 'timeout', id: window.setTimeout(callback, 0) }
          : { kind: 'animation-frame', id: frameId };
      },
      cancelFrame: frameHandle => {
        if (frameHandle?.kind === 'animation-frame') window.cancelAnimationFrame?.(frameHandle.id);
        if (frameHandle?.kind === 'timeout') window.clearTimeout(frameHandle.id);
      },
    });
  }
  if (!seriesLoadGenerationRef.current) {
    seriesLoadGenerationRef.current = createSeriesLoadGenerationCoordinator();
  }
  if (!viewportGeometryBroadcastCoordinatorRef.current) {
    viewportGeometryBroadcastCoordinatorRef.current = createViewportGeometryBroadcastCoordinator({
      publish: (viewportIndex, snapshot) => onViewportGeometryChangeRef.current?.(viewportIndex, snapshot),
    });
  }
  const lastProjectedPreloadRef = useRef({ key: '', at: 0 });
  const burstProjectedPreloadRef = useRef({ key: '', at: 0, direction: 0 });
  const initialLoadedRef = useRef(null);
  const measurementsRef = useRef([]);
  const pendingMeasureRef = useRef(null);
  const roisRef = useRef([]);
  const pendingRoiRef = useRef(null);
  const annotationsRef = useRef([]);
  const pendingArrowRef = useRef(null);
  const pendingAngleRef = useRef([]);
  const pendingCobbRef = useRef([]);
  const presentationRestoreKeyRef = useRef('');
  const presentationSuppressSaveRef = useRef(false);
  const presentationVersionRef = useRef(0);
  const presentationSyncedStateKeyRef = useRef('');
  const presentationBaseStateRef = useRef({ measurements: [], rois: [], annotations: [] });
  const presentationSaveCoordinatorRef = useRef(null);
  const presentationRestoreCoordinatorRef = useRef(null);
  const annotationDragRef = useRef(null);
  const suppressNextCanvasClickRef = useRef(false);
  const pixelSpacingRef = useRef(null);
  const seriesGeometryRef = useRef([]);
  const naturalSizeRef = useRef(null);
  const zoomRef = useRef(1.0);
  const onViewportZoomChangeRef = useRef(onViewportZoomChange);
  const onViewportFreezeChangeRef = useRef(onViewportFreezeChange);
  const panOffsetRef = useRef({ x: 0, y: 0 });
  const lastSyncCommandNonceRef = useRef(null);
  // Triangulation sync commands arrive from two delivery paths (React
  // syncCommand prop for committed anchors, imperative drag channel for
  // per-frame drag updates). They share one nonce dedupe so the committed
  // anchor cannot replay the identical command already applied by the channel.
  const lastTriangulationSyncCommandNonceRef = useRef(null);
  const maxSliceRef = useRef(maxSlice);
  maxSliceRef.current = maxSlice;
  const petSliceThicknessRef = useRef(1);

  const invalidateSpeculativeClaimAuthority = useCallback((kind = null) => {
    if (kind === 'adjacent' || kind === 'full-stack' || kind === 'linked-intent' || kind === 'burst-projected') {
      speculativeClaimAuthorityRef.current[kind] = null;
      return;
    }
    speculativeClaimAuthorityRef.current.adjacent = null;
    speculativeClaimAuthorityRef.current['full-stack'] = null;
    speculativeClaimAuthorityRef.current['linked-intent'] = null;
    speculativeClaimAuthorityRef.current['burst-projected'] = null;
  }, []);

  const prepareSpeculativeCandidates = useCallback(({
    kind,
    generation,
    series: candidateSeries,
    candidates,
    preserveExistingClaim = false,
  }) => {
    if (!Array.isArray(candidates)) {
      invalidateSpeculativeClaimAuthority(kind);
      return [];
    }
    const definitions = candidates.map((candidate, candidateIndex) => {
      try {
        const parsed = new URL(candidate?.url, window.location.href);
        if (parsed.origin !== window.location.origin) return null;
        const targetSlice = Number(candidate?.slice ?? parsed.searchParams.get('slice'));
        if (!Number.isSafeInteger(targetSlice) || targetSlice < 0) return null;
        return {
          url: parsed.href,
          distance: candidate?.distance,
          order: candidate?.order ?? candidateIndex,
          targetSlice,
        };
      } catch (_) {
        return null;
      }
    }).filter(Boolean);
    if (!isViewportEvidenceNetworkBridgeActive()) {
      invalidateSpeculativeClaimAuthority(kind);
      return definitions.map(({ url, distance, order }) => ({ url, distance, order }));
    }

    const durableProjectedBurst = kind === 'burst-projected';
    const rejectCandidates = () => {
      invalidateSpeculativeClaimAuthority(kind);
      return [];
    };
    const workload = evidenceWorkloadRef.current;
    const coordinator = interactiveRequestCoordinatorRef.current;
    const requestViewportRoot = viewportRootRef.current;
    const seriesLoadGeneration = currentSeriesLoadGenerationRef.current;
    let routeHref;
    try { routeHref = new URL(window.location.href).href; } catch (_) { return rejectCandidates(); }
    if (!requestViewportRoot || requestViewportRoot.isConnected !== true
        || !candidateSeries?.studyInstanceUID || !candidateSeries?.seriesInstanceUID
        || !Number.isSafeInteger(generation) || !coordinator?.isCurrentVisible(generation)
        || workload?.generation !== generation
        || workload?.phase !== 'single-step' || workload?.interaction !== 'wheel'
        || typeof window.fetch !== 'function' || typeof Blob !== 'function'
        || typeof URL?.createObjectURL !== 'function' || typeof URL?.revokeObjectURL !== 'function'
        || typeof Image !== 'function') return rejectCandidates();

    const seriesSnapshot = Object.freeze({
      studyInstanceUID: candidateSeries.studyInstanceUID,
      seriesInstanceUID: candidateSeries.seriesInstanceUID,
    });
    const priorClaim = preserveExistingClaim === true
      ? speculativeClaimAuthorityRef.current[kind]
      : null;
    const canPreserveClaim = priorClaim?.kind === kind
      && priorClaim.requestViewportRoot === requestViewportRoot
      && priorClaim.routeHref === routeHref
      && priorClaim.seriesLoadGeneration === seriesLoadGeneration
      && priorClaim.seriesSnapshot?.studyInstanceUID === seriesSnapshot.studyInstanceUID
      && priorClaim.seriesSnapshot?.seriesInstanceUID === seriesSnapshot.seriesInstanceUID;
    let claim;
    if (canPreserveClaim) {
      claim = priorClaim;
      claim.generation = generation;
      claim.urls = new Set(definitions.map(candidate => candidate.url));
    } else {
      invalidateSpeculativeClaimAuthority(kind);
      claim = {
        kind,
        generation,
        requestViewportRoot,
        routeHref,
        seriesLoadGeneration,
        seriesSnapshot,
        urls: new Set(definitions.map(candidate => candidate.url)),
      };
      speculativeClaimAuthorityRef.current[kind] = claim;
    }

    const viewportIsCurrent = () => {
      try {
        const currentSeries = seriesRef.current;
        return viewportMountedRef.current === true
          && viewportIndexRef.current === index
          && viewportRootRef.current === requestViewportRoot
          && requestViewportRoot.isConnected === true
          && currentSeriesLoadGenerationRef.current === seriesLoadGeneration
          && currentSeries?.studyInstanceUID === seriesSnapshot.studyInstanceUID
          && currentSeries?.seriesInstanceUID === seriesSnapshot.seriesInstanceUID
          && new URL(window.location.href).href === routeHref;
      } catch (_) {
        return false;
      }
    };

    return definitions.map(({ url, distance, order, targetSlice }) => {
      const claimIsCurrent = () => (
        speculativeClaimAuthorityRef.current[kind] === claim
        && claim.urls.has(url)
      );
      const targetStillMapsToCanonicalUrl = () => {
        try {
          const currentSeries = seriesRef.current;
          if (!currentSeries || !claimIsCurrent()) return false;
          const currentRenderUrl = buildPetSliceThicknessRenderUrl({
            studyUID: currentSeries.studyInstanceUID,
            seriesUID: currentSeries.seriesInstanceUID,
            slice: targetSlice,
            ww: wlRef.current.ww,
            wc: wlRef.current.wc,
            series: currentSeries,
            thicknessMm: petSliceThicknessRef.current,
            cacheEpoch: cacheEpochRef.current,
          });
          const parsed = new URL(currentRenderUrl, window.location.href);
          return parsed.origin === window.location.origin && parsed.href === url;
        } catch (_) {
          return false;
        }
      };
      const isMounted = () => viewportMountedRef.current === true;
      const isViewportCurrent = () => viewportIsCurrent();
      const isGenerationCurrent = () => (
        claimIsCurrent() && (durableProjectedBurst || coordinator.isCurrentVisible(generation))
      );
      const isSliceCurrent = () => targetStillMapsToCanonicalUrl();
      const isRenderAuthorityCurrent = () => (
        claimIsCurrent()
        && isViewportEvidenceNetworkBridgeActive()
        && isMounted()
        && isViewportCurrent()
        && isGenerationCurrent()
        && isSliceCurrent()
      );
      const evidencePreloadContext = createCineEvidencePreloadContext({
        viewport: index,
        generation,
        slice: targetSlice,
        phase: workload.phase,
        interaction: workload.interaction,
        temperature: 'cold',
        deliverySource: 'network',
        currentOrigin: window.location.origin,
        fetchImpl: (...args) => window.fetch(...args),
        blobFactory: (parts, options) => new Blob(parts, options),
        createObjectURL: blob => URL.createObjectURL(blob),
        revokeObjectURL: objectUrl => URL.revokeObjectURL(objectUrl),
        createImage: () => new Image(),
        isMounted,
        isViewportCurrent,
        isGenerationCurrent,
        isSliceCurrent,
        isRenderAuthorityCurrent,
      });
      return { url, distance, order, evidencePreloadContext };
    });
  }, [index, invalidateSpeculativeClaimAuthority]);

  const activeTool = localTool || toolMode || 'scroll';
  const hasAcceptedClinicalSurface = useCallback(() => (
    Boolean(acceptedVisibleSurfaceRef.current) && Number.isFinite(sliceRef.current)
  ), []);
  const ownsBrowserWarmup = shouldOwnViewportBrowserWarmup({ isActiveViewport, isViewportMaximized });
  const notifyWindowLevelChange = useCallback((wl) => {
    if (typeof onWindowLevelChange === 'function') onWindowLevelChange(wl);
  }, [onWindowLevelChange]);
  const annotationTools = ['measure', 'roi', 'select', 'arrow', 'text', 'pen', 'rect', 'ellipse', 'angle', 'cobb', 'level'];
  const isAnnotationTool = annotationTools.includes(activeTool);
  const resetToolToScroll = useCallback(() => {
    setLocalTool(null);
    if (typeof onToolComplete === 'function') onToolComplete('scroll');
  }, [onToolComplete]);
  const completeToolInteraction = useCallback(() => {
    if (toolCompletionBehavior === 'stay') return;
    resetToolToScroll();
  }, [resetToolToScroll, toolCompletionBehavior]);

  const toggleToolRailSection = useCallback((section) => {
    setExpandedToolRailSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const effectiveOverlayConfig = normalizeOverlayConfig(overlayConfig);
  const floatingToolRailSafeInsets = overlayToolRailSafeInsets(effectiveOverlayConfig, {
    topMinimum: VIEWPORT_FLOATING_TOOL_RAIL_TOP_INSET,
    bottomMinimum: VIEWPORT_FLOATING_TOOL_RAIL_BOTTOM_INSET,
    gutter: VIEWPORT_FLOATING_TOOL_RAIL_GUTTER,
  });
  const floatingToolRailHasSafeBand = !canvasCssSize.height || floatingToolRailSafeBandAvailable({
    viewportHeight: canvasCssSize.height,
    topInset: floatingToolRailSafeInsets.top,
    bottomInset: floatingToolRailSafeInsets.bottom,
  });

  const getFloatingToolRailBounds = useCallback(() => {
    const viewport = viewportRootRef.current;
    const rail = toolRailRef.current;
    if (!viewport || !rail) return null;
    const viewportRect = viewport.getBoundingClientRect();
    const railRect = rail.getBoundingClientRect();
    return {
      viewportWidth: viewportRect.width,
      viewportHeight: viewportRect.height,
      railWidth: railRect.width,
      railHeight: railRect.height,
      leftInset: VIEWPORT_FLOATING_TOOL_RAIL_GUTTER,
      rightInset: VIEWPORT_FLOATING_TOOL_RAIL_GUTTER,
      topInset: floatingToolRailSafeInsets.top,
      bottomInset: floatingToolRailSafeInsets.bottom,
      horizontalEdge: toolLayoutPrefs?.dock?.position === 'right' ? 'right' : 'left',
    };
  }, [floatingToolRailSafeInsets.bottom, floatingToolRailSafeInsets.top, toolLayoutPrefs?.dock?.position]);

  const beginToolRailDrag = useCallback((event) => {
    if (toolRailDocked || event.button !== 0) return;
    const viewport = viewportRootRef.current;
    const rail = toolRailRef.current;
    const bounds = getFloatingToolRailBounds();
    if (!viewport || !rail || !bounds) return;
    event.preventDefault();
    event.stopPropagation();
    const viewportRect = viewport.getBoundingClientRect();
    const railRect = rail.getBoundingClientRect();
    const startPosition = clampFloatingToolRailPosition({
      x: railRect.left - viewportRect.left,
      y: railRect.top - viewportRect.top,
    }, bounds);
    toolRailDragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPosition,
      position: startPosition,
    };
    setToolRailPosition(startPosition);
    setToolRailDragging(true);
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}
  }, [getFloatingToolRailBounds, toolRailDocked]);

  const moveToolRailDrag = useCallback((event) => {
    const drag = toolRailDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const bounds = getFloatingToolRailBounds();
    if (!bounds) return;
    event.preventDefault();
    event.stopPropagation();
    const next = clampFloatingToolRailPosition({
      x: drag.startPosition.x + event.clientX - drag.startClientX,
      y: drag.startPosition.y + event.clientY - drag.startClientY,
    }, bounds);
    drag.position = next;
    setToolRailPosition(next);
  }, [getFloatingToolRailBounds]);

  const endToolRailDrag = useCallback((event) => {
    const drag = toolRailDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    toolRailDragRef.current = null;
    setToolRailDragging(false);
    try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch (_) {}
  }, []);

  const moveToolRailWithKeyboard = useCallback((event) => {
    if (toolRailDocked || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    const bounds = getFloatingToolRailBounds();
    if (!bounds) return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 24 : 8;
    setToolRailPosition(previous => {
      const current = previous || defaultFloatingToolRailPosition(bounds);
      return clampFloatingToolRailPosition({
        x: current.x + (event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0),
        y: current.y + (event.key === 'ArrowDown' ? step : event.key === 'ArrowUp' ? -step : 0),
      }, bounds);
    });
  }, [getFloatingToolRailBounds, toolRailDocked]);

  useEffect(() => {
    if (toolRailDocked) {
      toolRailDragRef.current = null;
      setToolRailDragging(false);
      return undefined;
    }
    const settle = () => {
      const bounds = getFloatingToolRailBounds();
      if (!bounds) return;
      setToolRailPosition(previous => {
        const next = previous
          ? clampFloatingToolRailPosition(previous, bounds)
          : defaultFloatingToolRailPosition(bounds);
        return previous && previous.x === next.x && previous.y === next.y ? previous : next;
      });
    };
    const frame = window.requestAnimationFrame(settle);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(settle) : null;
    if (observer) {
      if (viewportRootRef.current) observer.observe(viewportRootRef.current);
      if (toolRailRef.current) observer.observe(toolRailRef.current);
    }
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [getFloatingToolRailBounds, toolRailDocked]);

  useEffect(() => {
    setAnnotationStyle(normalizeAnnotationAppearance(annotationAppearance));
  }, [annotationAppearance]);

  const refreshOverlayLayout = useCallback(() => {
    const canvas = canvasRef.current;
    const parent = boxRef.current || canvas?.parentElement;
    if (!parent) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = parent.getBoundingClientRect();
    const logicalW = Math.max(1, rect.width);
    const logicalH = Math.max(1, rect.height);

    if (canvas) {
      canvas.__logicalWidth = logicalW;
      canvas.__logicalHeight = logicalH;
      const pixelW = Math.round(logicalW * dpr);
      const pixelH = Math.round(logicalH * dpr);
      if (canvas.width !== pixelW) canvas.width = pixelW;
      if (canvas.height !== pixelH) canvas.height = pixelH;
      canvas.style.width = logicalW + 'px';
      canvas.style.height = logicalH + 'px';

      clearAnnotationCanvas(canvas);
    }

    setCanvasCssSize(prev => (Math.abs(prev.width - logicalW) > 0.1 || Math.abs(prev.height - logicalH) > 0.1)
      ? { width: logicalW, height: logicalH }
      : prev);

    // The visible annotation layer reads the live <img> DOM rect during render.
    // Parent/image ResizeObserver events often happen without any React state
    // changing (snap/maximize, scrollbar/layout settling), so force a lightweight
    // render to realign SVG/DOM annotations to the new image rect.
    setOverlayLayoutVersion(v => v + 1);
  }, []);

  // When the global toolbar tool changes, return this viewport to the global tool.
  // Otherwise a stale per-viewport localTool (especially 'wl') can silently block ROI/Measure events.
  useEffect(() => { setLocalTool(null); }, [toolMode]);

  // Keep refs in sync
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { petSliceThicknessRef.current = petSliceThicknessMm; }, [petSliceThicknessMm]);
  const cancelLinkedScrollFrame = useCallback(() => {
    if (linkedScrollFrameRef.current != null) {
      window.cancelAnimationFrame(linkedScrollFrameRef.current);
      linkedScrollFrameRef.current = null;
    }
  }, []);
  const cancelLinkedFollowerRetry = useCallback(() => {
    if (linkedFollowerRetryTimerRef.current != null) {
      window.clearTimeout(linkedFollowerRetryTimerRef.current);
      linkedFollowerRetryTimerRef.current = null;
    }
  }, []);
  useEffect(() => { onViewportZoomChangeRef.current = onViewportZoomChange; }, [onViewportZoomChange]);
  useEffect(() => { onViewportFreezeChangeRef.current = onViewportFreezeChange; }, [onViewportFreezeChange]);
  useEffect(() => {
    if (onViewportZoomChangeRef.current) onViewportZoomChangeRef.current(zoom);
  }, [zoom]);
  useEffect(() => {
    if (onViewportFreezeChangeRef.current) onViewportFreezeChangeRef.current(viewportFrozen);
  }, [viewportFrozen]);
  useEffect(() => { panOffsetRef.current = panOffset; }, [panOffset]);
  useEffect(() => {
    const normalized = normalizeViewportWindowLevel(windowLevel);
    wlRef.current = normalized;
    setWlDraft(formatWindowLevelDraft(normalized));
  }, [windowLevel]);
  useEffect(() => { seriesRef.current = series; }, [series]);
  useEffect(() => { measurementsRef.current = measurements; }, [measurements]);
  useEffect(() => { pendingMeasureRef.current = pendingMeasure; }, [pendingMeasure]);
  useEffect(() => { roisRef.current = rois; }, [rois]);
  useEffect(() => { pendingRoiRef.current = pendingRoi; }, [pendingRoi]);
  useEffect(() => { annotationsRef.current = annotations; }, [annotations]);
  useEffect(() => { pendingArrowRef.current = pendingArrow; }, [pendingArrow]);
  useEffect(() => { pendingAngleRef.current = pendingAngle; }, [pendingAngle]);
  useEffect(() => { pendingCobbRef.current = pendingCobb; }, [pendingCobb]);
  useEffect(() => { presentationVersionRef.current = presentationVersion; }, [presentationVersion]);

  if (!presentationSaveCoordinatorRef.current) {
    presentationSaveCoordinatorRef.current = createPresentationStateSaveCoordinator({
      save: presentationStateApi.save,
      load: presentationStateApi.get,
      getCurrentKey: () => {
        const current = seriesRef.current;
        return current?.studyInstanceUID && current?.seriesInstanceUID ? `${current.studyInstanceUID}:${current.seriesInstanceUID}` : '';
      },
      onVersion: v => {
        presentationVersionRef.current = v || 0;
        setPresentationVersion(v || 0);
      },
      onStatus: setPresentationSaveStatus,
      onError: setPresentationSaveError,
    });
  }

  if (!presentationRestoreCoordinatorRef.current) {
    presentationRestoreCoordinatorRef.current = createPresentationRestoreCoordinator({
      getCurrentKey: () => getPresentationRestoreKey(seriesRef.current),
      apply: result => {
        const restored = normalizePresentationState(result?.state);
        presentationBaseStateRef.current = restored;
        presentationSyncedStateKeyRef.current = presentationStateSyncKey(restored);
        setMeasurements(restored.measurements);
        setRois(restored.rois);
        setAnnotations(restored.annotations);
        setPresentationVersion(result?.version || 0);
        setPresentationSaveStatus(result?.version ? `Restored v${result.version}` : 'No saved presentation state');
        setPresentationSaveError('');
      },
      finish: (_key, stillCurrent) => {
        setTimeout(() => {
          if (stillCurrent()) presentationSuppressSaveRef.current = false;
        }, 0);
      },
      onError: error => {
        console.warn('Could not load presentation state', error);
      },
    });
  }

  const activePresentationRestoreKey = getPresentationRestoreKey(series);
  useEffect(() => {
    const ser = seriesRef.current;
    const key = activePresentationRestoreKey;
    if (!key || !ser) return;
    if (presentationRestoreKeyRef.current === key) return;
    roiStatsSavePendingRef.current = null;
    presentationRestoreKeyRef.current = key;
    presentationSuppressSaveRef.current = true;
    presentationRestoreCoordinatorRef.current.restore({
      key,
      load: () => loadSeriesPresentationState(ser, presentationStateApi.get),
    });
    return () => presentationRestoreCoordinatorRef.current?.invalidate();
  }, [activePresentationRestoreKey]);

  useEffect(() => {
    if (annotationDragRef.current?.id) return;
    if (propertyEditSessionRef.current?.target) return;
    if (roiStatsSavePendingRef.current) return;
    const ser = series;
    if (!ser?.studyInstanceUID || !ser?.seriesInstanceUID || presentationSuppressSaveRef.current) return;
    const state = serializePresentationState({ measurements, rois, annotations });
    const stateKey = presentationStateSyncKey(state);
    if (stateKey === presentationSyncedStateKeyRef.current) return;
    const key = `${ser.studyInstanceUID}:${ser.seriesInstanceUID}`;
    presentationSaveCoordinatorRef.current
      .enqueue({
        key,
        studyInstanceUID: ser.studyInstanceUID,
        seriesInstanceUID: ser.seriesInstanceUID,
        state,
        baseState: presentationBaseStateRef.current,
        getExpectedVersion: () => presentationVersionRef.current,
      })
      .then(result => {
        const currentSeries = seriesRef.current;
        if (`${currentSeries?.studyInstanceUID || ''}:${currentSeries?.seriesInstanceUID || ''}` !== key) return;
        const savedState = result?.state || state;
        presentationBaseStateRef.current = savedState;
        presentationSyncedStateKeyRef.current = presentationStateSyncKey(savedState);
      })
      .catch(e => {
        console.warn('Could not save presentation state', e);
      });
  }, [series, measurements, rois, annotations, annotationCommitRevision]);
  useEffect(() => { pixelSpacingRef.current = pixelSpacing; }, [pixelSpacing]);
  useEffect(() => { seriesGeometryRef.current = Array.isArray(seriesGeometry) ? seriesGeometry : []; }, [seriesGeometry]);
  React.useLayoutEffect(() => {
    if (typeof onViewportGeometryChangeRef.current !== 'function') return;
    if (!acceptedVisibleSurfaceRef.current || !Number.isFinite(slice)) {
      viewportGeometryBroadcastCoordinatorRef.current.update(index, null);
      return;
    }
    const snapshot = createGeometrySnapshot({
      viewportIndex: index,
      slice,
      total: maxSlice + 1,
      series,
      geometry: Array.isArray(seriesGeometry) ? seriesGeometry[slice] : null,
      seriesGeometry: Array.isArray(seriesGeometry) ? seriesGeometry : [],
      dimensions: naturalSize,
    });
    viewportGeometryBroadcastCoordinatorRef.current.update(index, snapshot);
  }, [index, maxSlice, naturalSize, series, seriesGeometry, slice]);

  const cancelScrollIdlePreload = useCallback(() => {
    if (scrollIdlePreloadTimerRef.current != null) {
      window.clearTimeout(scrollIdlePreloadTimerRef.current);
      scrollIdlePreloadTimerRef.current = null;
    }
  }, []);

  const cancelWheelScrollFrame = useCallback(() => {
    if (scrollFrameRef.current != null) {
      window.cancelAnimationFrame(scrollFrameRef.current);
      scrollFrameRef.current = null;
    }
    scrollPendingRef.current = 0;
    scrollPendingRapidBurstRef.current = false;
    lastWheelEventAtRef.current = 0;
    cineWheelEventRef.current = null;
    const retainedActivity = cineWheelActivityRef.current;
    cineWheelActivityRef.current = null;
    if (retainedActivity?.receipt) clearViewportCineWheelActivityForFrame(retainedActivity.receipt);
    const owner = cineWheelInteractionRef.current;
    cineWheelInteractionRef.current = null;
    cineWheelRenderRequestRef.current = null;
    cineWheelPendingRenderOrdinalRef.current = null;
    if (owner?.interaction && isViewportCineInteractionActive(owner.interaction)) {
      cancelViewportCineInteraction(owner.interaction);
    }
  }, []);

  const cancelScrubFrame = useCallback(() => {
    if (scrubFrameRef.current != null) {
      window.cancelAnimationFrame(scrubFrameRef.current);
      scrubFrameRef.current = null;
    }
    if (scrubThrottleTimerRef.current != null) {
      window.clearTimeout(scrubThrottleTimerRef.current);
      scrubThrottleTimerRef.current = null;
    }
    scrubPendingSliceRef.current = null;
    scrubActiveRef.current = false;
    scrubDisplayPendingRef.current = false;
  }, []);

  const invalidateActiveSeriesBrowserWarmup = useCallback(() => {
    invalidateSpeculativeClaimAuthority('full-stack');
    cineResourceGovernor?.removeSpeculative({ paneId: index, kind: 'full-stack' });
  }, [cineResourceGovernor, index, invalidateSpeculativeClaimAuthority]);

  const restoreAcceptedActiveImage = useCallback(() => {
    const acceptedSource = acceptedImgSrcRef.current;
    imgSrcRef.current = acceptedSource;
    setImgSrc(acceptedSource);
    const activeImage = imgRef.current;
    if (!activeImage) return acceptedSource;
    if (!acceptedSource) {
      try { activeImage.removeAttribute?.('src'); } catch (_) {}
      return acceptedSource;
    }
    if (!isExpectedViewportImageSource(acceptedSource, activeImage.src, window.location.href)) {
      try { activeImage.src = new URL(acceptedSource, window.location.href).href; } catch (_) {}
    }
    return acceptedSource;
  }, []);

  const cancelPendingViewportPublication = useCallback(() => {
    linkedFollowerPaintAuthorityRef.current?.cancel();
    const linkedIntentTakeover = activeLinkedIntentTakeoverRef.current;
    activeLinkedIntentTakeoverRef.current = null;
    try { linkedIntentTakeover?.cancel?.(); } catch (_) {}
    const pendingEvidenceOperation = pendingEvidenceOperationRef.current;
    pendingEvidenceOperationRef.current = null;
    pendingEvidenceOperation?.cancel();
    directDomSettlementCancelRef.current?.();
    directDomSettlementCancelRef.current = null;
    cancelActiveViewportImageSettlement(imgRef.current);
    const pendingVisible = pendingVisibleDomSettlementRef.current;
    pendingVisibleDomSettlementRef.current = null;
    if (pendingVisible?.image) cineResourceGovernor?.releaseDetachedImage(pendingVisible.image);
    try { pendingVisible?.onSettled?.(); } catch (_) {}
    restoreAcceptedActiveImage();
    scrubDisplayPendingRef.current = false;
  }, [cineResourceGovernor, restoreAcceptedActiveImage]);

  const closeWindowLevelEvidenceInteraction = useCallback((outcome = 'cancelled', expected = null) => {
    const state = windowLevelEvidenceInteractionRef.current;
    if (!state || (expected && state !== expected)) return false;
    windowLevelEvidenceInteractionRef.current = null;
    state.paintFrames.forEach(frameId => window.cancelAnimationFrame(frameId));
    state.paintFrames.clear();
    if (outcome === 'failed') failViewportWindowLevelInteraction(state.interaction);
    else if (outcome === 'cancelled') cancelViewportWindowLevelInteraction(state.interaction);
    return true;
  }, []);

  const attemptWindowLevelEvidenceSettlement = useCallback(state => {
    if (!state || windowLevelEvidenceInteractionRef.current !== state || !state.released) return false;
    if (!settleViewportWindowLevelInteraction(state.interaction)) return false;
    closeWindowLevelEvidenceInteraction(null, state);
    return true;
  }, [closeWindowLevelEvidenceInteraction]);

  const scheduleWindowLevelAcceptedPaint = useCallback(({
    state,
    input,
    publication,
    renderEvidence,
    surface,
    surfaceElement,
  }) => {
    if (!state || windowLevelEvidenceInteractionRef.current !== state
        || state.input !== input || state.publication) return false;
    state.publication = publication;
    state.renderEvidence = renderEvidence;
    const firstFrame = window.requestAnimationFrame(() => {
      state.paintFrames.delete(firstFrame);
      if (windowLevelEvidenceInteractionRef.current !== state || state.input !== input) return;
      const secondFrame = window.requestAnimationFrame(() => {
        state.paintFrames.delete(secondFrame);
        if (windowLevelEvidenceInteractionRef.current !== state || state.input !== input) return;
        const accepted = acceptedVisibleSurfaceRef.current;
        const preview = scrubPreviewCanvasRef.current;
        const exactSurface = surface === 'preview' ? preview : imgRef.current;
        const visible = surfaceElement === exactSurface && surfaceElement?.isConnected === true
          && accepted?.surface === surface && accepted?.evidenceOperation === renderEvidence
          && accepted?.windowLevelEvidenceInput === input
          && (surface === 'preview'
            ? preview?.style.display !== 'none'
            : isExpectedViewportImageSource(
              accepted?.displayUrl,
              surfaceElement?.src,
              window.location.href,
            )
              && Boolean(surfaceElement.complete && surfaceElement.naturalWidth > 0)
              && (!preview || preview.style.display === 'none'));
        if (!visible || !acceptViewportWindowLevelPaint(input, publication, renderEvidence)) {
          closeWindowLevelEvidenceInteraction('failed', state);
          return;
        }
        attemptWindowLevelEvidenceSettlement(state);
      });
      state.paintFrames.add(secondFrame);
    });
    state.paintFrames.add(firstFrame);
    return true;
  }, [attemptWindowLevelEvidenceSettlement, closeWindowLevelEvidenceInteraction]);

  const joinWindowLevelAcceptedPublication = useCallback(({
    workload,
    renderEvidence,
    surface,
    surfaceElement,
  }) => {
    const state = workload?.windowLevelEvidenceState;
    const input = workload?.windowLevelEvidenceInput;
    if (!state || windowLevelEvidenceInteractionRef.current !== state
        || state.input !== input || state.publication) return false;
    const publication = acceptViewportWindowLevelRenderPublication(input, renderEvidence);
    if (!publication) {
      closeWindowLevelEvidenceInteraction('failed', state);
      return false;
    }
    return scheduleWindowLevelAcceptedPaint({
      state,
      input,
      publication,
      renderEvidence,
      surface,
      surfaceElement,
    });
  }, [closeWindowLevelEvidenceInteraction, scheduleWindowLevelAcceptedPaint]);

  const clearVisible = useCallback(() => {
    // Fence every asynchronous series/geometry continuation before touching any
    // pixel owner or DOM surface.
    seriesLoadGenerationRef.current.invalidate();
    currentSeriesLoadGenerationRef.current = null;
    activeLinkedAuthorityRequestRef.current = null;
    viewportGeometryBroadcastCoordinatorRef.current.invalidate(index);
    cancelPendingViewportPublication();
    followerFrameCoordinatorRef.current?.cancel();
    interactiveRequestCoordinatorRef.current.reset({ initialVisibleSlice: null });
    sliceRef.current = null;
    requestedSliceRef.current = null;
    cancelLinkedScrollFrame();
    cancelLinkedFollowerRetry();
    linkedFollowerRetryRef.current = { key: '', attempts: 0 };
    cancelWheelScrollFrame();
    cancelScrubFrame();
    cancelScrollIdlePreload();
    // Fence any in-progress triangulation drag gesture: a series switch within
    // the same study must not let a captured pointer or queued drag rAF emit
    // triangulation points computed against the outgoing series. Ref-only
    // cleanup (helpers are declared later in the component body).
    if (triangulationDragFrameRef.current != null) {
      window.cancelAnimationFrame(triangulationDragFrameRef.current);
      triangulationDragFrameRef.current = null;
    }
    {
      const captureTarget = triangulationDragCaptureTargetRef.current;
      const capturePointerId = triangulationDragPointerIdRef.current;
      triangulationDragCaptureTargetRef.current = null;
      if (captureTarget && capturePointerId != null) {
        try {
          if (!captureTarget.hasPointerCapture || captureTarget.hasPointerCapture(capturePointerId)) {
            captureTarget.releasePointerCapture?.(capturePointerId);
          }
        } catch (_) {}
      }
    }
    triangulationDragPointerIdRef.current = null;
    triangulationDragGestureRef.current = null;
    triangulationDragStateRef.current = null;
    windowLevelDragCoordinatorRef.current?.cancel();
    closeWindowLevelEvidenceInteraction('cancelled');
    evidenceWorkloadRef.current = null;
    invalidateSpeculativeClaimAuthority();
    cineResourceGovernor?.removeSpeculative({ paneId: index, kind: 'full-stack' });
    cineResourceGovernor?.removeSpeculative({ paneId: index, kind: 'adjacent' });
    cineResourceGovernor?.removeSpeculative({ paneId: index, kind: 'linked-intent' });
    const acceptedEvidenceOperation = acceptedEvidenceOperationRef.current;
    acceptedEvidenceOperationRef.current = null;
    const activeImage = imgRef.current;
    if (activeImage) {
      activeImage.onload = null;
      activeImage.onerror = null;
      try { activeImage.removeAttribute?.('src'); } catch (_) {}
      try { activeImage.src = ''; } catch (_) {}
    }
    const preview = scrubPreviewCanvasRef.current;
    if (preview) {
      preview.width = 0;
      preview.height = 0;
      preview.style.display = 'none';
    }
    const annotationCanvas = canvasRef.current;
    if (annotationCanvas) {
      const context = annotationCanvas.getContext?.('2d');
      try { context?.clearRect(0, 0, annotationCanvas.width || 0, annotationCanvas.height || 0); } catch (_) {}
    }
    scrubDisplayPendingRef.current = false;
    imgSrcRef.current = null;
    acceptedImgSrcRef.current = null;
    acceptedVisibleSurfaceRef.current = null;
    acceptedEvidenceOperation?.release();
    lastLinkedPublishedSliceRef.current = null;
    onSliceChange?.(index, null, null, { source: 'cleared' });
    onReportContextChange?.({ viewportIndex: index, cleared: true });
    seriesRef.current = null;
    seriesGeometryRef.current = [];
    naturalSizeRef.current = null;
    setImgSrc(null);
    setSlice(null);
    setSeries(null);
    setSeriesGeometry([]);
    setPixelSpacing(null);
    setNaturalSize(null);
  }, [cancelLinkedFollowerRetry, cancelLinkedScrollFrame, cancelPendingViewportPublication, cancelScrollIdlePreload, cancelScrubFrame, cancelWheelScrollFrame, cineResourceGovernor, closeWindowLevelEvidenceInteraction, index, invalidateSpeculativeClaimAuthority, onReportContextChange, onSliceChange]);

  const pauseActiveSeriesBrowserWarmup = useCallback(() => {
    invalidateSpeculativeClaimAuthority('full-stack');
    cineResourceGovernor?.removeSpeculative({ paneId: index, kind: 'full-stack' });
  }, [cineResourceGovernor, index, invalidateSpeculativeClaimAuthority]);

  React.useLayoutEffect(() => {
    if (!cineResourceGovernor) return undefined;
    const unregisterPane = cineResourceGovernor.registerPane(index, {
      clearVisible,
      pauseFullWarmup: () => pauseActiveSeriesBrowserWarmup(),
    });
    return unregisterPane;
  }, [cineResourceGovernor, clearVisible, index, pauseActiveSeriesBrowserWarmup]);

  const startActiveSeriesBrowserWarmup = useCallback((ser, max, wl, centerSlice = 0) => {
    const shouldWarm = shouldOwnViewportBrowserWarmup({ isActiveViewport, isViewportMaximized });
    if (!shouldWarm || !ser?.studyInstanceUID || !ser?.seriesInstanceUID || !cacheEpoch) {
      invalidateActiveSeriesBrowserWarmup();
      return false;
    }
    const safeMax = Math.max(0, Number(max) || 0);
    // Tiny stacks are already covered by normal adjacent preloads; reserve the
    // full-series browser cache warm path for larger cine/scrub stacks.
    if (safeMax < 80) {
      invalidateActiveSeriesBrowserWarmup();
      return false;
    }
    const effectiveWl = normalizeViewportWindowLevel(wl);
    const generation = interactiveRequestCoordinatorRef.current.snapshot().visibleGeneration;
    const direction = scrollDirectionRef.current || 1;
    const legacyCandidates = createViewportBrowserWarmupCandidates({
      maxSlice: safeMax,
      centerSlice,
      buildUrl: idx => {
        const url = buildPetSliceThicknessRenderUrl({
          studyUID: ser.studyInstanceUID,
          seriesUID: ser.seriesInstanceUID,
          slice: idx,
          ww: effectiveWl.ww,
          wc: effectiveWl.wc,
          series: ser,
          thicknessMm: petSliceThicknessRef.current,
          cacheEpoch,
        });
        return url;
      },
    });
    const candidates = prepareSpeculativeCandidates({
      kind: 'full-stack',
      generation,
      series: ser,
      candidates: legacyCandidates,
    });
    cineResourceGovernor?.replaceSpeculative({
      paneId: index,
      generation,
      kind: 'full-stack',
      direction,
      candidates,
    });
    return true;
  }, [cacheEpoch, cineResourceGovernor, index, invalidateActiveSeriesBrowserWarmup, isActiveViewport, isViewportMaximized, prepareSpeculativeCandidates]);

  const resumeActiveSeriesBrowserWarmup = useCallback(() => {
    if (!shouldOwnViewportBrowserWarmup({ isActiveViewport, isViewportMaximized }) || !seriesRef.current) return false;
    return startActiveSeriesBrowserWarmup(seriesRef.current, maxSlice, wlRef.current, sliceRef.current);
  }, [isActiveViewport, isViewportMaximized, maxSlice, startActiveSeriesBrowserWarmup]);

  React.useLayoutEffect(() => {
    viewportMountedRef.current = true;
    return () => {
      viewportMountedRef.current = false;
      // Layout cleanup runs while DOM refs still exist, both for StrictMode replay
      // and viewport-only layout/VRT replacement unmounts.
      clearVisible();
      lastProjectedPreloadRef.current = { key: '', at: 0 };
      burstProjectedPreloadRef.current = { key: '', at: 0, direction: 0 };
    };
  }, [clearVisible]);

  // Auto-load initialSeries on mount
  useEffect(() => {
    if (!initialSeries?.studyInstanceUID || !initialSeries?.seriesInstanceUID) return undefined;
    const initialSeriesKey = `${initialSeries.studyInstanceUID}:${initialSeries.seriesInstanceUID}`;
    if (initialLoadedRef.current === initialSeriesKey) return undefined;
    initialLoadedRef.current = initialSeriesKey;
    loadSeriesIntoViewport(initialSeries);
    return () => {
      // React StrictMode replays effects without another render. Resetting this
      // setup-local guard lets replay start the replacement transaction after the
      // general lifecycle cleanup invalidates the first one.
      if (initialLoadedRef.current === initialSeriesKey) initialLoadedRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSeries?.studyInstanceUID, initialSeries?.seriesInstanceUID]);

  useEffect(() => {
    if (!series?.studyInstanceUID || !series?.seriesInstanceUID || !ownsBrowserWarmup) {
      invalidateActiveSeriesBrowserWarmup();
      return undefined;
    }
    startActiveSeriesBrowserWarmup(series, maxSlice, wlRef.current, sliceRef.current);
    return undefined;
  }, [series, maxSlice, ownsBrowserWarmup, startActiveSeriesBrowserWarmup, invalidateActiveSeriesBrowserWarmup]);

  // Clear/redraw the event canvas and reference lines whenever overlay inputs change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !naturalSize) return;

    clearAnnotationCanvas(canvas);

    // Draw reference lines from other viewports
    if (referenceFractions && referenceFractions.length > 0) {
      const refCtx = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;
      // Use parent dimensions if canvas hasn't been sized by ResizeObserver yet
      let logicalW = canvas.width / dpr;
      let logicalH = canvas.height / dpr;
      if (logicalH < 200 && canvas.parentElement) {
        logicalW = canvas.parentElement.clientWidth || logicalW;
        logicalH = canvas.parentElement.clientHeight || logicalH;
        // Also properly size the canvas now
        canvas.width = logicalW * dpr;
        canvas.height = logicalH * dpr;
        canvas.style.width = logicalW + 'px';
        canvas.style.height = logicalH + 'px';
      }
      if (!logicalW || !logicalH) return; // truly no dimensions yet
      const colors = ["#ff9800", "#e040fb", "#ffeb3b", "#4caf50"];
      referenceFractions.forEach(({ idx, frac }, i) => {
        const y = Math.round(frac * logicalH) + 0.5;
        refCtx.save();
        refCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        refCtx.beginPath();
        refCtx.moveTo(0, y);
        refCtx.lineTo(logicalW, y);
        refCtx.strokeStyle = colors[i % colors.length];
        refCtx.lineWidth = 1;
        refCtx.setLineDash([6, 4]);
        refCtx.globalAlpha = 0.9;
        refCtx.stroke();
        refCtx.setLineDash([]);
        refCtx.globalAlpha = 1.0;
        refCtx.font = "bold 9px monospace";
        refCtx.fillStyle = colors[i % colors.length];
        refCtx.globalAlpha = 0.9;
        refCtx.fillText("V" + (idx + 1), 4, y > 14 ? y - 3 : y + 10);
        refCtx.restore();
      });
    }
  }, [measurements, pendingMeasure, rois, pendingRoi, annotations, pendingArrow, pendingFreehand, pendingRect, pendingEllipse, pendingAngle, pendingCobb, pixelSpacing, naturalSize, imgSrc, referenceFractions, zoom, panOffset]);
  // Keep canvas and visible DOM/SVG annotations aligned with the actual viewport/image DOM.
  useEffect(() => {
    let raf = null;
    let raf2 = null;
    const schedule = () => {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        refreshOverlayLayout();
        // Browser snap/maximize transitions can settle image object-fit geometry one
        // frame after the parent resize; sample again to catch that final rect.
        raf2 = window.requestAnimationFrame(() => {
          raf2 = null;
          refreshOverlayLayout();
        });
      });
    };

    const obs = new ResizeObserver(schedule);
    if (boxRef.current) obs.observe(boxRef.current);
    if (canvasRef.current?.parentElement && canvasRef.current.parentElement !== boxRef.current) {
      obs.observe(canvasRef.current.parentElement);
    }
    if (imgRef.current) obs.observe(imgRef.current);

    window.addEventListener('resize', schedule);
    schedule();

    return () => {
      if (raf !== null) window.cancelAnimationFrame(raf);
      if (raf2 !== null) window.cancelAnimationFrame(raf2);
      window.removeEventListener('resize', schedule);
      obs.disconnect();
    };
  }, [refreshOverlayLayout, imgSrc, naturalSize]);

  const settlePendingVisibleDom = useCallback((visible, activeSource) => {
    const pending = pendingVisibleDomSettlementRef.current;
    if (!pending) return false;
    if (!interactiveRequestCoordinatorRef.current.isCurrentVisible(pending.generation)) return false;
    if (!isExpectedViewportImageSource(pending.url, activeSource, window.location.href)) return false;
    pendingVisibleDomSettlementRef.current = null;
    try {
      if (visible) {
        const retained = cineResourceGovernor?.retainDecoded({ url: pending.absoluteUrl, image: pending.image });
        if (retained && !retained.retained) cineResourceGovernor?.releaseDetachedImage(pending.image);
        const acceptedSourceBeforeSettlement = acceptedImgSrcRef.current;
        const acceptedSurfaceBeforeSettlement = acceptedVisibleSurfaceRef.current;
        const accepted = pending.onVisible?.() === true;
        if (!accepted) {
          acceptedImgSrcRef.current = acceptedSourceBeforeSettlement;
          acceptedVisibleSurfaceRef.current = acceptedSurfaceBeforeSettlement;
          imgSrcRef.current = acceptedSourceBeforeSettlement;
          setImgSrc(acceptedSourceBeforeSettlement);
          const activeImage = imgRef.current;
          if (activeImage) {
            if (!acceptedSourceBeforeSettlement) {
              try { activeImage.removeAttribute?.('src'); } catch (_) {}
            } else if (!isExpectedViewportImageSource(
              acceptedSourceBeforeSettlement,
              activeImage.src,
              window.location.href,
            )) {
              try { activeImage.src = new URL(acceptedSourceBeforeSettlement, window.location.href).href; } catch (_) {}
            }
          }
          return false;
        }
        acceptedImgSrcRef.current = pending.url;
        acceptedVisibleSurfaceRef.current = { surface: 'active-image', url: pending.url, generation: pending.generation };
        imgSrcRef.current = pending.url;
        setImgSrc(pending.url);
        const preview = scrubPreviewCanvasRef.current;
        if (preview) preview.style.display = 'none';
        pending.onPublished?.({ surface: 'active-image', surfaceElement: imgRef.current });
        return true;
      }
      cineResourceGovernor?.releaseDetachedImage(pending.image);
      const failed = pending.onFailure?.() === true;
      if (!failed) return false;
      const restoredSource = restoreAcceptedActiveImage();
      const retainedPreview = acceptedVisibleSurfaceRef.current?.surface === 'preview';
      return restoredSource || retainedPreview ? 'failed-retained' : 'failed-empty';
    } finally {
      try { pending.onSettled?.(); } catch (_) {}
    }
  }, [cineResourceGovernor, restoreAcceptedActiveImage]);

  // Submit only the nearest encoded neighbors. The page governor owns all
  // speculative fetch, stream, dedupe, fairness, cancellation, and concurrency.
  const preloadAdjacent = useCallback((s, wl, ser, direction, expectedGeneration = null) => {
    if (window.__KAIROS_CINE_COLD_SAMPLE_ACTIVE__ === true) return false;
    if (!ser?.studyInstanceUID || !ser?.seriesInstanceUID || !cacheEpoch) return false;
    const dir = direction || scrollDirectionRef.current || 1;
    const coordinator = interactiveRequestCoordinatorRef.current;
    const generation = expectedGeneration ?? coordinator.snapshot().visibleGeneration;
    if (!coordinator.isCurrentVisible(generation)) return false;
    const legacyCandidates = [];
    [dir, -dir].forEach((offset, order) => {
      const idx = s + offset;
      if (idx < 0 || idx > ser.maxSlice) return;
      const url = buildPetSliceThicknessRenderUrl({
        studyUID: ser.studyInstanceUID,
        seriesUID: ser.seriesInstanceUID,
        slice: idx,
        ww: wl.ww,
        wc: wl.wc,
        series: ser,
        thicknessMm: petSliceThicknessRef.current,
        cacheEpoch,
      });
      legacyCandidates.push({
        slice: idx,
        url,
        distance: Math.abs(offset),
        order,
      });
    });
    const candidates = prepareSpeculativeCandidates({
      kind: 'adjacent',
      generation,
      series: ser,
      candidates: legacyCandidates,
    });
    cineResourceGovernor?.replaceSpeculative({
      paneId: index,
      generation,
      kind: 'adjacent',
      direction: dir,
      candidates,
    });
    return candidates.length > 0;
  }, [cacheEpoch, cineResourceGovernor, index, prepareSpeculativeCandidates]);

  const submitInitialVisibleNeighbors = useCallback(({ slice, wl, series: target, generation, direction }) => (
    preloadAdjacent(slice, wl, target, direction, generation)
  ), [preloadAdjacent]);

  const preloadBurstProjectedFrames = useCallback(({
    slice: acceptedTargetSlice,
    wl,
    series: target,
    direction,
    consumedSteps,
    consumedInputCount,
    generation,
  }) => {
    if (!target?.studyInstanceUID || !target?.seriesInstanceUID || !cacheEpoch) return false;
    const requestedStepMagnitude = Math.abs(Math.trunc(Number(consumedSteps) || 0));
    if (requestedStepMagnitude < VIEWPORT_BURST_PROJECTED_MIN_STEPS) return false;
    const dir = Number(direction) < 0 ? -1 : 1;
    const effectiveWl = normalizeViewportWindowLevel(wl);
    const targetMaxSlice = Number.isSafeInteger(Number(target.maxSlice))
      ? Number(target.maxSlice)
      : maxSlice;
    const key = [
      target.studyInstanceUID,
      target.seriesInstanceUID,
      cacheEpoch,
      effectiveWl.ww,
      effectiveWl.wc,
      petSliceThicknessRef.current || '',
      dir,
    ].join('|');
    const now = window.performance?.now?.() || Date.now();
    const recent = burstProjectedPreloadRef.current;
    const continuingSession = recent.key === key && recent.direction === dir
      && Number.isSafeInteger(recent.stepMagnitude)
      && recent.stepMagnitude >= VIEWPORT_BURST_PROJECTED_MIN_STEPS
      && now - recent.at < VIEWPORT_BURST_PROJECTED_PRELOAD_TTL_MS;
    const stepMagnitude = continuingSession ? recent.stepMagnitude : requestedStepMagnitude;
    const settledStride = Math.max(1, Math.floor(stepMagnitude / 2));
    const transientStride = Math.max(settledStride, Math.floor((stepMagnitude * 5) / 6));

    // Keep the initial full lattice alive while browser frames are still
    // consuming paired inputs so the first singleton target is warmed well in
    // advance. Once accepted frames become singletons, reconcile that broad
    // work with a small next-two-burst frontier. Canonical-URL overlap keeps
    // its product claim and governor job/root ownership instead of being
    // destructively aborted and refetched.
    const projectionPlan = createViewportBurstProjectionPlan({
      currentTargetSlice: acceptedTargetSlice,
      direction: dir,
      stepMagnitude,
      settledStride,
      transientStride,
      maxSlice: targetMaxSlice,
      continuingSession,
      consumedInputCount,
    });
    if (projectionPlan.mode === 'preserve') {
      burstProjectedPreloadRef.current = {
        ...recent,
        at: now,
        slice: acceptedTargetSlice,
      };
      return true;
    }
    const projectedSlices = projectionPlan.slices;
    const legacyCandidates = projectedSlices
      .map((projectedSlice, order) => {
        const url = buildPetSliceThicknessRenderUrl({
          studyUID: target.studyInstanceUID,
          seriesUID: target.seriesInstanceUID,
          slice: projectedSlice,
          ww: effectiveWl.ww,
          wc: effectiveWl.wc,
          series: target,
          thicknessMm: petSliceThicknessRef.current,
          cacheEpoch,
        });
        return {
          slice: projectedSlice,
          url,
          distance: projectionPlan.mode === 'lattice'
            ? Math.abs(projectedSlice - acceptedTargetSlice)
            : order,
          order,
        };
      });
    if (legacyCandidates.length === 0) return false;
    const candidates = prepareSpeculativeCandidates({
      kind: 'burst-projected',
      generation,
      series: target,
      candidates: legacyCandidates,
      preserveExistingClaim: projectionPlan.mode === 'frontier',
    });
    const admission = projectionPlan.mode === 'frontier'
      ? cineResourceGovernor?.reconcileSpeculative
      : cineResourceGovernor?.replaceSpeculative;
    const accepted = admission?.({
      paneId: index,
      generation,
      kind: 'burst-projected',
      direction: dir,
      candidates,
    });
    if (!accepted || accepted.accepted < 1) return false;
    burstProjectedPreloadRef.current = {
      key,
      at: now,
      direction: dir,
      stepMagnitude,
      slice: acceptedTargetSlice,
    };
    return true;
  }, [cacheEpoch, cineResourceGovernor, index, maxSlice, prepareSpeculativeCandidates]);

  const preloadProjectedInteractiveFrames = useCallback((s, wl, ser, direction) => {
    const target = ser || seriesRef.current;
    if (!target) return false;
    const effectiveWl = normalizeViewportWindowLevel(wl);
    const dir = direction || scrollDirectionRef.current || 1;
    const key = [target.studyInstanceUID, target.seriesInstanceUID, s, effectiveWl.ww, effectiveWl.wc, petSliceThicknessRef.current || '', dir].join('|');
    const now = window.performance?.now?.() || Date.now();
    const recent = lastProjectedPreloadRef.current;
    if (recent.key === key && now - recent.at < VIEWPORT_RECENT_PROJECTED_PRELOAD_SUPPRESS_MS) return false;
    lastProjectedPreloadRef.current = { key, at: now };
    preloadAdjacent(s, effectiveWl, { ...target, maxSlice }, dir);
    return true;
  }, [maxSlice, preloadAdjacent]);

  const scheduleScrollIdlePreload = useCallback((s, wl, ser, direction, { publishLinked = true } = {}) => {
    const scheduledLinkedAuthorityEpoch = linkedAuthorityEpoch;
    if (scheduledLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current) return;
    const scheduledSlice = Number.isFinite(Number(s)) ? Number(s) : requestedSliceRef.current;
    const scheduledGeneration = interactiveRequestCoordinatorRef.current.snapshot().visibleGeneration;
    cancelScrollIdlePreload();
    const timerId = window.setTimeout(() => {
      if (scrollIdlePreloadTimerRef.current !== timerId) return;
      scrollIdlePreloadTimerRef.current = null;
      if (scheduledLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current) return;
      const current = interactiveRequestCoordinatorRef.current.snapshot();
      if (current.visibleGeneration !== scheduledGeneration
          || requestedSliceRef.current !== scheduledSlice) return;
      if (publishLinked) publishScrubSliceStateRef.current?.(scheduledSlice, { force: true });
      resumeActiveSeriesBrowserWarmup();
      preloadProjectedInteractiveFrames(scheduledSlice, wl, ser, direction);
    }, VIEWPORT_SCROLL_IDLE_PRELOAD_DELAY_MS);
    scrollIdlePreloadTimerRef.current = timerId;
  }, [cancelScrollIdlePreload, linkedAuthorityEpoch, preloadProjectedInteractiveFrames, resumeActiveSeriesBrowserWarmup]);

  const loadSlice = useCallback((s, wl, ser, options = {}) => {
    const notifyDirectDomSettled = () => {
      const callback = options.onDirectDomSettled;
      if (typeof callback !== 'function') return;
      try { callback(); } catch (_) {}
    };
    const requestOrigin = options.requestOrigin || VIEWPORT_REQUEST_ORIGIN.LOCAL;
    const linkedFollowerVisibleRequest = requestOrigin === VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER
      || requestOrigin === VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER_RETRY;
    const localLinkedVisibleRequest = requestOrigin === VIEWPORT_REQUEST_ORIGIN.LOCAL_LINKED;
    // Every exact visible clinical target stays high priority. Browser low-priority
    // hints can legally delay both the original and sole retry past settlement,
    // leaving final follower pixels stale. Source protection is instead enforced by
    // non-preempting governor admission and linked-only speculative suppression.
    const visibleFetchPriority = 'high';
    if (options.followerCoordinated !== true) followerFrameCoordinatorRef.current?.cancel();
    const target = ser || seriesRef.current;
    if (!target) {
      notifyDirectDomSettled();
      return;
    }
    if (window.__KAIROS_CINE_COLD_SAMPLE_ACTIVE__ === true) pauseActiveSeriesBrowserWarmup();
    const requestLinkedAuthorityEpoch = options.linkedAuthorityEpoch;
    const requestFollowerCommandGeneration = options.followerCommandGeneration;
    const requestFreezeAuthorityEpoch = options.freezeAuthorityEpoch;
    if (isLinkedAuthorityRequest(requestOrigin)
        && requestLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current) {
      notifyDirectDomSettled();
      return;
    }
    const requestSliceIntentAuthority = options.followerCoordinated === true
      ? sliceIntentAuthorityRef.current
      : claimSliceIntentAuthority(target, s);
    const requestSliceIntentTarget = Object.freeze({
      studyInstanceUID: target.studyInstanceUID,
      seriesInstanceUID: target.seriesInstanceUID,
      slice: s,
    });
    const requestSliceIntentStillCurrent = () => {
      const current = sliceIntentAuthorityRef.current;
      return current.generation === requestSliceIntentAuthority.generation
        && current.studyInstanceUID === requestSliceIntentTarget.studyInstanceUID
        && current.seriesInstanceUID === requestSliceIntentTarget.seriesInstanceUID
        && current.slice === requestSliceIntentTarget.slice;
    };
    const requestedInputCausality = options.inputCausality || null;
    const requestedWindowLevelState = options.windowLevelEvidenceState;
    const requestedWindowLevelInput = options.windowLevelEvidenceInput;
    const requestedCineRenderRequest = options.cineRenderRequest
      && options.cineRenderRequest === cineWheelRenderRequestRef.current
      ? options.cineRenderRequest
      : null;
    const requestedVisibleDirection = Number(options.direction || scrollDirectionRef.current || 1) < 0 ? -1 : 1;
    const projectedBurstVisibleRequest = Boolean(requestedCineRenderRequest && requestedInputCausality)
      && Math.abs(Math.trunc(Number(requestedInputCausality.consumedSteps) || 0))
        >= VIEWPORT_BURST_PROJECTED_MIN_STEPS;
    const projectedBurstSnapshot = burstProjectedPreloadRef.current;
    const preserveProjectedBurstWork = projectedBurstSnapshot.direction !== 0
      && projectedBurstSnapshot.direction === requestedVisibleDirection
      && (window.performance?.now?.() || Date.now()) - projectedBurstSnapshot.at
        < VIEWPORT_BURST_PROJECTED_PRELOAD_TTL_MS;
    const linkedFollowerEvidenceWorkload = linkedFollowerVisibleRequest
      && options.linkedCausality
      && isViewportEvidenceCinePhaseActive()
      ? Object.freeze({ phase: 'single-step', interaction: 'wheel' })
      : null;
    const requestedEvidenceWorkload = requestedCineRenderRequest
      ? Object.freeze({
        phase: 'cine',
        interaction: 'wheel',
        generation: requestedCineRenderRequest.seriesGeneration,
        cineRenderRequest: requestedCineRenderRequest,
      })
      : options.evidencePhase === 'single-step'
      && options.evidenceInteraction === 'wheel'
      ? Object.freeze({ phase: options.evidencePhase, interaction: options.evidenceInteraction })
      : options.evidencePhase === 'single-step'
        && options.evidenceInteraction === 'drag'
        && requestedWindowLevelState
        && requestedWindowLevelState === windowLevelEvidenceInteractionRef.current
        && requestedWindowLevelState.input === requestedWindowLevelInput
        ? Object.freeze({
          phase: options.evidencePhase,
          interaction: options.evidenceInteraction,
          generation: requestedWindowLevelState.generation,
          windowLevelEvidenceState: requestedWindowLevelState,
          windowLevelEvidenceInput: requestedWindowLevelInput,
        })
        : linkedFollowerEvidenceWorkload;
    const directDisplay = options.directDisplay === true && Boolean(acceptedVisibleSurfaceRef.current);
    if (requestOrigin !== VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER
        && requestOrigin !== VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER_RETRY) {
      cancelLinkedFollowerRetry();
      linkedFollowerRetryRef.current = { key: '', attempts: 0 };
    }
    const requestedMaxSlice = Number(options.resolvedMaxSlice);
    const effectiveMaxSlice = Number.isFinite(requestedMaxSlice)
      ? Math.max(0, Math.floor(requestedMaxSlice))
      : maxSlice;
    const priorLinkedIntentTakeover = activeLinkedIntentTakeoverRef.current;
    activeLinkedIntentTakeoverRef.current = null;
    try { priorLinkedIntentTakeover?.cancel?.(); } catch (_) {}
    // Transfer/cancel any prior caller-owned publication before generation or
    // direct-DOM source replacement can make its settler stale.
    if (preserveProjectedBurstWork) {
      invalidateSpeculativeClaimAuthority('adjacent');
      invalidateSpeculativeClaimAuthority('full-stack');
      invalidateSpeculativeClaimAuthority('linked-intent');
    } else {
      invalidateSpeculativeClaimAuthority();
    }
    cancelPendingViewportPublication();
    const coordinator = interactiveRequestCoordinatorRef.current;
    const visibleRequest = coordinator.beginVisible({
      slice: s,
      direction: requestedVisibleDirection,
    });
    const interactionPerformanceAuthority = options.evidenceInteraction === 'wheel'
      && options.interactionPerformanceAuthority
      ? Object.freeze({
        ...options.interactionPerformanceAuthority,
        visibleRequestGeneration: visibleRequest.generation,
      })
      : null;
    if (interactionPerformanceAuthority) {
      try {
        const monotonicTargetAtMs = window.performance?.now?.();
        onInteractionPerformanceEvent?.({
          type: 'target',
          viewportIndex: index,
          targetSlice: s,
          atMs: Number.isFinite(monotonicTargetAtMs) ? monotonicTargetAtMs : Date.now(),
          ...interactionPerformanceAuthority,
        });
      } catch (_) {
        // Interaction telemetry cannot block visible-request admission.
      }
    }
    evidenceWorkloadRef.current = requestedEvidenceWorkload
      ? Object.freeze({
        ...requestedEvidenceWorkload,
        generation: requestedEvidenceWorkload.generation || visibleRequest.generation,
        requestGeneration: visibleRequest.generation,
      })
      : null;
    activeLinkedAuthorityRequestRef.current = null;
    const ownsCurrentLinkedAuthority = () => (
      !isLinkedAuthorityRequest(requestOrigin)
      || requestLinkedAuthorityEpoch === linkedAuthorityEpochRef.current
    );
    const ownsCurrentFollowerCommand = () => ownsCurrentFollowerCommandGeneration({
      followerCoordinated: linkedFollowerVisibleRequest,
      requestGeneration: requestFollowerCommandGeneration,
      currentGeneration: followerCommandGenerationRef.current,
    });
    const ownsCurrentFreezeAuthority = () => (
      requestFreezeAuthorityEpoch == null
      || requestFreezeAuthorityEpoch === freezeAuthorityEpochRef.current
    );
    const ownsCurrentRequestAuthority = () => (
      ownsCurrentLinkedAuthority()
      && ownsCurrentFollowerCommand()
      && ownsCurrentFreezeAuthority()
      && requestSliceIntentStillCurrent()
    );
    const isRequestCurrent = () => (
      coordinator.isCurrentVisible(visibleRequest.generation)
      && ownsCurrentRequestAuthority()
    );
    requestedSliceRef.current = visibleRequest.slice;
    const effectiveWl = normalizeViewportWindowLevel(wl);
    if (!acceptedImgSrcRef.current
        && acceptedVisibleSurfaceRef.current?.surface !== 'preview'
        && options.followerCoordinated !== true) {
      firstSurfaceRecoveryRef.current = Object.freeze({
        studyInstanceUID: target.studyInstanceUID,
        seriesInstanceUID: target.seriesInstanceUID,
        slice: s,
        wl: effectiveWl,
        firstImageRetryAttempt: Number(options.firstImageRetryAttempt) || 0,
        requestOrigin,
      });
    }
    const url = buildPetSliceThicknessRenderUrl({ studyUID: target.studyInstanceUID, seriesUID: target.seriesInstanceUID, slice: s, ww: effectiveWl.ww, wc: effectiveWl.wc, series: target, thicknessMm: petSliceThicknessRef.current, cacheEpoch });
    const absoluteUrl = new URL(url, window.location.href).href;
    const linkedIntentTakeover = requestOrigin === VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER
      && imgRef.current
      ? cineResourceGovernor?.takeLinkedIntentImage?.({ paneId: index, url: absoluteUrl })
      : null;
    if (linkedIntentTakeover) activeLinkedIntentTakeoverRef.current = linkedIntentTakeover;
    cineResourceGovernor?.beginVisible({
      paneId: index,
      generation: visibleRequest.generation,
      direction: visibleRequest.direction,
      url: absoluteUrl,
      preemptSpeculative: !linkedFollowerVisibleRequest,
      preserveSpeculativeKinds: [
        ...(localLinkedVisibleRequest ? ['linked-intent'] : []),
        ...(preserveProjectedBurstWork ? ['burst-projected'] : []),
      ],
    });
    let governorSettled = false;
    const settleGovernorAccounting = (visible) => {
      if (governorSettled) return false;
      governorSettled = true;
      cineResourceGovernor?.settleVisible({
        paneId: index,
        generation: visibleRequest.generation,
        url: absoluteUrl,
        ok: visible,
      });
      return true;
    };
    const clearLinkedAuthorityRequest = () => {
      if (activeLinkedAuthorityRequestRef.current?.generation === visibleRequest.generation) {
        activeLinkedAuthorityRequestRef.current = null;
      }
    };
    const rejectStaleLinkedAuthorityRequest = () => {
      if (!coordinator.isCurrentVisible(visibleRequest.generation)) return false;
      if (ownsCurrentRequestAuthority()) return false;
      cancelPendingViewportPublication();
      coordinator.abortVisibleImage();
      settleGovernorAccounting(false);
      if (!coordinator.failVisible({ generation: visibleRequest.generation })) return false;
      requestedSliceRef.current = sliceRef.current;
      if (Number.isFinite(sliceRef.current)) sliceSliderRef.current?.setLiveSlice(sliceRef.current);
      cancelScrollIdlePreload();
      scrubDisplayPendingRef.current = false;
      restoreAcceptedActiveImage();
      const hasAcceptedSurface = Boolean(acceptedImgSrcRef.current)
        || acceptedVisibleSurfaceRef.current?.surface === 'preview';
      setLoading(!hasAcceptedSurface);
      setImgError(false);
      clearLinkedAuthorityRequest();
      return true;
    };
    if (isLinkedAuthorityRequest(requestOrigin) || requestFreezeAuthorityEpoch != null) {
      activeLinkedAuthorityRequestRef.current = {
        generation: visibleRequest.generation,
        authorityEpoch: requestLinkedAuthorityEpoch,
        freezeAuthorityEpoch: requestFreezeAuthorityEpoch,
        reject: rejectStaleLinkedAuthorityRequest,
      };
    }
    const acceptVisibleRequest = ({ surface = 'active-image',
      surfaceElement = surface === 'preview' ? scrubPreviewCanvasRef.current : imgRef.current,
      deferVisibleState = surface === 'preview',
      displayUrl = url,
      evidenceOperation = null,
      evidenceLineage = null,
      windowLevelEvidenceWorkload = null,
    } = {}) => {
      if (!coordinator.isCurrentVisible(visibleRequest.generation)) return false;
      if (!ownsCurrentRequestAuthority()) {
        rejectStaleLinkedAuthorityRequest();
        return false;
      }
      if (!coordinator.acceptVisible({ generation: visibleRequest.generation, slice: s })) return false;
      if (projectedBurstVisibleRequest) {
        preloadBurstProjectedFrames({
          slice: visibleRequest.slice,
          wl: effectiveWl,
          series: { ...target, maxSlice: effectiveMaxSlice },
          direction: visibleRequest.direction,
          consumedSteps: requestedInputCausality.consumedSteps,
          consumedInputCount: requestedInputCausality.consumedInputCount,
          generation: visibleRequest.generation,
        });
      }
      firstSurfaceRecoveryRef.current = null;
      clearLinkedAuthorityRequest();
      settleGovernorAccounting(true);
      requestedSliceRef.current = s;
      acceptedVisibleSurfaceRef.current = {
        surface,
        url,
        displayUrl,
        generation: visibleRequest.generation,
        evidenceOperation,
        evidenceLineage,
        inputCausality: options.inputCausality || null,
        linkedCausality: options.linkedCausality || (isLocalLinkedRequest(requestOrigin)
          && localLinkedCommandCausalityRef.current?.targetSlice === s
          ? localLinkedCommandCausalityRef.current : null),
        windowLevelEvidenceInput: windowLevelEvidenceWorkload?.windowLevelEvidenceInput || null,
        windowLevelEvidenceWorkload,
      };
      if (surface === 'active-image') {
        const previousEvidenceOperation = acceptedEvidenceOperationRef.current;
        acceptedEvidenceOperationRef.current = evidenceOperation;
        if (pendingEvidenceOperationRef.current === evidenceOperation) pendingEvidenceOperationRef.current = null;
        acceptedImgSrcRef.current = displayUrl;
        imgSrcRef.current = displayUrl;
        const preview = scrubPreviewCanvasRef.current;
        if (preview) preview.style.display = 'none';
        if (previousEvidenceOperation && previousEvidenceOperation !== evidenceOperation) {
          previousEvidenceOperation.release();
        }
      }
      publishObserverAcceptedVisible({
        surface,
        surfaceElement: surfaceElement || (surface === 'preview' ? scrubPreviewCanvasRef.current : imgRef.current),
      });
      if (!deferVisibleState) {
        publishVisibleSliceStateRef.current?.(s, {
          requestOrigin,
          interactionPerformanceAuthority,
        });
      }
      if (options.initialVisibleNeighbors === true) {
        submitInitialVisibleNeighbors({
          slice: s,
          wl: effectiveWl,
          series: { ...target, maxSlice: effectiveMaxSlice },
          generation: visibleRequest.generation,
          direction: visibleRequest.direction,
        });
      }
      return true;
    };
    const failVisibleRequest = () => {
      if (!coordinator.isCurrentVisible(visibleRequest.generation)) return false;
      if (!ownsCurrentRequestAuthority()) return rejectStaleLinkedAuthorityRequest();
      settleGovernorAccounting(false);
      if (!coordinator.failVisible({ generation: visibleRequest.generation })) return false;
      clearLinkedAuthorityRequest();
      const acceptedSlice = sliceRef.current;
      requestedSliceRef.current = acceptedSlice;
      if ((requestOrigin === VIEWPORT_REQUEST_ORIGIN.LOCAL
          || requestOrigin === VIEWPORT_REQUEST_ORIGIN.LOCAL_NAVIGATION
          || isLocalLinkedRequest(requestOrigin))
          && scrollFrameRef.current == null) {
        scrollPendingRef.current = 0;
        scrollPendingRapidBurstRef.current = false;
        lastWheelEventAtRef.current = 0;
      }
      if (Number.isFinite(acceptedSlice)) sliceSliderRef.current?.setLiveSlice(acceptedSlice);
      cancelScrollIdlePreload();
      if (linkedScrollEnabled
          && isLocalLinkedRequest(requestOrigin)
          && effectiveOnLinkedSliceRequest
          && Number.isFinite(acceptedSlice)
          && lastLinkedPublishedSliceRef.current != null
          && lastLinkedPublishedSliceRef.current !== acceptedSlice) {
        lastLinkedPublishedSliceRef.current = acceptedSlice;
        effectiveOnLinkedSliceRequest(index, acceptedSlice, effectiveMaxSlice + 1, { source: 'linked_rollback' });
      }
      if (linkedFollowerVisibleRequest && requestSliceIntentStillCurrent()) {
        const retryKey = `${target.studyInstanceUID}:${target.seriesInstanceUID}:${s}`;
        const retryState = linkedFollowerRetryRef.current;
        const failedGeneration = visibleRequest.generation;
        const failedFollowerCommandGeneration = requestFollowerCommandGeneration;
        const failedFollowerCommand = options.followerCommand;
        if (retryState.key === retryKey) {
          retryState.attempts += 1;
          const retryDelayMs = nextFollowerRetryDelayMs(retryState.attempts);
          cancelLinkedFollowerRetry();
          if (!Number.isFinite(retryDelayMs)) return true;
          linkedFollowerRetryTimerRef.current = window.setTimeout(() => {
            linkedFollowerRetryTimerRef.current = null;
            if (requestLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current) return;
            if (failedFollowerCommandGeneration !== followerCommandGenerationRef.current) return;
            if (!requestSliceIntentStillCurrent()) return;
            const current = interactiveRequestCoordinatorRef.current.snapshot();
            if (linkedFollowerRetryRef.current.key !== retryKey
                || current.visibleGeneration !== failedGeneration
                || current.visiblePending
                || current.acceptedVisibleSlice === s
                || seriesRef.current?.seriesInstanceUID !== target.seriesInstanceUID
                || failedFollowerCommand?.channel !== 'linked') return;
            followerFrameCoordinatorRef.current?.schedule({
              ...failedFollowerCommand,
              requestOrigin: VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER_RETRY,
              followerCommandGeneration: failedFollowerCommandGeneration,
              preemptible: true,
            });
          }, retryDelayMs);
        }
      }
      return true;
    };
    let observerPublicationEmitted = false;
    let cineObservationRequested = false;
    let cinePublicationEmitted = false;
    const requestCinePaintObservation = accepted => {
      if (cineObservationRequested || cinePublicationEmitted
          || evidenceWorkload?.phase !== 'cine') return false;
      const publication = acceptViewportCineRenderPublication(
        evidenceWorkload.cineRenderRequest.command,
        accepted.evidenceOperation,
        visibleRequest.generation,
      );
      cineObservationRequested = Boolean(publication && observeViewportCinePaint(
        publication,
        accepted.evidenceOperation,
        Object.freeze({
          onAuthenticatedPaint: paint => {
            const currentAccepted = acceptedVisibleSurfaceRef.current;
            if (paint?.slice !== s || paint?.requestGeneration !== visibleRequest.generation
                || currentAccepted?.generation !== visibleRequest.generation
                || currentAccepted?.url !== url || !cineRenderRequestIsCurrent()) return false;
            const renderedPublished = publishAuthenticatedRenderedSliceDom({
              root: viewportRootRef.current,
              slice: s,
              generation: visibleRequest.generation,
            });
            if (!renderedPublished) return false;
            cinePublicationEmitted = true;
            if (cineWheelRenderRequestRef.current === evidenceWorkload.cineRenderRequest) {
              cineWheelPendingRenderOrdinalRef.current = null;
            }
            return true;
          },
        }),
      ));
      return cineObservationRequested;
    };
    const retryCineObservationAfterEvidencePublication = evidenceOperation => {
      const accepted = acceptedVisibleSurfaceRef.current;
      if (!observerPublicationEmitted || accepted?.evidenceOperation !== evidenceOperation
          || accepted?.generation !== visibleRequest.generation || accepted?.url !== url) return false;
      return requestCinePaintObservation(accepted);
    };
    const publishObserverAcceptedVisible = ({ surface, surfaceElement }) => {
      if (window.__KAIROS_CINE_OBSERVER_ACTIVE__ !== true) return false;
      const accepted = acceptedVisibleSurfaceRef.current;
      const acceptedMatches = accepted?.generation === visibleRequest.generation
        && accepted?.url === url && accepted?.surface === surface;
      const preview = scrubPreviewCanvasRef.current;
      const revealed = surface === 'preview'
        ? surfaceElement === preview && preview?.style.display !== 'none'
        : surfaceElement === imgRef.current && Boolean(surfaceElement?.complete && surfaceElement?.naturalWidth > 0)
          && (!preview || preview.style.display === 'none');
      if (!acceptedMatches || !revealed) return false;
      let published = false;
      if (!observerPublicationEmitted) {
        if (!cineObservationRequested && !cinePublicationEmitted) {
          const renderAuthorityCleared = clearAuthenticatedRenderedSliceDom(viewportRootRef.current);
          if (!renderAuthorityCleared) return false;
        }
        published = publishAcceptedVisibleFrameDom({
          root: viewportRootRef.current,
          surfaceElement,
          slice: s,
          generation: visibleRequest.generation,
          surface,
          sourceUrl: absoluteUrl,
          requestOrigin,
          observerActive: true,
          revealed: true,
          evidenceLineage: accepted.evidenceLineage,
          inputCausality: accepted.inputCausality,
          linkedCausality: accepted.linkedCausality,
        });
        if (published) observerPublicationEmitted = true;
      } else if (surface === 'active-image' && !transferAcceptedVisibleFrameDom({
        surfaceElement,
        slice: s,
        generation: visibleRequest.generation,
        surface,
      })) {
        return false;
      }
      if (linkedFollowerVisibleRequest && observerPublicationEmitted) {
        const requestRoot = viewportRootRef.current;
        const acceptedSurface = accepted;
        linkedFollowerPaintAuthorityRef.current.schedule({
          root: requestRoot,
          slice: s,
          generation: visibleRequest.generation,
          isCurrent: () => {
            const currentAccepted = acceptedVisibleSurfaceRef.current;
            return viewportMountedRef.current === true
              && window.__KAIROS_CINE_OBSERVER_ACTIVE__ === true
              && viewportRootRef.current === requestRoot
              && requestRoot?.isConnected === true
              && coordinator.isCurrentVisible(visibleRequest.generation)
              && ownsCurrentRequestAuthority()
              && requestedSliceRef.current === s
              && sliceRef.current === s
              && currentAccepted === acceptedSurface
              && currentAccepted?.surface === surface
              && currentAccepted?.url === url
              && currentAccepted?.generation === visibleRequest.generation
              && readCanonicalUnsignedDomAttribute(surfaceElement, 'data-viewport-accepted-slice-current') === s
              && readCanonicalUnsignedDomAttribute(surfaceElement, 'data-viewport-accepted-generation-current') === visibleRequest.generation
              && surfaceElement.getAttribute?.('data-viewport-accepted-surface-current') === surface;
          },
          isAcceptedVisible: () => {
            if (acceptedVisibleSurfaceRef.current !== acceptedSurface
                || surfaceElement?.isConnected !== true) return false;
            const currentPreview = scrubPreviewCanvasRef.current;
            if (surface === 'preview') {
              return surfaceElement === currentPreview
                && currentPreview?.style.display !== 'none'
                && surfaceElement.width > 0 && surfaceElement.height > 0;
            }
            return surface === 'active-image'
              && surfaceElement === imgRef.current
              && surfaceElement.complete === true
              && surfaceElement.naturalWidth > 0 && surfaceElement.naturalHeight > 0
              && isExpectedViewportImageSource(
                acceptedSurface.displayUrl || url,
                surfaceElement.src,
                window.location.href,
              )
              && (!currentPreview || currentPreview.style.display === 'none');
          },
          publish: publishAuthenticatedRenderedSliceDom,
        });
      }
      const operationAccepted = observerPublicationEmitted
        && accepted.evidenceOperation?.acceptedVisible(surface) === true;
      if (operationAccepted) requestCinePaintObservation(accepted);
      if (operationAccepted && accepted.windowLevelEvidenceInput) {
        joinWindowLevelAcceptedPublication({
          workload: accepted.windowLevelEvidenceWorkload,
          renderEvidence: accepted.evidenceOperation,
          surface,
          surfaceElement,
        });
      }
      return published;
    };
    const settleGovernorVisible = (visible, acceptedOptions = {}) => {
      return visible ? acceptVisibleRequest(acceptedOptions) : failVisibleRequest();
    };
    const evidenceWorkload = evidenceWorkloadRef.current?.requestGeneration === visibleRequest.generation
      ? evidenceWorkloadRef.current
      : null;
    const cineRenderRequestIsCurrent = () => {
      if (evidenceWorkload?.phase !== 'cine') return true;
      const request = evidenceWorkload.cineRenderRequest;
      if (evidenceWorkload.requestGeneration !== visibleRequest.generation
          || request !== cineWheelRenderRequestRef.current
          || typeof request?.isCurrent !== 'function') return false;
      try { return request.isCurrent() === true; } catch (_) { return false; }
    };
    const failWindowLevelEvidenceRender = () => {
      const state = evidenceWorkload?.windowLevelEvidenceState;
      if (evidenceWorkload?.interaction !== 'drag' || !state
          || windowLevelEvidenceInteractionRef.current !== state
          || state.input !== evidenceWorkload.windowLevelEvidenceInput) return false;
      closeWindowLevelEvidenceInteraction('failed', state);
      windowLevelDragCoordinatorRef.current?.cancel();
      return true;
    };
    const transferAcceptedEvidencePixelsToPreview = ({ evidenceOperation, displayUrl, activeImage }) => {
      const accepted = acceptedVisibleSurfaceRef.current;
      if (!evidenceOperation || acceptedEvidenceOperationRef.current !== evidenceOperation
          || accepted?.evidenceOperation !== evidenceOperation
          || accepted?.generation !== visibleRequest.generation
          || !activeImage || !isExpectedViewportImageSource(displayUrl, activeImage.src, window.location.href)) return false;
      const preview = scrubPreviewCanvasRef.current;
      let metadataTransferred = false;
      const visible = Boolean(drawScrubPreviewImageRef.current?.(activeImage, {
        beforeReveal: () => {
          metadataTransferred = transferAcceptedVisibleFrameDom({
            surfaceElement: preview,
            slice: visibleRequest.slice,
            generation: visibleRequest.generation,
            surface: 'preview',
          });
          return metadataTransferred;
        },
      }));
      if (!visible || !metadataTransferred) return false;
      acceptedEvidenceOperationRef.current = null;
      acceptedImgSrcRef.current = null;
      imgSrcRef.current = null;
      acceptedVisibleSurfaceRef.current = {
        ...accepted,
        surface: 'preview',
        evidenceOperation: null,
      };
      try { activeImage.removeAttribute?.('src'); } catch (_) {}
      if (viewportMountedRef.current) setImgSrc(null);
      return true;
    };
    const evidenceBridgeActive = directDisplay
      && ((evidenceWorkload?.phase === 'single-step'
        && (evidenceWorkload?.interaction === 'wheel' || evidenceWorkload?.interaction === 'drag'))
        || (evidenceWorkload?.phase === 'cine'
          && evidenceWorkload?.interaction === 'wheel'
          && cineRenderRequestIsCurrent()))
      && isViewportEvidenceNetworkBridgeActive();
    const renderBinding = evidenceWorkload?.phase === 'cine'
      ? evidenceWorkload.cineRenderRequest.binding
      : evidenceWorkload?.interaction === 'drag'
      ? Object.freeze({
        input: evidenceWorkload.windowLevelEvidenceInput,
        windowLevel: Object.freeze({ ww: effectiveWl.ww, wc: effectiveWl.wc }),
      })
      : null;
    const publishDecodedImage = (decodedImage, {
      retainOnSuccess = false,
      displayUrl = absoluteUrl,
      evidenceOperation = null,
      evidenceLineage = null,
      evidenceCacheRootRef = null,
      windowLevelEvidenceWorkload = null,
    } = {}) => {
      let decodedFollowerSettled = false;
      let decodedFollowerSettlementTimer = null;
      const settleDecodedFollower = () => {
        if (decodedFollowerSettled) return false;
        decodedFollowerSettled = true;
        if (decodedFollowerSettlementTimer != null) {
          window.clearTimeout(decodedFollowerSettlementTimer);
          decodedFollowerSettlementTimer = null;
        }
        notifyDirectDomSettled();
        return true;
      };
      const failDecodedFollowerPublication = () => {
        const requestStillCurrent = isRequestCurrent();
        cancelActiveViewportImageSettlement(imgRef.current);
        if (requestStillCurrent) {
          failVisibleRequest();
          restoreAcceptedActiveImage();
          setLoading(false);
          if (!acceptedVisibleSurfaceRef.current) setImgError(true);
        }
        settleDecodedFollower();
      };
      if (typeof options.onDirectDomSettled === 'function') {
        decodedFollowerSettlementTimer = window.setTimeout(
          failDecodedFollowerPublication,
          VIEWPORT_FOLLOWER_IMAGE_SETTLEMENT_TIMEOUT_MS,
        );
      }
      imgSrcRef.current = displayUrl;
      scrubDisplayPendingRef.current = true;
      const published = displayDecodedViewportFrame({
        decodedImage,
        activeImage: imgRef.current,
        absoluteUrl: displayUrl,
        isCurrent: () => isRequestCurrent(),
        drawPreview: (previewImage, { beforeReveal } = {}) => {
          let previewAccepted = false;
          const visible = Boolean(drawScrubPreviewImageRef.current?.(previewImage, {
            beforeReveal: () => {
              previewAccepted = beforeReveal?.() !== false;
              return previewAccepted;
            },
          }));
          if (visible && previewAccepted) {
            if (window.__KAIROS_CINE_OBSERVER_ACTIVE__ === true) {
              publishVisibleFrameDom({
                root: viewportRootRef.current,
                canvas: scrubPreviewCanvasRef.current,
                slice: s,
                generation: visibleRequest.generation,
              });
              publishObserverAcceptedVisible({ surface: 'preview', surfaceElement: scrubPreviewCanvasRef.current });
            }
            publishVisibleSliceStateRef.current?.(s, { requestOrigin, interactionPerformanceAuthority });
          }
          return visible;
        },
        onVisible: ({ surface } = {}) => acceptVisibleRequest({
          surface,
          surfaceElement: surface === 'preview' ? scrubPreviewCanvasRef.current : imgRef.current,
          deferVisibleState: surface === 'preview',
          displayUrl,
          evidenceOperation,
          evidenceLineage,
          windowLevelEvidenceWorkload,
        }),
        onCancelled: () => {
          if (retainOnSuccess) {
            cineResourceGovernor?.releaseDetachedImage(decodedImage);
            const provisionalRoot = evidenceCacheRootRef?.current;
            if (evidenceCacheRootRef) evidenceCacheRootRef.current = null;
            provisionalRoot?.evict();
          }
          settleDecodedFollower();
        },
        onSettled: ({ visible }) => {
          const provisionalRoot = evidenceCacheRootRef?.current;
          if (evidenceCacheRootRef) evidenceCacheRootRef.current = null;
          if (!visible && evidenceOperation) {
            if (pendingEvidenceOperationRef.current === evidenceOperation) {
              pendingEvidenceOperationRef.current = null;
            }
            evidenceOperation.cancel();
          }
          if (!isRequestCurrent()) {
            provisionalRoot?.evict();
            settleDecodedFollower();
            return;
          }
          scrubDisplayPendingRef.current = false;
          if (visible && retainOnSuccess) {
            const retained = cineResourceGovernor?.retainDecoded({ url: absoluteUrl, image: decodedImage });
            if (retained && !retained.retained) cineResourceGovernor?.releaseDetachedImage(decodedImage);
            if (provisionalRoot) {
              const registered = retained?.retained === true
                && evidenceOperation?.transferDecodedCacheRoot(root => (
                  cineResourceGovernor?.registerDecodedEvidenceRoot(absoluteUrl, root)
                )) === true;
              if (!registered) provisionalRoot.evict();
            }
          } else if (!visible && retainOnSuccess) {
            cineResourceGovernor?.releaseDetachedImage(decodedImage);
            provisionalRoot?.evict();
          }
          settleGovernorVisible(visible);
          if (visible) {
            const previousEvidenceOperation = acceptedEvidenceOperationRef.current;
            acceptedEvidenceOperationRef.current = evidenceOperation;
            if (pendingEvidenceOperationRef.current === evidenceOperation) pendingEvidenceOperationRef.current = null;
            acceptedImgSrcRef.current = displayUrl;
            acceptedVisibleSurfaceRef.current = {
              surface: 'active-image',
              url,
              displayUrl,
              generation: visibleRequest.generation,
              evidenceOperation,
              evidenceLineage,
              windowLevelEvidenceInput: windowLevelEvidenceWorkload?.windowLevelEvidenceInput || null,
              windowLevelEvidenceWorkload,
            };
            imgSrcRef.current = displayUrl;
            const preview = scrubPreviewCanvasRef.current;
            if (preview) preview.style.display = 'none';
            if (previousEvidenceOperation && previousEvidenceOperation !== evidenceOperation) {
              previousEvidenceOperation.release();
            }
            publishObserverAcceptedVisible({ surface: 'active-image', surfaceElement: imgRef.current });
          } else {
            setLoading(false);
            if (!imgRef.current?.src && acceptedVisibleSurfaceRef.current?.surface !== 'preview') setImgError(true);
          }
          if (!visible) failWindowLevelEvidenceRender();
          settleDecodedFollower();
        },
      });
      if (!published) {
        if (retainOnSuccess) cineResourceGovernor?.releaseDetachedImage(decodedImage);
        failDecodedFollowerPublication();
      }
      return published;
    };
    const linkedIntentDecodedImage = requestOrigin === VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER
      && !requestedEvidenceWorkload
      ? cineResourceGovernor?.getDecoded(absoluteUrl)
      : null;
    if (linkedIntentDecodedImage && imgRef.current
        && linkedIntentDecodedImage !== linkedIntentTakeover?.image) {
      if (activeLinkedIntentTakeoverRef.current === linkedIntentTakeover) {
        activeLinkedIntentTakeoverRef.current = null;
      }
      try { linkedIntentTakeover?.cancel?.(); } catch (_) {}
      Promise.resolve().then(() => {
        if (!isRequestCurrent()) {
          notifyDirectDomSettled();
          return;
        }
        publishDecodedImage(linkedIntentDecodedImage);
      });
      return;
    }
    if (linkedIntentTakeover && imgRef.current) {
      Promise.resolve(linkedIntentTakeover.ready).then(decodedImage => {
        if (activeLinkedIntentTakeoverRef.current !== linkedIntentTakeover) {
          if (decodedImage) cineResourceGovernor?.releaseDetachedImage(decodedImage);
          return;
        }
        activeLinkedIntentTakeoverRef.current = null;
        if (!decodedImage) {
          if (isRequestCurrent()) failVisibleRequest();
          notifyDirectDomSettled();
          return;
        }
        if (!isRequestCurrent()) {
          cineResourceGovernor?.releaseDetachedImage(decodedImage);
          notifyDirectDomSettled();
          return;
        }
        publishDecodedImage(decodedImage, { retainOnSuccess: true });
      }, () => {
        if (activeLinkedIntentTakeoverRef.current !== linkedIntentTakeover) return;
        activeLinkedIntentTakeoverRef.current = null;
        if (isRequestCurrent()) failVisibleRequest();
        notifyDirectDomSettled();
      });
      return;
    }
    const requestFollowerCommand = options.followerCommand;
    const latestLinkedIntent = latestLinkedIntentRef.current;
    const preservesNewerLinkedIntentPreparation = requestFollowerCommand?.channel === 'linked'
      && Number.isSafeInteger(requestFollowerCommand.commandId)
      && Number.isSafeInteger(latestLinkedIntent?.commandId)
      && latestLinkedIntent.commandId > requestFollowerCommand.commandId
      && latestLinkedIntent.targetSlice !== s;
    const linkedFollowerNeedsOrdinaryFallback = requestOrigin === VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER
      && !linkedIntentDecodedImage
      && !linkedIntentTakeover
      && !preservesNewerLinkedIntentPreparation;
    if (linkedFollowerNeedsOrdinaryFallback) {
      // Cancel the exact speculative claim before the ordinary visible Image is
      // created. Cancelling it after visible I/O starts can abort Chromium's
      // coalesced same-URL request and leave every follower on prior pixels.
      invalidateSpeculativeClaimAuthority('linked-intent');
      cineResourceGovernor?.removeSpeculative({ paneId: index, kind: 'linked-intent' });
    }
    if (evidenceBridgeActive && imgRef.current) {
      const evidenceCacheRoot = cineResourceGovernor?.getDecodedEvidenceRoot(absoluteUrl);
      if (evidenceCacheRoot) {
        const requestViewportRoot = viewportRootRef.current;
        const warmBegin = beginViewportEvidenceDecodedCacheHit({
          benchmarkMode: true,
          context: Object.freeze({
            viewport: index,
            generation: evidenceWorkload.generation,
            slice: visibleRequest.slice,
            phase: evidenceWorkload.phase,
            interaction: evidenceWorkload.interaction,
            temperature: 'warm',
            deliverySource: 'decoded-cache',
          }),
          canonicalUrl: absoluteUrl,
          cacheRoot: evidenceCacheRoot,
          isMounted: () => viewportMountedRef.current === true,
          isViewportCurrent: () => viewportRootRef.current === requestViewportRoot
            && requestViewportRoot?.isConnected === true,
          isGenerationCurrent: () => coordinator.isCurrentVisible(visibleRequest.generation),
          isSliceCurrent: () => requestedSliceRef.current === visibleRequest.slice,
          isRenderAuthorityCurrent: () => isRequestCurrent()
            && cineRenderRequestIsCurrent()
            && seriesRef.current?.studyInstanceUID === target.studyInstanceUID
            && seriesRef.current?.seriesInstanceUID === target.seriesInstanceUID,
        }, renderBinding);
        if (warmBegin?.outcome === 'terminal-warm-attempt'
            || warmBegin?.outcome === 'terminal-stale-no-fallback') {
          failVisibleRequest();
          restoreAcceptedActiveImage();
          failWindowLevelEvidenceRender();
          notifyDirectDomSettled();
          return;
        }
        const evidenceOperation = warmBegin?.outcome === 'warm-operation'
          ? warmBegin.operation
          : null;
        if (evidenceOperation) {
          pendingEvidenceOperationRef.current = evidenceOperation;
          let publishedDisplayUrl = null;
          const evidenceLineage = evidenceOperation.lineage();
          const cacheRootOrdinal = evidenceLineage.cacheRootOrdinal;
          const published = Number.isSafeInteger(cacheRootOrdinal) && evidenceOperation.publish(({ image }) => {
            const rootDisplayUrl = typeof image?.src === 'string' ? image.src : '';
            if (!rootDisplayUrl) return false;
            publishedDisplayUrl = rootDisplayUrl;
            return publishDecodedImage(image, {
              displayUrl: rootDisplayUrl,
              evidenceOperation,
              evidenceLineage,
              windowLevelEvidenceWorkload: evidenceWorkload,
            });
          }, () => {
            const activeImage = imgRef.current;
            if (publishedDisplayUrl && activeImage
                && isExpectedViewportImageSource(publishedDisplayUrl, activeImage.src, window.location.href)) {
              if (transferAcceptedEvidencePixelsToPreview({
                evidenceOperation,
                displayUrl: publishedDisplayUrl,
                activeImage,
              })) return true;
              if (acceptedEvidenceOperationRef.current === evidenceOperation) {
                acceptedEvidenceOperationRef.current = null;
                acceptedImgSrcRef.current = null;
                imgSrcRef.current = null;
                if (acceptedVisibleSurfaceRef.current?.evidenceOperation === evidenceOperation) {
                  acceptedVisibleSurfaceRef.current = null;
                }
                try { activeImage.removeAttribute?.('src'); } catch (_) {}
                if (viewportMountedRef.current) setImgSrc(null);
              } else {
                restoreAcceptedActiveImage();
              }
            }
            return !publishedDisplayUrl || !activeImage
              || !isExpectedViewportImageSource(publishedDisplayUrl, activeImage.src, window.location.href);
          });
          if (published) {
            retryCineObservationAfterEvidencePublication(evidenceOperation);
            return;
          }
          if (pendingEvidenceOperationRef.current === evidenceOperation) pendingEvidenceOperationRef.current = null;
          failWindowLevelEvidenceRender();
          notifyDirectDomSettled();
          return;
        }
      }
    } else {
      const cachedDecodedImage = linkedIntentDecodedImage || cineResourceGovernor?.getDecoded(absoluteUrl);
      if (options.evidenceInteraction === 'wheel') {
        try {
          onInteractionPerformanceEvent?.({
            type: 'cache',
            viewportIndex: index,
            hit: Boolean(cachedDecodedImage),
            ...interactionPerformanceAuthority,
          });
        } catch (_) {
          // Cache telemetry is observational and fail-open.
        }
      }
      if (cachedDecodedImage && imgRef.current) {
        Promise.resolve().then(() => {
          if (!isRequestCurrent()) {
            notifyDirectDomSettled();
            return;
          }
          publishDecodedImage(cachedDecodedImage);
        });
        return;
      }
    }
    if (evidenceBridgeActive) {
      const requestViewportRoot = viewportRootRef.current;
      const evidenceOperation = beginViewportEvidenceNetworkRequest({
        benchmarkMode: true,
        context: Object.freeze({
          viewport: index,
          generation: evidenceWorkload.generation,
          slice: visibleRequest.slice,
          phase: evidenceWorkload.phase,
          interaction: evidenceWorkload.interaction,
          temperature: 'cold',
          deliverySource: 'network',
        }),
        requestUrl: url,
        currentOrigin: window.location.href,
        fetchImpl: window.fetch,
        blobFactory: (parts, blobOptions) => new window.Blob(parts, blobOptions),
        createObjectURL: blob => window.URL.createObjectURL(blob),
        revokeObjectURL: objectUrl => window.URL.revokeObjectURL(objectUrl),
        createImage: () => new window.Image(),
        isMounted: () => viewportMountedRef.current === true,
        isViewportCurrent: () => viewportRootRef.current === requestViewportRoot
          && requestViewportRoot?.isConnected === true,
        isGenerationCurrent: () => coordinator.isCurrentVisible(visibleRequest.generation),
        isSliceCurrent: () => requestedSliceRef.current === visibleRequest.slice,
        isRenderAuthorityCurrent: () => isRequestCurrent()
          && cineRenderRequestIsCurrent()
          && seriesRef.current?.studyInstanceUID === target.studyInstanceUID
          && seriesRef.current?.seriesInstanceUID === target.seriesInstanceUID,
      }, renderBinding);
      if (evidenceOperation) {
        pendingEvidenceOperationRef.current = evidenceOperation;
        let publishedObjectUrl = null;
        let sourcePublished = false;
        const evidenceCacheRootRef = { current: null };
        evidenceOperation.ready().then(ready => {
          if (ready !== true || pendingEvidenceOperationRef.current !== evidenceOperation || !isRequestCurrent()) {
            if (pendingEvidenceOperationRef.current === evidenceOperation) pendingEvidenceOperationRef.current = null;
            evidenceOperation.cancel();
            if (isRequestCurrent()) settleGovernorVisible(false);
            failWindowLevelEvidenceRender();
            notifyDirectDomSettled();
            return;
          }
          const published = evidenceOperation.publish(({ image, objectUrl }) => {
            publishedObjectUrl = objectUrl;
            return publishDecodedImage(image, {
              retainOnSuccess: true,
              displayUrl: objectUrl,
              evidenceOperation,
              evidenceLineage: evidenceOperation.lineage(),
              evidenceCacheRootRef,
              windowLevelEvidenceWorkload: evidenceWorkload,
            });
          }, () => {
            const activeImage = imgRef.current;
            if (publishedObjectUrl && activeImage
                && isExpectedViewportImageSource(publishedObjectUrl, activeImage.src, window.location.href)) {
              if (transferAcceptedEvidencePixelsToPreview({
                evidenceOperation,
                displayUrl: publishedObjectUrl,
                activeImage,
              })) return true;
              if (acceptedEvidenceOperationRef.current === evidenceOperation) {
                acceptedEvidenceOperationRef.current = null;
                acceptedImgSrcRef.current = null;
                imgSrcRef.current = null;
                if (acceptedVisibleSurfaceRef.current?.evidenceOperation === evidenceOperation) {
                  acceptedVisibleSurfaceRef.current = null;
                }
                try { activeImage.removeAttribute?.('src'); } catch (_) {}
                if (viewportMountedRef.current) setImgSrc(null);
              } else {
                restoreAcceptedActiveImage();
              }
            }
            return !publishedObjectUrl || !activeImage
              || !isExpectedViewportImageSource(publishedObjectUrl, activeImage.src, window.location.href);
          });
          sourcePublished = published;
          if (!published) {
            if (pendingEvidenceOperationRef.current === evidenceOperation) pendingEvidenceOperationRef.current = null;
            if (isRequestCurrent()) settleGovernorVisible(false);
            failWindowLevelEvidenceRender();
            notifyDirectDomSettled();
          } else {
            retryCineObservationAfterEvidencePublication(evidenceOperation);
            evidenceCacheRootRef.current = evidenceOperation.createDecodedCacheRoot({ canonicalUrl: absoluteUrl });
          }
        }).catch(() => {
          if (sourcePublished) return;
          if (pendingEvidenceOperationRef.current === evidenceOperation) pendingEvidenceOperationRef.current = null;
          evidenceOperation.cancel();
          if (isRequestCurrent()) settleGovernorVisible(false);
          failWindowLevelEvidenceRender();
          notifyDirectDomSettled();
        });
        return;
      }
      if (evidenceWorkload.interaction === 'drag') {
        failWindowLevelEvidenceRender();
        settleGovernorVisible(false);
        notifyDirectDomSettled();
        return;
      }
    }
    if (options.directDomDisplay) {
      // Continuous W/L is refs + DOM only: assigning the active image directly
      // avoids a React commit for every pointer event. Wait for load/error before
      // dispatching the next latest value so renderer work remains bounded.
      const image = imgRef.current;
      if (!image) {
        settleGovernorVisible(false);
        notifyDirectDomSettled();
        return;
      }
      if (image.src === absoluteUrl && image.complete && image.naturalWidth > 0) {
        Promise.resolve().then(() => {
          if (isRequestCurrent()) settleGovernorVisible(true);
          notifyDirectDomSettled();
        });
        return;
      }
      let settled = false;
      let timeoutId = null;
      const finish = (visible) => {
        if (settled) return;
        settled = true;
        image.removeEventListener('load', handleLoad);
        image.removeEventListener('error', handleError);
        if (timeoutId != null) window.clearTimeout(timeoutId);
        if (directDomSettlementCancelRef.current === cancel) directDomSettlementCancelRef.current = null;
        if (isRequestCurrent()) {
          if (!visible) restoreAcceptedActiveImage();
          settleGovernorVisible(visible);
        }
        notifyDirectDomSettled();
      };
      const cancel = () => {
        if (settled) return;
        settled = true;
        image.removeEventListener('load', handleLoad);
        image.removeEventListener('error', handleError);
        if (timeoutId != null) window.clearTimeout(timeoutId);
        if (directDomSettlementCancelRef.current === cancel) directDomSettlementCancelRef.current = null;
        notifyDirectDomSettled();
      };
      const handleLoad = () => finish(true);
      const handleError = () => finish(false);
      directDomSettlementCancelRef.current = cancel;
      image.addEventListener('load', handleLoad);
      image.addEventListener('error', handleError);
      timeoutId = window.setTimeout(() => finish(false), 1000);
      imgSrcRef.current = url;
      try {
        image.src = absoluteUrl;
      } catch (_) {
        finish(false);
      }
      return;
    }
    setImgError(false);
    // Don't clear current image — keep showing it until new one is ready.
    // If no image yet (first load), show loading.
    if (!imgRef.current?.src || !series) {
      setLoading(true);
    }
    if (directDisplay) {
      // Keep the current diagnostic pixels visible while a single detached Image
      // loads the newest cine/scrub target. The coordinator aborts the prior
      // detached request on every newer generation. Once decoded, publish through
      // the DOM/canvas compositor without forcing a React commit for every frame.
      const alreadyDisplayed = imgRef.current?.src === absoluteUrl && imgRef.current?.complete && imgRef.current?.naturalWidth > 0;
      if (alreadyDisplayed) {
        imgSrcRef.current = url;
        scrubDisplayPendingRef.current = false;
        Promise.resolve().then(() => {
          if (isRequestCurrent()) settleGovernorVisible(true);
          notifyDirectDomSettled();
        });
        return;
      }
      const image = new window.Image();
      try { image.fetchPriority = visibleFetchPriority; } catch (_) {}
      let followerSettlementTimer = null;
      let followerLaneSettled = false;
      const settleFollowerLane = () => {
        if (followerLaneSettled) return;
        followerLaneSettled = true;
        if (followerSettlementTimer != null) window.clearTimeout(followerSettlementTimer);
        followerSettlementTimer = null;
        notifyDirectDomSettled();
      };
      const clearFollowerSettlementTimer = () => {
        if (followerSettlementTimer != null) window.clearTimeout(followerSettlementTimer);
        followerSettlementTimer = null;
      };
      if (!coordinator.registerVisibleImage({
        image,
        generation: visibleRequest.generation,
        onAbort: settleFollowerLane,
      })) {
        settleFollowerLane();
        return;
      }
      scrubDisplayPendingRef.current = true;
      image.onload = () => {
        if (!isRequestCurrent()) {
          rejectStaleLinkedAuthorityRequest();
          settleFollowerLane();
          return;
        }
        if (!coordinator.completeVisibleImage({ image, generation: visibleRequest.generation })) {
          settleFollowerLane();
          return;
        }
        imgSrcRef.current = url;
        displayDecodedViewportFrame({
          decodedImage: image,
          activeImage: imgRef.current,
          absoluteUrl,
          isCurrent: () => isRequestCurrent(),
          drawPreview: (previewImage, { beforeReveal } = {}) => {
            let previewAccepted = false;
            const visible = Boolean(drawScrubPreviewImageRef.current?.(previewImage, {
              beforeReveal: () => {
                previewAccepted = beforeReveal?.() !== false;
                return previewAccepted;
              },
            }));
            if (visible && previewAccepted) {
              if (window.__KAIROS_CINE_OBSERVER_ACTIVE__ === true) {
                publishVisibleFrameDom({
                  root: viewportRootRef.current,
                  canvas: scrubPreviewCanvasRef.current,
                  slice: s,
                  generation: visibleRequest.generation,
                });
                publishObserverAcceptedVisible({ surface: 'preview', surfaceElement: scrubPreviewCanvasRef.current });
              }
              publishVisibleSliceStateRef.current?.(s, { requestOrigin, interactionPerformanceAuthority });
            }
            return visible;
          },
          onVisible: acceptVisibleRequest,
          onCancelled: () => {
            cineResourceGovernor?.releaseDetachedImage(image);
            settleFollowerLane();
          },
          onSettled: ({ visible }) => {
            if (!isRequestCurrent()) {
              settleFollowerLane();
              return;
            }
            scrubDisplayPendingRef.current = false;
            if (visible) {
              const retained = cineResourceGovernor?.retainDecoded({ url: absoluteUrl, image });
              if (retained && !retained.retained) cineResourceGovernor?.releaseDetachedImage(image);
            } else {
              cineResourceGovernor?.releaseDetachedImage(image);
            }
            settleGovernorVisible(visible);
            if (!visible) {
              restoreAcceptedActiveImage();
              setLoading(false);
              if (!imgRef.current?.src && acceptedVisibleSurfaceRef.current?.surface !== 'preview') setImgError(true);
              settleFollowerLane();
              return;
            }
            acceptedImgSrcRef.current = url;
            acceptedVisibleSurfaceRef.current = { surface: 'active-image', url, generation: visibleRequest.generation };
            imgSrcRef.current = url;
            const preview = scrubPreviewCanvasRef.current;
            if (preview) preview.style.display = 'none';
            publishObserverAcceptedVisible({ surface: 'active-image', surfaceElement: imgRef.current });
            settleFollowerLane();
          },
        });
      };
      image.onerror = () => {
        clearFollowerSettlementTimer();
        if (!isRequestCurrent()) {
          rejectStaleLinkedAuthorityRequest();
          settleFollowerLane();
          return;
        }
        if (!coordinator.completeVisibleImage({ image, generation: visibleRequest.generation })) {
          settleFollowerLane();
          return;
        }
        cineResourceGovernor?.releaseDetachedImage(image);
        settleGovernorVisible(false);
        scrubDisplayPendingRef.current = false;
        setLoading(false);
        if (!imgRef.current?.src && acceptedVisibleSurfaceRef.current?.surface !== 'preview') setImgError(true);
        settleFollowerLane();
      };
      if (options.followerCoordinated === true) {
        followerSettlementTimer = window.setTimeout(() => {
          followerSettlementTimer = null;
          if (!isRequestCurrent()) {
            rejectStaleLinkedAuthorityRequest();
            settleFollowerLane();
            return;
          }
          coordinator.abortVisibleImage();
          cancelPendingViewportPublication();
          failVisibleRequest();
          scrubDisplayPendingRef.current = false;
          setLoading(false);
          if (!acceptedImgSrcRef.current && acceptedVisibleSurfaceRef.current?.surface !== 'preview') setImgError(true);
          settleFollowerLane();
        }, VIEWPORT_FOLLOWER_IMAGE_SETTLEMENT_TIMEOUT_MS);
      }
      try {
        image.src = absoluteUrl;
      } catch (_) {
        clearFollowerSettlementTimer();
        if (coordinator.completeVisibleImage({ image, generation: visibleRequest.generation })) {
          cineResourceGovernor?.releaseDetachedImage(image);
          failVisibleRequest();
          restoreAcceptedActiveImage();
          scrubDisplayPendingRef.current = false;
          setLoading(false);
          if (!acceptedImgSrcRef.current && acceptedVisibleSurfaceRef.current?.surface !== 'preview') setImgError(true);
        }
        settleFollowerLane();
      }
      return;
    }
    // Preload image; swap when ready. Generation-guard completion so a slow
    // request from a previous series/slice cannot paint stale pixels after a
    // series switch or newer linked-scroll target arrives.
    const img = new window.Image();
    try { img.fetchPriority = visibleFetchPriority; } catch (_) {}
    let followerSettlementTimer = null;
    let followerLaneSettled = false;
    const settleFollowerLane = () => {
      if (followerLaneSettled) return;
      followerLaneSettled = true;
      if (followerSettlementTimer != null) window.clearTimeout(followerSettlementTimer);
      followerSettlementTimer = null;
      notifyDirectDomSettled();
    };
    if (!coordinator.registerVisibleImage({
      image: img,
      generation: visibleRequest.generation,
      onAbort: settleFollowerLane,
    })) {
      settleFollowerLane();
      return;
    }
    const loadingTimer = setTimeout(() => {
      if (isRequestCurrent()) setLoading(true);
    }, 100);
    if (options.followerCoordinated === true) {
      followerSettlementTimer = window.setTimeout(() => {
        followerSettlementTimer = null;
        if (!isRequestCurrent()) {
          rejectStaleLinkedAuthorityRequest();
          settleFollowerLane();
          return;
        }
        coordinator.abortVisibleImage();
        cancelPendingViewportPublication();
        failVisibleRequest();
        scrubDisplayPendingRef.current = false;
        setLoading(false);
        if (!acceptedImgSrcRef.current && acceptedVisibleSurfaceRef.current?.surface !== 'preview') setImgError(true);
        settleFollowerLane();
      }, VIEWPORT_FOLLOWER_IMAGE_SETTLEMENT_TIMEOUT_MS);
    }
    img.onload = () => {
      clearTimeout(loadingTimer);
      if (!isRequestCurrent()) {
        rejectStaleLinkedAuthorityRequest();
        settleFollowerLane();
        return;
      }
      if (!coordinator.completeVisibleImage({ image: img, generation: visibleRequest.generation })) {
        settleFollowerLane();
        return;
      }
      pendingVisibleDomSettlementRef.current = {
        generation: visibleRequest.generation,
        url,
        absoluteUrl,
        image: img,
        onVisible: acceptVisibleRequest,
        onFailure: failVisibleRequest,
        onPublished: publishObserverAcceptedVisible,
        onSettled: settleFollowerLane,
      };
      const activeImage = imgRef.current;
      const reactSourceAlreadyCurrent = imgSrc === url;
      imgSrcRef.current = url;
      setImgSrc(url);
      // Imperative W/L publication can leave the mounted DOM source newer than
      // React's still-current source state. Returning ordinarily to that state is
      // otherwise a setState no-op, so no DOM load fires and the visible owner
      // never settles. Reassign only this divergent no-op case after decode; the
      // previous valid pixels remain visible until the browser accepts the source.
      if (reactSourceAlreadyCurrent
          && activeImage
          && !isExpectedViewportImageSource(url, activeImage.src, window.location.href)) {
        try {
          activeImage.src = absoluteUrl;
        } catch (_) {
          pendingVisibleDomSettlementRef.current = null;
          cineResourceGovernor?.releaseDetachedImage(img);
          settleGovernorVisible(false);
          restoreAcceptedActiveImage();
          if (!acceptedImgSrcRef.current && acceptedVisibleSurfaceRef.current?.surface !== 'preview') setImgError(true);
          setLoading(false);
          settleFollowerLane();
          return;
        }
      }
      settleCompleteActiveViewportImage({
        activeImage,
        expectedSource: url,
        baseUrl: window.location.href,
        settle: settlePendingVisibleDom,
      });
      setLoading(false);
    };
    img.onerror = () => {
      clearTimeout(loadingTimer);
      if (!isRequestCurrent()) {
        rejectStaleLinkedAuthorityRequest();
        settleFollowerLane();
        return;
      }
      if (!coordinator.completeVisibleImage({ image: img, generation: visibleRequest.generation })) {
        settleFollowerLane();
        return;
      }
      cineResourceGovernor?.releaseDetachedImage(img);
      const firstImageRetryAttempt = Number(options.firstImageRetryAttempt) || 0;
      const firstImageRetryDelay = nextFirstViewportImageRetryDelayMs(firstImageRetryAttempt);
      if (!requestSliceIntentStillCurrent()
          && !acceptedImgSrcRef.current
          && acceptedVisibleSurfaceRef.current?.surface !== 'preview') {
        failVisibleRequest();
        setImgError(false);
        setLoading(true);
        settleFollowerLane();
        return;
      }
      if (!acceptedImgSrcRef.current
          && acceptedVisibleSurfaceRef.current?.surface !== 'preview'
          && Number.isFinite(firstImageRetryDelay)) {
        const failedGeneration = visibleRequest.generation;
        const recovery = firstSurfaceRecoveryRef.current;
        if (recovery
            && recovery.studyInstanceUID === target.studyInstanceUID
            && recovery.seriesInstanceUID === target.seriesInstanceUID
            && recovery.slice === s) {
          firstSurfaceRecoveryRef.current = Object.freeze({
            ...recovery,
            firstImageRetryAttempt: firstImageRetryAttempt + 1,
          });
        }
        failVisibleRequest();
        setImgError(false);
        setLoading(true);
        window.setTimeout(() => {
          const current = interactiveRequestCoordinatorRef.current.snapshot();
          if (viewportMountedRef.current !== true
              || current.visibleGeneration !== failedGeneration
              || current.visiblePending
              || !requestSliceIntentStillCurrent()
              || acceptedImgSrcRef.current
              || acceptedVisibleSurfaceRef.current?.surface === 'preview'
              || seriesRef.current?.studyInstanceUID !== target.studyInstanceUID
              || seriesRef.current?.seriesInstanceUID !== target.seriesInstanceUID) return;
          loadSliceRef.current?.(s, effectiveWl, target, {
            ...options,
            firstImageRetryAttempt: firstImageRetryAttempt + 1,
          });
        }, firstImageRetryDelay);
        settleFollowerLane();
        return;
      }
      settleGovernorVisible(false);
      restoreAcceptedActiveImage();
      firstSurfaceRecoveryRef.current = null;
      if (!acceptedImgSrcRef.current && acceptedVisibleSurfaceRef.current?.surface !== 'preview') setImgError(true);
      setLoading(false);
      settleFollowerLane();
    };
    try {
      img.src = absoluteUrl;
    } catch (_) {
      clearTimeout(loadingTimer);
      if (coordinator.completeVisibleImage({ image: img, generation: visibleRequest.generation })) {
        cineResourceGovernor?.releaseDetachedImage(img);
        failVisibleRequest();
        restoreAcceptedActiveImage();
        setLoading(false);
        if (!acceptedImgSrcRef.current && acceptedVisibleSurfaceRef.current?.surface !== 'preview') setImgError(true);
      }
      settleFollowerLane();
    }
  }, [cacheEpoch, cancelLinkedFollowerRetry, cancelPendingViewportPublication, cancelScrollIdlePreload, cineResourceGovernor, claimSliceIntentAuthority, closeWindowLevelEvidenceInteraction, effectiveOnLinkedSliceRequest, imgSrc, index, invalidateSpeculativeClaimAuthority, joinWindowLevelAcceptedPublication, linkedScrollEnabled, maxSlice, onInteractionPerformanceEvent, pauseActiveSeriesBrowserWarmup, preloadBurstProjectedFrames, restoreAcceptedActiveImage, series, settlePendingVisibleDom, submitInitialVisibleNeighbors]);
  loadSliceRef.current = loadSlice;

  React.useLayoutEffect(() => {
    cancelWheelScrollFrame();
    cancelScrubFrame();
    cancelScrollIdlePreload();
    cancelLinkedFollowerRetry();
    linkedFollowerRetryRef.current = { key: '', attempts: 0 };
    requestedSliceRef.current = sliceRef.current;
    if (Number.isFinite(sliceRef.current)) sliceSliderRef.current?.setLiveSlice(sliceRef.current);
    if (viewportFrozen) {
      cancelPendingViewportPublication();
      followerFrameCoordinatorRef.current?.cancel();
      const recovery = firstSurfaceRecoveryRef.current;
      const authority = sliceIntentAuthorityRef.current;
      const recoveryAuthorityCurrent = recovery
        && authority.studyInstanceUID === recovery.studyInstanceUID
        && authority.seriesInstanceUID === recovery.seriesInstanceUID
        && authority.slice === recovery.slice;
      if (recovery && !recoveryAuthorityCurrent
          && !acceptedImgSrcRef.current
          && acceptedVisibleSurfaceRef.current?.surface !== 'preview') {
        firstSurfaceRecoveryRef.current = null;
        const resetGeneration = interactiveRequestCoordinatorRef.current.reset({ initialVisibleSlice: null });
        cineResourceGovernor?.fencePaneGeneration({
          paneId: index,
          generation: resetGeneration,
          direction: scrollDirectionRef.current || 1,
        });
        setLoading(false);
        setImgError(true);
      }
    }
    const activeRequest = activeLinkedAuthorityRequestRef.current;
    const linkedAuthorityStale = activeRequest?.authorityEpoch != null
      && activeRequest.authorityEpoch !== linkedAuthorityEpoch;
    const freezeAuthorityStale = activeRequest?.freezeAuthorityEpoch != null
      && activeRequest.freezeAuthorityEpoch !== freezeAuthorityEpochRef.current;
    if (activeRequest && (linkedAuthorityStale || freezeAuthorityStale)) {
      activeRequest.reject?.();
    }
  }, [cancelLinkedFollowerRetry, cancelPendingViewportPublication, cancelScrubFrame, cancelScrollIdlePreload, cancelWheelScrollFrame, cineResourceGovernor, index, linkedAuthorityEpoch, viewportFrozen]);

  if (!windowLevelDragCoordinatorRef.current) {
    windowLevelDragCoordinatorRef.current = createWindowLevelDragCoordinator({
      requestFrame: callback => window.requestAnimationFrame(callback),
      cancelFrame: frameId => window.cancelAnimationFrame(frameId),
      consumeEvidenceInput: (input, frameWl) => {
        const state = windowLevelEvidenceInteractionRef.current;
        if (!state || state.input !== input) return false;
        const consumed = consumeViewportWindowLevelInputForCoordinator(
          input,
          Object.freeze({ ww: frameWl.ww, wc: frameWl.wc }),
        ) === true;
        if (!consumed) closeWindowLevelEvidenceInteraction('failed', state);
        return consumed;
      },
      onFrame: (frameWl, complete, appliedInput) => {
        wlRef.current = frameWl;
        const draft = formatWindowLevelDraft(frameWl);
        const viewportRoot = document.querySelector(`[data-testid="diagnostic-viewport-drop-target-${index}"]`);
        viewportRoot?.querySelectorAll('[data-kairos-wl-draft-key="ww"]').forEach(input => { input.value = draft.ww; });
        viewportRoot?.querySelectorAll('[data-kairos-wl-draft-key="wc"]').forEach(input => { input.value = draft.wc; });
        viewportRoot?.querySelectorAll(`[data-kairos-marker="${VIEWPORT_WINDOW_LEVEL_INLINE_EDIT_MARKER}"]`).forEach(editor => {
          editor.setAttribute('aria-label', `Effective window level for viewport ${index + 1}: ${describeWindowLevelOverlay(frameWl)}`);
        });
        const state = windowLevelEvidenceInteractionRef.current;
        if (appliedInput && state?.input === appliedInput) {
          loadSlice(sliceRef.current, frameWl, undefined, {
            directDisplay: true,
            skipPreload: true,
            evidencePhase: state.phase,
            evidenceInteraction: 'drag',
            windowLevelEvidenceState: state,
            windowLevelEvidenceInput: appliedInput,
            onDirectDomSettled: complete,
          });
          return;
        }
        loadSlice(sliceRef.current, frameWl, undefined, { directDomDisplay: true, skipPreload: true, onDirectDomSettled: complete });
      },
    });
  }

  useEffect(() => () => {
    windowLevelDragCoordinatorRef.current?.cancel();
  }, [series?.seriesInstanceUID]);

  const publishVisibleSliceState = useCallback((visibleSlice, meta = null) => {
    const clamped = publishLiveSliceDom({
      root: viewportRootRef.current,
      next: visibleSlice,
      maxSlice,
    });
    sliceRef.current = clamped;
    sliceSliderRef.current?.setLiveSlice(clamped);
    flushSync(() => setSlice(clamped));
    const callbackMeta = {
      source: 'accepted_visible',
      requestOrigin: meta?.requestOrigin || VIEWPORT_REQUEST_ORIGIN.LOCAL,
    };
    onSliceChange?.(index, clamped, maxSlice + 1, callbackMeta);
    if (meta?.interactionPerformanceAuthority) {
      try {
        const monotonicAcceptedAtMs = window.performance?.now?.();
        onInteractionPerformanceEvent?.({
          type: 'accepted',
          viewportIndex: index,
          slice: clamped,
          atMs: Number.isFinite(monotonicAcceptedAtMs) ? monotonicAcceptedAtMs : Date.now(),
          ...meta.interactionPerformanceAuthority,
        });
      } catch (_) {
        // Interaction telemetry is fail-open after accepted clinical publication.
      }
    }
    // The page progress callback normally promotes accepted local LINK pixels to
    // transport authority. Keep a direct child-to-page fail-safe after that
    // telemetry path: an accepted source frame must never leave the imperative
    // channel stranded at intent while followers remain one frame behind.
    // useLinkedScroll deduplicates an already-published exact accepted identity.
    if (linkedScrollEnabled
        && isLocalLinkedRequest(callbackMeta.requestOrigin)
        && effectiveOnLinkedSliceRequest) {
      lastLinkedPublishedSliceRef.current = clamped;
      effectiveOnLinkedSliceRequest(index, clamped, maxSlice + 1, { source: 'linked_final' });
    }
    return clamped;
  }, [effectiveOnLinkedSliceRequest, index, linkedScrollEnabled, maxSlice, onInteractionPerformanceEvent, onSliceChange]);
  publishVisibleSliceStateRef.current = publishVisibleSliceState;

  const publishScrubSliceState = useCallback((next, options = {}) => {
    const clamped = normalizeLiveSlice(next, maxSlice);
    requestedSliceRef.current = clamped;
    const now = window.performance?.now?.() || Date.now();
    const publishLinkedFrame = linkedScrollEnabled
      && now - scrubLastStateAtRef.current >= VIEWPORT_SCRUB_STATE_INTERVAL_MS;
    let shouldPublishLinkedTarget = publishLinkedFrame;
    if (options.force === true) {
      shouldPublishLinkedTarget = linkedScrollEnabled;
    }
    if (shouldPublishLinkedTarget) {
      scrubLastStateAtRef.current = now;
      if (effectiveOnLinkedSliceRequest) {
        lastLinkedPublishedSliceRef.current = clamped;
        const commandId = effectiveOnLinkedSliceRequest(index, clamped, maxSlice + 1);
        localLinkedCommandCausalityRef.current = Number.isSafeInteger(commandId) && commandId > 0
          ? Object.freeze({
            commandId,
            sourceViewportIndex: index,
            sourceSlice: clamped,
            targetViewportIndex: index,
            targetSlice: clamped,
          }) : null;
      }
    }
    return clamped;
  }, [effectiveOnLinkedSliceRequest, index, linkedScrollEnabled, maxSlice]);
  publishScrubSliceStateRef.current = publishScrubSliceState;

  const hideScrubPreview = useCallback(() => {
    const canvas = scrubPreviewCanvasRef.current;
    if (canvas) canvas.style.display = 'none';
  }, []);

  useEffect(() => beginViewportLayoutTransition({
    cancelScrubFrame,
    hideScrubPreview,
    refreshOverlayLayout,
    scrubActiveRef,
    scrubDisplayPendingRef,
  }), [viewportLayoutMode, cancelScrubFrame, hideScrubPreview, refreshOverlayLayout]);

  const drawScrubPreviewImage = useCallback((img, { beforeReveal } = {}) => {
    const canvas = scrubPreviewCanvasRef.current;
    const parent = boxRef.current;
    if (!canvas || !parent || !img?.complete || !img.naturalWidth || !img.naturalHeight) return false;
    const rect = parent.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    const dpr = window.devicePixelRatio || 1;
    const pixelW = Math.max(1, Math.round(rect.width * dpr));
    const pixelH = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== pixelW) canvas.width = pixelW;
    if (canvas.height !== pixelH) canvas.height = pixelH;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    const zoomValue = zoomRef.current || 1;
    const pan = panOffsetRef.current || { x: 0, y: 0 };
    const scale = Math.min(rect.width / img.naturalWidth, rect.height / img.naturalHeight) * zoomValue;
    const drawW = img.naturalWidth * scale;
    const drawH = img.naturalHeight * scale;
    const drawX = (rect.width - drawW) / 2 + (pan.x || 0);
    const drawY = (rect.height - drawH) / 2 + (pan.y || 0);
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    if (beforeReveal?.() === false) return false;
    canvas.style.display = 'block';
    return true;
  }, []);

  useEffect(() => {
    drawScrubPreviewImageRef.current = drawScrubPreviewImage;
    return () => {
      if (drawScrubPreviewImageRef.current === drawScrubPreviewImage) drawScrubPreviewImageRef.current = null;
    };
  }, [drawScrubPreviewImage]);

  const scheduleScrubSliceLoad = useCallback((next, options = {}) => {
    const scheduledLinkedAuthorityEpoch = linkedAuthorityEpoch;
    if (scheduledLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current) return;
    const clamped = Math.max(0, Math.min(maxSlice, Number(next) || 0));
    scrubPendingSliceRef.current = clamped;
    scrubActiveRef.current = options.active !== false;
    cancelScrollIdlePreload();
    if (scrubActiveRef.current) {
      invalidateSpeculativeClaimAuthority();
      cineResourceGovernor?.beginVisibleInteraction({
        preserveSpeculativeKinds: linkedScrollEnabled ? ['linked-intent'] : [],
      });
      pauseActiveSeriesBrowserWarmup();
    }
    if (scrubFrameRef.current != null || scrubThrottleTimerRef.current != null) return;
    const requestVisibleScrubFrame = () => {
      scrubThrottleTimerRef.current = null;
      if (scheduledLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current) {
        scrubPendingSliceRef.current = null;
        scrubActiveRef.current = false;
        return;
      }
      scrubFrameRef.current = window.requestAnimationFrame(() => {
        scrubFrameRef.current = null;
        if (scheduledLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current) {
          scrubPendingSliceRef.current = null;
          scrubActiveRef.current = false;
          return;
        }
        const targetSlice = scrubPendingSliceRef.current;
        scrubPendingSliceRef.current = null;
        if (targetSlice == null) return;
        scrubLastDisplayAtRef.current = window.performance?.now?.() || Date.now();
        if (options.allowColdDisplay === true || options.force === true) {
          loadSlice(targetSlice, wlRef.current, seriesRef.current, {
            skipPreload: true,
            directDisplay: true,
            requestOrigin: linkedScrollEnabled ? VIEWPORT_REQUEST_ORIGIN.LOCAL_LINKED : VIEWPORT_REQUEST_ORIGIN.LOCAL_NAVIGATION,
            linkedAuthorityEpoch: scheduledLinkedAuthorityEpoch,
          });
        }
      });
    };
    if (options.force === true) {
      requestVisibleScrubFrame();
      return;
    }
    const now = window.performance?.now?.() || Date.now();
    const waitMs = Math.max(0, VIEWPORT_SCRUB_COLD_DISPLAY_INTERVAL_MS - (now - scrubLastDisplayAtRef.current));
    if (waitMs > 0) {
      scrubThrottleTimerRef.current = window.setTimeout(requestVisibleScrubFrame, waitMs);
    } else {
      requestVisibleScrubFrame();
    }
  }, [cineResourceGovernor, cancelScrollIdlePreload, invalidateSpeculativeClaimAuthority, pauseActiveSeriesBrowserWarmup, loadSlice, linkedAuthorityEpoch, linkedScrollEnabled, maxSlice]);

  const commitScrubSliceLoad = useCallback((next = requestedSliceRef.current) => {
    const scheduledLinkedAuthorityEpoch = linkedAuthorityEpoch;
    if (scheduledLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current) return;
    const clamped = Math.max(0, Math.min(maxSlice, Number(next) || 0));
    cancelScrubFrame();
    scrubActiveRef.current = false;
    scrubDisplayPendingRef.current = false;
    loadSlice(clamped, wlRef.current, seriesRef.current, {
      skipPreload: true,
      directDisplay: true,
      requestOrigin: linkedScrollEnabled ? VIEWPORT_REQUEST_ORIGIN.LOCAL_LINKED : VIEWPORT_REQUEST_ORIGIN.LOCAL_NAVIGATION,
      linkedAuthorityEpoch: scheduledLinkedAuthorityEpoch,
    });
    scheduleScrollIdlePreload(clamped, wlRef.current, seriesRef.current, scrollDirectionRef.current || 1);
  }, [cancelScrubFrame, linkedAuthorityEpoch, loadSlice, linkedScrollEnabled, maxSlice, scheduleScrollIdlePreload]);

  const finishScrubInteraction = useCallback((next = requestedSliceRef.current) => {
    const clamped = Math.max(0, Math.min(maxSlice, Number(next) || 0));
    const requestSnapshot = interactiveRequestCoordinatorRef.current.snapshot();
    const alreadyAccepted = clamped === sliceRef.current && !requestSnapshot.visiblePending;
    if (lastLinkedPublishedSliceRef.current !== clamped) {
      publishScrubSliceState(clamped, { force: true });
    }
    if (alreadyAccepted) {
      if (effectiveOnLinkedSliceRequest && linkedScrollEnabled) {
        lastLinkedPublishedSliceRef.current = clamped;
        effectiveOnLinkedSliceRequest(index, clamped, maxSlice + 1, { source: 'linked_final' });
      }
      cancelScrubFrame();
      scrubActiveRef.current = false;
      scrubDisplayPendingRef.current = false;
      return;
    }
    commitScrubSliceLoad(clamped);
  }, [cancelScrubFrame, commitScrubSliceLoad, effectiveOnLinkedSliceRequest, index, linkedScrollEnabled, maxSlice, publishScrubSliceState]);

  const prepareLinkedFollowerIntent = useCallback((targetSlice, currentSeries) => {
    if (!currentSeries || !Number.isSafeInteger(targetSlice) || targetSlice < 0
        || targetSlice > maxSliceRef.current || !cacheEpochRef.current) return false;
    const generation = interactiveRequestCoordinatorRef.current.snapshot().visibleGeneration;
    const effectiveWl = normalizeViewportWindowLevel(wlRef.current);
    const url = buildPetSliceThicknessRenderUrl({
      studyUID: currentSeries.studyInstanceUID,
      seriesUID: currentSeries.seriesInstanceUID,
      slice: targetSlice,
      ww: effectiveWl.ww,
      wc: effectiveWl.wc,
      series: currentSeries,
      thicknessMm: petSliceThicknessRef.current,
      cacheEpoch: cacheEpochRef.current,
    });
    const candidates = prepareSpeculativeCandidates({
      kind: 'linked-intent',
      generation,
      series: currentSeries,
      candidates: [{ slice: targetSlice, url, distance: 0, order: 0 }],
    });
    if (candidates.length !== 1) return false;
    cineResourceGovernor?.replaceSpeculative({
      paneId: index,
      generation,
      kind: 'linked-intent',
      direction: targetSlice >= sliceRef.current ? 1 : -1,
      candidates,
    });
    return true;
  }, [cineResourceGovernor, index, prepareSpeculativeCandidates]);

  dispatchFollowerCommandRef.current = (command, complete) => {
    const currentSeries = seriesRef.current;
    const targetSlice = Number(command?.targetSlice);
    if (!currentSeries || !Number.isSafeInteger(targetSlice)
        || currentSeries.studyInstanceUID !== command.studyInstanceUID
        || currentSeries.seriesInstanceUID !== command.seriesInstanceUID
        || command.followerCommandGeneration !== followerCommandGenerationRef.current
        || (command.channel === 'triangulation' && command.freezeAuthorityEpoch !== freezeAuthorityEpochRef.current)
        || (command.channel === 'linked' && command.linkedAuthorityEpoch !== linkedAuthorityEpochRef.current)) {
      complete();
      return;
    }
    const requestSnapshot = interactiveRequestCoordinatorRef.current.snapshot();
    if (targetSlice === sliceRef.current && !requestSnapshot.visiblePending) {
      complete();
      return;
    }
    pauseActiveSeriesBrowserWarmup();
    cancelScrollIdlePreload();
    scrollDirectionRef.current = targetSlice > requestedSliceRef.current ? 1 : -1;
    requestedSliceRef.current = targetSlice;
    cancelLinkedScrollFrame();
    const linkedTransportToken = command.channel === 'linked' ? {} : null;
    if (linkedTransportToken) activeLinkedFollowerTransportRef.current = linkedTransportToken;
    const settleFollowerTransport = () => {
      if (linkedTransportToken && activeLinkedFollowerTransportRef.current === linkedTransportToken) {
        activeLinkedFollowerTransportRef.current = null;
      }
      complete();
    };
    loadSlice(targetSlice, wlRef.current, currentSeries, {
      skipPreload: true,
      directDisplay: true,
      followerCoordinated: true,
      requestOrigin: command.requestOrigin,
      linkedAuthorityEpoch: command.linkedAuthorityEpoch,
      freezeAuthorityEpoch: command.freezeAuthorityEpoch,
      followerCommandGeneration: command.followerCommandGeneration,
      followerCommand: command,
      linkedCausality: command.channel === 'linked' ? Object.freeze({
        commandId: command.commandId,
        sourceViewportIndex: command.sourceViewportIndex,
        sourceSlice: command.sourceSlice,
        targetViewportIndex: index,
        targetSlice,
      }) : null,
      onDirectDomSettled: settleFollowerTransport,
    });
    const latestIntent = latestLinkedIntentRef.current;
    const preservesNewerIntentPreparation = command.channel === 'linked'
      && Number.isSafeInteger(command.commandId)
      && Number.isSafeInteger(latestIntent?.commandId)
      && latestIntent.commandId > command.commandId
      && latestIntent.targetSlice !== targetSlice;
    if (command.channel === 'linked' && !activeLinkedIntentTakeoverRef.current
        && !preservesNewerIntentPreparation) {
      // loadSlice has already consumed any exact decoded cache entry or admitted
      // the accepted visible replacement. When it adopted the exact in-flight
      // intent Image, accepted authority owns that transfer and lifecycle fences
      // revoke it; do not cancel it as obsolete speculative work here.
      invalidateSpeculativeClaimAuthority('linked-intent');
      cineResourceGovernor?.removeSpeculative({ paneId: index, kind: 'linked-intent' });
    }
    // A LINK follower needs the exact commanded frame, not adjacent speculative
    // frames. Let the active source own the shared two-request/24-frame lookahead
    // budget. Triangulation retains its historical follower preload behavior.
    if (command.channel !== 'linked') {
      scheduleScrollIdlePreload(targetSlice, wlRef.current, currentSeries, scrollDirectionRef.current || 1, { publishLinked: false });
    }
  };

  const cancelLinkedFollowerIntentPreparation = useCallback(() => {
    const linkedIntentTakeover = activeLinkedIntentTakeoverRef.current;
    activeLinkedIntentTakeoverRef.current = null;
    try { linkedIntentTakeover?.cancel?.(); } catch (_) {}
    invalidateSpeculativeClaimAuthority('linked-intent');
    cineResourceGovernor?.removeSpeculative({ paneId: index, kind: 'linked-intent' });
  }, [cineResourceGovernor, index, invalidateSpeculativeClaimAuthority]);

  const resumeFirstSurfaceRecovery = useCallback(() => {
    const recovery = firstSurfaceRecoveryRef.current;
    const currentSeries = seriesRef.current;
    if (!recovery || viewportFrozenRef.current
        || acceptedImgSrcRef.current
        || acceptedVisibleSurfaceRef.current?.surface === 'preview'
        || !currentSeries
        || currentSeries.studyInstanceUID !== recovery.studyInstanceUID
        || currentSeries.seriesInstanceUID !== recovery.seriesInstanceUID
        || !Number.isSafeInteger(recovery.slice)) return false;
    cancelPendingViewportPublication();
    const resetGeneration = interactiveRequestCoordinatorRef.current.reset({ initialVisibleSlice: null });
    cineResourceGovernor?.fencePaneGeneration({
      paneId: index,
      generation: resetGeneration,
      direction: scrollDirectionRef.current || 1,
    });
    claimSliceIntentAuthority(currentSeries, recovery.slice);
    requestedSliceRef.current = recovery.slice;
    setImgError(false);
    setLoading(true);
    loadSliceRef.current?.(recovery.slice, recovery.wl, currentSeries, {
      skipPreload: true,
      requestOrigin: recovery.requestOrigin,
      firstImageRetryAttempt: recovery.firstImageRetryAttempt,
    });
    return true;
  }, [cancelPendingViewportPublication, cineResourceGovernor, claimSliceIntentAuthority, index]);

  const cancelLinkedFollowerAuthority = useCallback(({ preserveCommandIdentity = false } = {}) => {
    const hadLinkedIntentAuthority = latestLinkedIntentRef.current != null;
    cancelLinkedFollowerRetry();
    cancelLinkedFollowerIntentPreparation();
    linkedFollowerRetryRef.current = { key: '', attempts: 0 };
    if (!preserveCommandIdentity) {
      lastLinkedScrollCommandIdRef.current = null;
      lastLinkedAcceptedCommandIdRef.current = null;
      latestLinkedIntentRef.current = null;
    }
    lastLinkedScrollAppliedRef.current = null;
    const coordinator = followerFrameCoordinatorRef.current;
    const coordinatorSnapshot = coordinator.snapshot();
    const hadActiveLinkedRequest = coordinatorSnapshot.active?.channel === 'linked';
    const hadActiveLinkedTransport = activeLinkedFollowerTransportRef.current != null;
    const preservedQueuedTriangulation = coordinatorSnapshot.latest?.channel === 'triangulation'
      ? coordinatorSnapshot.latest
      : null;
    const hadLinkedWork = hadActiveLinkedRequest
      || hadActiveLinkedTransport
      || coordinatorSnapshot.latest?.channel === 'linked';
    coordinator.cancelWhere(command => command.channel === 'linked');
    if (!hadLinkedWork) {
      if (!preserveCommandIdentity && hadLinkedIntentAuthority && !preservedQueuedTriangulation) {
        resumeFirstSurfaceRecovery();
      }
      return;
    }
    // Invalidate LINK acceptance, then refresh only a preserved queued
    // triangulation command to the new generation. Its target/geometry and
    // existing queued frame remain unchanged.
    followerCommandGenerationRef.current += 1;
    if (preservedQueuedTriangulation) {
      coordinator.schedule({
        ...preservedQueuedTriangulation,
        followerCommandGeneration: followerCommandGenerationRef.current,
      });
    }
    if (hadActiveLinkedTransport || hadActiveLinkedRequest) {
      // A decoded LINK frame may already own the active-image DOM settler even
      // after its detached Image and coordinator lane have completed. Restore
      // accepted pixels before changing interactive generation.
      cancelPendingViewportPublication();
      const resetGeneration = interactiveRequestCoordinatorRef.current.reset({ initialVisibleSlice: sliceRef.current });
      cineResourceGovernor?.fencePaneGeneration({
        paneId: index,
        generation: resetGeneration,
        direction: scrollDirectionRef.current || 1,
      });
      activeLinkedFollowerTransportRef.current = null;
    }
    if (!preserveCommandIdentity && !preservedQueuedTriangulation) resumeFirstSurfaceRecovery();
  }, [cancelLinkedFollowerIntentPreparation, cancelLinkedFollowerRetry, cancelPendingViewportPublication, cineResourceGovernor, index, resumeFirstSurfaceRecovery]);

  const scheduleLinkedScrollCommand = useCallback((payload, { continuous = false } = {}) => {
    const currentSeries = seriesRef.current;
    if (!payload || payload.sourceViewportIdx === index || !currentSeries) return;
    if (payload.linkedAuthorityEpoch !== linkedAuthorityEpochRef.current) return;
    if (payload.phase !== 'intent' && payload.phase !== 'accepted') return;
    const currentMaxSlice = maxSliceRef.current;
    if (!Number.isSafeInteger(currentMaxSlice) || currentMaxSlice < 0
        || typeof payload.fraction !== 'number'
        || !Number.isFinite(payload.fraction)
        || payload.fraction < 0 || payload.fraction > 1) return;
    const totalSlices = currentMaxSlice + 1;
    const targetSlice = Math.round(payload.fraction * (totalSlices - 1));
    if (!Number.isSafeInteger(targetSlice) || targetSlice < 0 || targetSlice > currentMaxSlice) return;
    const commandId = payload.commandId;
    const previousCommandId = lastLinkedScrollCommandIdRef.current;
    const previousAcceptedCommandId = lastLinkedAcceptedCommandIdRef.current;
    const numberedCommand = Number.isSafeInteger(commandId);
    const adoptSeenCommandId = () => {
      if (!numberedCommand) return;
      lastLinkedScrollCommandIdRef.current = Number.isSafeInteger(lastLinkedScrollCommandIdRef.current)
        ? Math.max(lastLinkedScrollCommandIdRef.current, commandId)
        : commandId;
    };
    if (payload.phase === 'intent') {
      if (numberedCommand && Number.isSafeInteger(previousCommandId)
          && commandId <= previousCommandId) return;
      const intentRequestSnapshot = interactiveRequestCoordinatorRef.current.snapshot();
      const intentTargetKey = `${currentSeries.studyInstanceUID}:${currentSeries.seriesInstanceUID}:${targetSlice}`;
      const sameLinkedTargetAlreadyOwned = lastLinkedScrollAppliedRef.current === intentTargetKey
        && ((targetSlice === requestedSliceRef.current && intentRequestSnapshot.visiblePending)
          || (targetSlice === sliceRef.current && !intentRequestSnapshot.visiblePending));
      if (sameLinkedTargetAlreadyOwned) {
        // Repeated wheel/RAF delivery can emit a newer command identity for the
        // exact LINK target while its accepted follower frame is still settling.
        // Adopt identity without revoking the already exact transport or pixels;
        // the matching accepted phase will retain the normal duplicate guard.
        adoptSeenCommandId();
        latestLinkedIntentRef.current = { commandId, targetSlice };
        return;
      }
      claimSliceIntentAuthority(currentSeries, targetSlice);
      // Intent owns clinical authority immediately. It fences/aborts obsolete
      // LINK pixels while preserving active and queued triangulation, then starts
      // only bounded exact-target preparation. Preparation cannot mutate visible
      // ownership or publish pixels; the matching accepted phase still owns the
      // sole clinical follower dispatch.
      cancelLinkedFollowerAuthority({ preserveCommandIdentity: true });
      adoptSeenCommandId();
      latestLinkedIntentRef.current = { commandId, targetSlice };
      prepareLinkedFollowerIntent(targetSlice, currentSeries);
      return;
    }
    // Request-time intent order and accepted-visible order are distinct. A source
    // frame may visibly settle after a newer wheel intent was emitted; that real
    // accepted frame must still fan out. Only accepted frames at or behind the
    // last accepted-visible identity are stale.
    if (numberedCommand && Number.isSafeInteger(previousAcceptedCommandId)
        && commandId <= previousAcceptedCommandId) return;
    const latestIntent = latestLinkedIntentRef.current;
    const preservesNewerIntentPreparation = numberedCommand
      && Number.isSafeInteger(latestIntent?.commandId)
      && latestIntent.commandId > commandId
      && latestIntent.targetSlice !== targetSlice;
    if (!preservesNewerIntentPreparation) claimSliceIntentAuthority(currentSeries, targetSlice);
    if (numberedCommand) lastLinkedAcceptedCommandIdRef.current = commandId;
    adoptSeenCommandId();
    const retryKey = `${currentSeries.studyInstanceUID}:${currentSeries.seriesInstanceUID}:${targetSlice}`;
    const requestSnapshot = interactiveRequestCoordinatorRef.current.snapshot();

    // A newer source frame can map to the same follower slice. Adopt its newer
    // command identity without aborting/restarting the already exact request.
    if (targetSlice === requestedSliceRef.current && requestSnapshot.visiblePending
        && lastLinkedScrollAppliedRef.current === retryKey) {
      // A newer accepted command may quantize to the same exact follower slice
      // while accepted authority already owns the transferred intent decode.
      // Adopt only its newer command identity; revoking here would discard the
      // exact decode and force a duplicate retry. Non-transferred preparation
      // retains the historical cleanup behavior.
      if (!activeLinkedIntentTakeoverRef.current && !preservesNewerIntentPreparation) {
        cancelLinkedFollowerIntentPreparation();
      }
      return;
    }
    if (targetSlice === sliceRef.current && !requestSnapshot.visiblePending) {
      lastLinkedScrollAppliedRef.current = retryKey;
      cancelLinkedFollowerRetry();
      linkedFollowerRetryRef.current = { key: retryKey, attempts: 0 };
      if (!preservesNewerIntentPreparation) cancelLinkedFollowerIntentPreparation();
      return;
    }
    if (!numberedCommand && lastLinkedScrollAppliedRef.current === retryKey) return;

    followerCommandGenerationRef.current += 1;
    cancelLinkedFollowerRetry();
    linkedFollowerRetryRef.current = { key: retryKey, attempts: 0 };
    // Superseded LINK targets release this lane so the interactive request
    // coordinator can abort old detached work and dispatch the newest exact
    // target. Generation/authority fencing rejects stale pixels. Triangulation
    // remains settlement-locked on its separate scheduling branch.
    lastLinkedScrollAppliedRef.current = retryKey;
    followerFrameCoordinatorRef.current.schedule({
      channel: 'linked',
      targetSlice,
      commandId,
      sourceViewportIndex: payload.sourceViewportIdx,
      sourceSlice: payload.slice,
      studyInstanceUID: currentSeries.studyInstanceUID,
      seriesInstanceUID: currentSeries.seriesInstanceUID,
      requestOrigin: VIEWPORT_REQUEST_ORIGIN.LINKED_FOLLOWER,
      linkedAuthorityEpoch: payload.linkedAuthorityEpoch,
      followerCommandGeneration: followerCommandGenerationRef.current,
      preemptible: true,
    }, { immediate: continuous && payload.phase === 'accepted' });
  }, [cancelLinkedFollowerAuthority, cancelLinkedFollowerIntentPreparation, cancelLinkedFollowerRetry, claimSliceIntentAuthority, index, prepareLinkedFollowerIntent]);

  // Durable seed/fallback delivery for LINK toggle and late-mounted panes. The
  // current imperative snapshot wins over an older React seed.
  useEffect(() => {
    const liveSnapshot = linkedScrollCommandChannel?.getSnapshot?.();
    const livePayload = liveSnapshot?.payload;
    if (livePayload && series) {
      scheduleLinkedScrollCommand(livePayload);
      return;
    }
    // A versioned null snapshot is an authority tombstone from reset/disable.
    // Never allow an older prop seed to reacquire ownership before React commits.
    if (liveSnapshot && liveSnapshot.version > 0) {
      cancelLinkedFollowerAuthority();
      return;
    }
    if (effectiveLinkedScrollFraction === null || !series) {
      cancelLinkedFollowerAuthority();
      return;
    }
    scheduleLinkedScrollCommand({
      sourceViewportIdx: -1,
      fraction: effectiveLinkedScrollFraction,
      commandId: linkedScrollCommandId,
      linkedAuthorityEpoch,
      phase: 'accepted',
    });
  }, [effectiveLinkedScrollFraction, linkedAuthorityEpoch, linkedScrollCommandChannel, linkedScrollCommandId, series, cancelLinkedFollowerAuthority, scheduleLinkedScrollCommand]);

  // Continuous LINK delivery mirrors triangulation drag: direct follower
  // scheduling without ViewerPage state or full viewport-tree React renders.
  useEffect(() => {
    if (!linkedScrollCommandChannel || typeof linkedScrollCommandChannel.subscribe !== 'function') return undefined;
    return linkedScrollCommandChannel.subscribe((payload) => {
      if (payload) scheduleLinkedScrollCommand(payload, { continuous: true });
      else cancelLinkedFollowerAuthority();
    });
  }, [cancelLinkedFollowerAuthority, linkedScrollCommandChannel, scheduleLinkedScrollCommand]);

  // Single triangulation scheduling path shared by the React syncCommand prop
  // (committed anchors) and the imperative drag channel (per-frame drags).
  // Semantics are byte-identical to the historical effect branch: nonce dedupe,
  // freeze rejection, slice clamp, follower generation bump, and the same
  // authority fields on the scheduled follower command.
  const dispatchTriangulationSyncCommand = useCallback((command) => {
    const currentMaxSlice = maxSliceRef.current;
    if (!command || command.type !== 'triangulation_sync'
        || !Number.isSafeInteger(command.slice)
        || !Number.isSafeInteger(currentMaxSlice)
        || command.slice < 0 || command.slice > currentMaxSlice
        || !seriesRef.current) return;
    const nonceKey = command.nonce != null ? `triangulation_sync:${command.nonce}` : 'triangulation_sync:no-nonce';
    if (lastTriangulationSyncCommandNonceRef.current === nonceKey) return;
    lastTriangulationSyncCommandNonceRef.current = nonceKey;
    if (viewportFrozenRef.current) return;
    const next = command.slice;
    claimSliceIntentAuthority(seriesRef.current, next);
    followerCommandGenerationRef.current += 1;
    followerFrameCoordinatorRef.current.schedule({
      channel: 'triangulation',
      targetSlice: next,
      commandId: command.nonce,
      studyInstanceUID: seriesRef.current.studyInstanceUID,
      seriesInstanceUID: seriesRef.current.seriesInstanceUID,
      requestOrigin: VIEWPORT_REQUEST_ORIGIN.TRIANGULATION,
      followerCommandGeneration: followerCommandGenerationRef.current,
      freezeAuthorityEpoch: freezeAuthorityEpochRef.current,
    });
  }, [claimSliceIntentAuthority]);
  triangulationFollowerDispatchRef.current = dispatchTriangulationSyncCommand;
  const scheduleTriangulationSyncCommand = useCallback((command, { continuous = false } = {}) => {
    if (continuous) return triangulationFollowerCadenceRef.current?.submit(command);
    return triangulationFollowerCadenceRef.current?.flush(command);
  }, []);
  useEffect(() => {
    triangulationFollowerCadenceRef.current?.cancel();
    return () => triangulationFollowerCadenceRef.current?.cancel();
  }, [studyInstanceUID, series?.seriesInstanceUID, viewportFrozen]);

  // Imperative drag-channel subscription: follower scheduling for per-frame
  // triangulation drags happens here with zero React re-renders. Target
  // eligibility (geometry projection, parallel-stack exclusion, study match)
  // was already decided by createTriangulationSyncCommands upstream; this
  // consumes only the command addressed to this viewport index.
  useEffect(() => {
    if (!triangulationDragChannel || typeof triangulationDragChannel.subscribe !== 'function') return undefined;
    return triangulationDragChannel.subscribe((payload) => {
      const command = payload?.commands?.[index];
      if (command) scheduleTriangulationSyncCommand(command, { continuous: true });
    });
  }, [index, scheduleTriangulationSyncCommand, triangulationDragChannel]);

  useEffect(() => {
    if (!syncCommand || !syncCommand.type) return;
    const currentMaxSlice = maxSliceRef.current;
    if ((syncCommand.type === 'triangulation_sync' || syncCommand.type === 'viewport_scroll')
        && (!Number.isSafeInteger(syncCommand.slice)
          || !Number.isSafeInteger(currentMaxSlice)
          || syncCommand.slice < 0
          || syncCommand.slice > currentMaxSlice
          || !seriesRef.current)) return;
    const commandKey = syncCommand.nonce != null ? `${syncCommand.type}:${syncCommand.nonce}` : `${syncCommand.type}:no-nonce`;
    if (lastSyncCommandNonceRef.current === commandKey) return;
    lastSyncCommandNonceRef.current = commandKey;
    if (syncCommand.type === 'series_loaded' && syncCommand.series) {
      loadSeriesIntoViewport(syncCommand.series, {
        notify: false,
        requestOrigin: syncCommand.requestOrigin || VIEWPORT_REQUEST_ORIGIN.LOCAL,
      });
      return;
    }
    if (syncCommand.type === 'triangulation_sync') {
      scheduleTriangulationSyncCommand(syncCommand);
      return;
    }
    if (syncCommand.type === 'viewport_scroll'
        && Number.isSafeInteger(syncCommand.slice)
        && syncCommand.slice >= 0 && syncCommand.slice <= maxSlice
        && seriesRef.current) {
      const next = syncCommand.slice;
      const inboundMultiMonitor = syncCommand.requestOrigin === VIEWPORT_REQUEST_ORIGIN.MULTI_MONITOR_INBOUND;
      const requestOrigin = inboundMultiMonitor
        ? VIEWPORT_REQUEST_ORIGIN.MULTI_MONITOR_INBOUND
        : (linkedScrollEnabled ? VIEWPORT_REQUEST_ORIGIN.LOCAL_LINKED : VIEWPORT_REQUEST_ORIGIN.KEYBOARD);
      pauseActiveSeriesBrowserWarmup();
      cancelScrollIdlePreload();
      scrollDirectionRef.current = next > requestedSliceRef.current ? 1 : -1;
      requestedSliceRef.current = next;
      if (inboundMultiMonitor && linkedScrollEnabled && effectiveOnLinkedSliceRequest) {
        effectiveOnLinkedSliceRequest(index, next, maxSlice + 1, { source: 'multi_monitor_inbound' });
      }
      if (!inboundMultiMonitor && linkedScrollEnabled) publishScrubSliceState(next, { force: true });
      loadSlice(next, wlRef.current, seriesRef.current, {
        skipPreload: true,
        directDisplay: true,
        requestOrigin,
        linkedAuthorityEpoch,
      });
      scheduleScrollIdlePreload(next, wlRef.current, seriesRef.current, scrollDirectionRef.current || 1, { publishLinked: false });
      return;
    }
    if (syncCommand.type === 'window_level' && syncCommand.wl && seriesRef.current) {
      if (viewportFrozen) return;
      const wl = normalizeViewportWindowLevel({ ...syncCommand.wl, source: syncCommand.wl.source || 'manual' });
      windowLevelSourceRef.current = wl.source || 'manual';
      setWindowLevel(wl);
      wlRef.current = wl;
      setWlDraft(formatWindowLevelDraft(wl));
      if (Number.isFinite(sliceRef.current)) {
        loadSlice(sliceRef.current, wl, undefined, {
          requestOrigin: syncCommand.requestOrigin || VIEWPORT_REQUEST_ORIGIN.LOCAL,
        });
      }
      return;
    }
    if (syncCommand.type === 'toggle_freeze') {
      if (syncCommand.viewportIndex != null && syncCommand.viewportIndex !== index) return;
      toggleViewportFrozen();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncCommand?.nonce, syncCommand?.slice, syncCommand?.type, viewportFrozen]);

  // Throttled scroll handler. Track wheel distance, not just event count, so
  // high-magnitude mouse/trackpad flings can quickly traverse large CT stacks
  // without forcing a render request for every intermediate slice.
  const scheduleWheelScroll = useCallback(() => {
    if (scrollFrameRef.current != null) return;
    const scheduledLinkedAuthorityEpoch = linkedAuthorityEpoch;
    const scheduledCacheEpoch = cacheEpochRef.current;
    let scheduledRouteHref = null;
    try { scheduledRouteHref = new URL(window.location.href).href; } catch (_) {}
    if (scheduledLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current
        || !scheduledCacheEpoch || !scheduledRouteHref) return;
    if (isViewportEvidenceNetworkBridgeActive()) {
      const owner = cineWheelInteractionRef.current;
      if (owner) recordViewportCineWheelEffect(owner.interaction, index, 'wheel-raf-schedule');
    }
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      const retainedWheelEvent = cineWheelEventRef.current;
      const retainedWheelActivity = cineWheelActivityRef.current?.nativeEvent === retainedWheelEvent
        ? cineWheelActivityRef.current
        : null;
      cineWheelEventRef.current = null;
      cineWheelActivityRef.current = null;
      if (retainedWheelActivity?.receipt) {
        clearViewportCineWheelActivityForFrame(retainedWheelActivity.receipt);
      }
      let routeAuthorityCurrent = false;
      try { routeAuthorityCurrent = new URL(window.location.href).href === scheduledRouteHref; } catch (_) {}
      if (scheduledLinkedAuthorityEpoch !== linkedAuthorityEpochRef.current
          || cacheEpochRef.current !== scheduledCacheEpoch || !routeAuthorityCurrent) {
        scrollPendingRef.current = 0;
        scrollPendingRapidBurstRef.current = false;
        lastWheelEventAtRef.current = 0;
        return;
      }
      const seriesSize = maxSlice + 1;
      const rapidBurst = scrollPendingRapidBurstRef.current;
      scrollPendingRapidBurstRef.current = false;
      const { steps, remainder } = consumeViewportWheelSteps(
        scrollPendingRef.current,
        seriesSize,
        { rapidBurst },
      );
      scrollPendingRef.current = remainder;
      const observeNoWorkWheelActivity = direction => {
        if (!retainedWheelEvent || !direction || !isViewportEvidenceNetworkBridgeActive()) return;
        const owner = cineWheelInteractionRef.current;
        if (!owner) return;
        const ownerIsActive = isViewportCineInteractionActive(owner.interaction);
        let ownerIsCurrent = false;
        try { ownerIsCurrent = owner.isCurrent() === true; } catch (_) { ownerIsCurrent = false; }
        if (!ownerIsActive || !ownerIsCurrent || owner.direction !== direction) {
          if (ownerIsActive) cancelViewportCineInteraction(owner.interaction);
          cineWheelInteractionRef.current = null;
          cineWheelRenderRequestRef.current = null;
          cineWheelPendingRenderOrdinalRef.current = null;
          return;
        }
        if (retainedWheelActivity?.interaction === owner.interaction
            && retainedWheelActivity.accepted === true) return;
        const testInjection = resolveWindowLevelEvidenceTestInjection(cineWheelEvidenceTestInjection);
        observeViewportCineWheelActivity(
          owner.interaction,
          retainedWheelActivity
            ? retainedWheelActivity.evidenceEvent
            : (testInjection ? testInjection.eventForNativeEvent(retainedWheelEvent) : retainedWheelEvent),
        );
      };
      if (steps !== 0) {
        scrollDirectionRef.current = steps > 0 ? 1 : -1;
        const currentSlice = requestedSliceRef.current ?? sliceRef.current ?? 0;
        const next = Math.max(0, Math.min(maxSlice, currentSlice + steps));
        const inputCausality = wheelInputCausalityRef.current.consume({
          bridgeActive: isViewportEvidenceCinePhaseActive(),
          steps: next - currentSlice,
          intentSlice: next,
          remainder,
        });
        if (next !== currentSlice) {
          const monotonicInputAtMs = window.performance?.now?.();
          const inputAtMs = Number.isFinite(monotonicInputAtMs) ? monotonicInputAtMs : Date.now();
          const interactionPerformanceAuthority = Object.freeze({
            interactionOrdinal: interactionPerformanceOrdinalRef.current + 1,
            seriesLoadGeneration: Math.max(0, Number(currentSeriesLoadGenerationRef.current) || 0),
            viewportInstanceEpoch: Math.max(0, Number(viewportInstanceEpoch) || 0),
            cacheEpoch: cacheEpochRef.current,
            inputAtMs,
          });
          interactionPerformanceOrdinalRef.current = interactionPerformanceAuthority.interactionOrdinal;
          try {
            onInteractionPerformanceEvent?.({
              type: 'input',
              viewportIndex: index,
              frameCount: Math.abs(next - currentSlice),
              ...interactionPerformanceAuthority,
            });
          } catch (_) {
            // Interaction telemetry must never consume a radiologist wheel event.
          }
          let cineRenderRequest = null;
          if (retainedWheelEvent && isViewportEvidenceNetworkBridgeActive()) {
            const direction = steps > 0 ? 'forward' : 'backward';
            const requestViewportRoot = viewportRootRef.current;
            const seriesLoadGeneration = currentSeriesLoadGenerationRef.current;
            const currentSeries = seriesRef.current;
            let routeHref = null;
            try { routeHref = new URL(window.location.href).href; } catch (_) {}
            let owner = cineWheelInteractionRef.current;
            const ownerIsActive = owner
              ? isViewportCineInteractionActive(owner.interaction)
              : false;
            if (owner && (!ownerIsActive
                || owner.direction !== direction || owner.isCurrent() !== true)) {
              if (ownerIsActive) cancelViewportCineInteraction(owner.interaction);
              cineWheelInteractionRef.current = null;
              cineWheelRenderRequestRef.current = null;
              cineWheelPendingRenderOrdinalRef.current = null;
              owner = null;
            }
            if (!owner && requestViewportRoot && Number.isSafeInteger(seriesLoadGeneration)
                && seriesLoadGeneration > 0 && currentSeries && routeHref) {
              const testInjection = resolveWindowLevelEvidenceTestInjection(cineWheelEvidenceTestInjection);
              const clock = testInjection?.clock || createViewportCineBrowserClock();
              const isMountedAndCurrent = () => viewportMountedRef.current === true
                && viewportIndexRef.current === index
                && viewportRootRef.current === requestViewportRoot;
              const candidate = {
                interaction: null,
                direction,
                seriesLoadGeneration,
                cacheEpoch: scheduledCacheEpoch,
                starting: true,
                isCurrent: () => {
                  try {
                    const liveSeries = seriesRef.current;
                    return isMountedAndCurrent()
                      && isActiveViewportRef.current === true
                      && requestViewportRoot.isConnected === true
                      && currentSeriesLoadGenerationRef.current === seriesLoadGeneration
                      && cacheEpochRef.current === candidate.cacheEpoch
                      && seriesLoadGenerationRef.current.isCurrent(seriesLoadGeneration)
                      && liveSeries?.studyInstanceUID === currentSeries.studyInstanceUID
                      && liveSeries?.seriesInstanceUID === currentSeries.seriesInstanceUID
                      && new URL(window.location.href).href === routeHref
                      && (candidate.starting || cineWheelInteractionRef.current === candidate);
                  } catch (_) {
                    return false;
                  }
                },
              };
              candidate.interaction = beginViewportCineInteraction({
                event: null,
                clock,
                phase: 'cine',
                viewport: index,
                seriesGeneration: seriesLoadGeneration,
                interaction: 'wheel',
                direction,
                initialSlice: currentSlice,
                isRouteCurrent: () => candidate.isCurrent(),
                isSeriesCurrent: () => candidate.isCurrent(),
                isGenerationCurrent: () => candidate.isCurrent(),
                isVisible: () => candidate.isCurrent()
                  && document.visibilityState !== 'hidden',
                isFocused: () => candidate.isCurrent(),
                isActiveViewport: () => candidate.isCurrent(),
              });
              candidate.starting = false;
              if (candidate.interaction) {
                cineWheelInteractionRef.current = candidate;
                owner = candidate;
              }
            }
            if (owner) {
              const testInjection = resolveWindowLevelEvidenceTestInjection(cineWheelEvidenceTestInjection);
              const input = observeViewportCineInput(owner.interaction, {
                event: retainedWheelActivity
                  ? retainedWheelActivity.evidenceEvent
                  : (testInjection
                    ? testInjection.eventForNativeEvent(retainedWheelEvent)
                    : retainedWheelEvent),
                targetBefore: currentSlice,
                targetAfter: next,
              });
              const command = input && consumeViewportCineInputForCoordinator(input, next);
              const binding = command && createViewportCineRenderBinding(command);
              const pendingRenderOrdinal = command && binding
                ? reserveViewportCinePendingRenderOrdinal(owner.interaction, index) : null;
              if (command && binding && Number.isSafeInteger(pendingRenderOrdinal)
                  && pendingRenderOrdinal > 0) {
                cineRenderRequest = Object.freeze({
                  interaction: owner.interaction,
                  command,
                  binding,
                  seriesGeneration: owner.seriesLoadGeneration,
                  pendingRenderOrdinal,
                  isCurrent: () => owner.isCurrent() === true,
                });
                cineWheelRenderRequestRef.current = cineRenderRequest;
                cineWheelPendingRenderOrdinalRef.current = pendingRenderOrdinal;
              } else {
                if (isViewportCineInteractionActive(owner.interaction)) {
                  cancelViewportCineInteraction(owner.interaction);
                }
                cineWheelInteractionRef.current = null;
                cineWheelRenderRequestRef.current = null;
                cineWheelPendingRenderOrdinalRef.current = null;
              }
            }
          }
          if (isViewportEvidenceNetworkBridgeActive()) {
            const owner = cineWheelInteractionRef.current;
            if (owner) recordViewportCineWheelEffect(owner.interaction, index, 'scrub-publication');
          }
          publishScrubSliceState(next);
          if (linkedScrollEnabled
              && localLinkedCommandCausalityRef.current?.targetSlice !== next) {
            publishScrubSliceState(next, { force: true });
          }
          const scheduledLinkedCausality = localLinkedCommandCausalityRef.current?.targetSlice === next
            ? localLinkedCommandCausalityRef.current
            : null;
          let linkedIntentLeadFrames = 0;
          const commitVisibleWheelSlice = () => {
            let commitRouteAuthorityCurrent = false;
            try { commitRouteAuthorityCurrent = new URL(window.location.href).href === scheduledRouteHref; } catch (_) {}
            const commitAuthorityCurrent = scheduledLinkedAuthorityEpoch === linkedAuthorityEpochRef.current
              && scheduledCacheEpoch === cacheEpochRef.current
              && commitRouteAuthorityCurrent
              && requestedSliceRef.current === next
              && scrollDirectionRef.current === (steps > 0 ? 1 : -1);
            const resourceSnapshot = cineResourceGovernor?.snapshot?.();
            const linkedIntentLeadLimit = linkedIntentLeadFrameLimit(resourceSnapshot);
            if (commitAuthorityCurrent && linkedScrollEnabled
                && linkedIntentLeadFrames < linkedIntentLeadLimit) {
              const linkedIntentOutstanding = Number(resourceSnapshot?.linkedIntentPending || 0)
                + Number(resourceSnapshot?.linkedIntentActive || 0);
              if (linkedIntentOutstanding > 0) {
                linkedIntentLeadFrames += 1;
                scrollFrameRef.current = window.requestAnimationFrame(() => {
                  scrollFrameRef.current = null;
                  commitVisibleWheelSlice();
                });
                return;
              }
            }
            if (commitAuthorityCurrent) {
              if (isViewportEvidenceNetworkBridgeActive()) {
                const owner = cineWheelInteractionRef.current;
                if (owner) recordViewportCineWheelEffect(owner.interaction, index, 'visible-load-start');
              }
              loadSlice(next, wlRef.current, undefined, {
                skipPreload: true,
                directDisplay: true,
                evidencePhase: cineRenderRequest ? 'cine' : 'single-step',
                evidenceInteraction: 'wheel',
                interactionPerformanceAuthority,
                cineRenderRequest,
                inputCausality,
                linkedCausality: scheduledLinkedCausality,
                requestOrigin: linkedScrollEnabled ? VIEWPORT_REQUEST_ORIGIN.LOCAL_LINKED : VIEWPORT_REQUEST_ORIGIN.LOCAL_NAVIGATION,
                linkedAuthorityEpoch: scheduledLinkedAuthorityEpoch,
              });
              if (isViewportEvidenceNetworkBridgeActive()) {
                const owner = cineWheelInteractionRef.current;
                if (owner) recordViewportCineWheelEffect(owner.interaction, viewportIndexRef.current, 'idle-preload-schedule');
              }
              scheduleScrollIdlePreload(next, wlRef.current, seriesRef.current, scrollDirectionRef.current);
            }
            if (Math.abs(scrollPendingRef.current) >= VIEWPORT_WHEEL_PIXELS_PER_SLICE) scheduleWheelScroll();
          };
          if (linkedScrollEnabled) {
            scrollFrameRef.current = window.requestAnimationFrame(() => {
              scrollFrameRef.current = null;
              commitVisibleWheelSlice();
            });
          } else {
            commitVisibleWheelSlice();
          }
        } else observeNoWorkWheelActivity(steps > 0 ? 'forward' : 'backward');
      }
      if (steps === 0) observeNoWorkWheelActivity(
        scrollDirectionRef.current > 0 ? 'forward'
          : (scrollDirectionRef.current < 0 ? 'backward' : null),
      );
      if (Math.abs(scrollPendingRef.current) >= VIEWPORT_WHEEL_PIXELS_PER_SLICE) {
        scheduleWheelScroll();
      }
    });
  }, [cineResourceGovernor, cineWheelEvidenceTestInjection, index, linkedAuthorityEpoch, linkedScrollEnabled, maxSlice, loadSlice, onInteractionPerformanceEvent, publishScrubSliceState, scheduleScrollIdlePreload, viewportInstanceEpoch]);

  const observeCurrentWheelOwnerActivity = useCallback((nativeEvent, direction, retainForFrame) => {
    const owner = cineWheelInteractionRef.current;
    if (!owner || !direction) return false;
    const ownerIsActive = isViewportCineInteractionActive(owner.interaction);
    let ownerIsCurrent = false;
    try { ownerIsCurrent = owner.isCurrent() === true; } catch (_) { ownerIsCurrent = false; }
    if (!ownerIsActive || !ownerIsCurrent || owner.direction !== direction) {
      if (ownerIsActive) cancelViewportCineInteraction(owner.interaction);
      cineWheelInteractionRef.current = null;
      cineWheelRenderRequestRef.current = null;
      cineWheelPendingRenderOrdinalRef.current = null;
      return false;
    }
    const testInjection = resolveWindowLevelEvidenceTestInjection(cineWheelEvidenceTestInjection);
    const evidenceEvent = testInjection ? testInjection.eventForNativeEvent(nativeEvent) : nativeEvent;
    if (!retainForFrame) {
      const readSliceState = () => {
        const safeNullable = candidate => Number.isSafeInteger(candidate) && candidate >= 0
          ? candidate : null;
        const acceptedSlice = safeNullable(sliceRef.current);
        const acceptedSurface = acceptedVisibleSurfaceRef.current;
        const paintedSurface = acceptedSurface?.surface === 'preview'
          ? scrubPreviewCanvasRef.current : imgRef.current;
        const renderedSlice = readCanonicalUnsignedDomAttribute(
          paintedSurface, 'data-viewport-accepted-slice-current',
        );
        if (renderedSlice === null) throw new Error('rendered_slice_authority_unavailable');
        return Object.freeze({
          requestedSlice: safeNullable(requestedSliceRef.current),
          acceptedSlice,
          renderedSlice,
          sourceOrdinal: safeNullable(acceptedVisibleSurfaceRef.current?.generation),
          pendingRenderOrdinal: safeNullable(cineWheelPendingRenderOrdinalRef.current),
        });
      };
      return observeViewportCineZeroDelta(
        owner.interaction,
        evidenceEvent,
        index,
        readSliceState,
      ) === true;
    }
    const receipt = observeViewportCineWheelActivityForFrame(
      owner.interaction,
      evidenceEvent,
      index,
    );
    const accepted = receipt !== null;
    cineWheelActivityRef.current = {
      nativeEvent,
      interaction: owner.interaction,
      evidenceEvent,
      accepted,
      receipt,
    };
    return accepted;
  }, [cineWheelEvidenceTestInjection, index]);

  const handleWheel = useCallback((e) => {
    // Native wheel listener replacement is passive-effect driven. A listener
    // from the prior render can therefore run after the authority-changing
    // layout effect; reject it before it can contaminate shared burst/pending
    // state or perform governor/focus/warmup work.
    if (linkedAuthorityEpoch !== linkedAuthorityEpochRef.current) {
      e.preventDefault();
      return;
    }
    if (viewportFrozen) { e.preventDefault(); return; }
    if (!acceptedVisibleSurfaceRef.current || !Number.isFinite(sliceRef.current)) { e.preventDefault(); return; }
    e.preventDefault();
    // Ctrl+scroll = zoom
    if (e.ctrlKey) {
      const factor = e.deltaY < 0 ? 1.15 : 0.87;
      setZoom(prev => Math.max(0.5, Math.min(8, prev * factor)));
      return;
    }
    const rawDeltaPixels = normalizeViewportWheelDelta(e);
    if (!rawDeltaPixels) {
      if (isViewportEvidenceNetworkBridgeActive()) {
        observeCurrentWheelOwnerActivity(
          e,
          scrollDirectionRef.current > 0 ? 'forward'
            : (scrollDirectionRef.current < 0 ? 'backward' : null),
          false,
        );
      }
      return;
    }
    const now = window.performance?.now?.() || Date.now();
    deferredSeriesGeometryCoordinatorRef.current.deferForInteraction();
    const deltaPixels = applyViewportWheelBurstDamping(rawDeltaPixels, lastWheelEventAtRef.current, now);
    const rapidBurst = deltaPixels !== rawDeltaPixels;
    lastWheelEventAtRef.current = now;
    if (!deltaPixels) return;
    const evidenceBridgeActive = isViewportEvidenceNetworkBridgeActive();
    const wheelDirection = deltaPixels < 0 ? -1 : 1;
    const projectedBurstSnapshot = burstProjectedPreloadRef.current;
    const preserveProjectedBurstOnWheel = projectedBurstSnapshot.direction !== 0
      && projectedBurstSnapshot.direction === wheelDirection
      && now - projectedBurstSnapshot.at < VIEWPORT_BURST_PROJECTED_PRELOAD_TTL_MS;
    wheelInputCausalityRef.current.observe({
      bridgeActive: isViewportEvidenceCinePhaseActive(),
      rawDelta: rawDeltaPixels,
      effectiveDelta: deltaPixels,
    });
    if (evidenceBridgeActive) {
      const priorActivity = cineWheelActivityRef.current;
      if (priorActivity?.receipt) clearViewportCineWheelActivityForFrame(priorActivity.receipt);
      cineWheelEventRef.current = e;
      cineWheelActivityRef.current = null;
      const direction = deltaPixels > 0 ? 'forward' : 'backward';
      const ownerBeforeActivity = cineWheelInteractionRef.current;
      let ownerExpectedCurrent = Boolean(ownerBeforeActivity
        && ownerBeforeActivity.direction === direction
        && isViewportCineInteractionActive(ownerBeforeActivity.interaction));
      if (ownerExpectedCurrent) {
        try { ownerExpectedCurrent = ownerBeforeActivity.isCurrent() === true; } catch (_) {
          ownerExpectedCurrent = false;
        }
      }
      const currentOwnerActivityAccepted = observeCurrentWheelOwnerActivity(e, direction, true);
      if (ownerExpectedCurrent && currentOwnerActivityAccepted !== true) {
        // Evidence diagnostics must never consume a real radiologist wheel event.
        // Quarantine the invalid observer owner and let the clinical navigation
        // path below schedule the exact visible request under fresh authority.
        if (ownerBeforeActivity && isViewportCineInteractionActive(ownerBeforeActivity.interaction)) {
          cancelViewportCineInteraction(ownerBeforeActivity.interaction);
        }
        cineWheelInteractionRef.current = null;
        cineWheelRenderRequestRef.current = null;
        cineWheelPendingRenderOrdinalRef.current = null;
      }
    }
    if (isViewportEvidenceNetworkBridgeActive()) {
      const owner = cineWheelInteractionRef.current;
      if (owner) recordViewportCineWheelEffect(
        owner.interaction,
        viewportIndexRef.current,
        'speculative-authority-invalidation',
      );
    }
    if (preserveProjectedBurstOnWheel) {
      invalidateSpeculativeClaimAuthority('adjacent');
      invalidateSpeculativeClaimAuthority('full-stack');
      invalidateSpeculativeClaimAuthority('linked-intent');
    } else {
      invalidateSpeculativeClaimAuthority();
    }
    if (isViewportEvidenceNetworkBridgeActive()) {
      const owner = cineWheelInteractionRef.current;
      if (owner) recordViewportCineWheelEffect(
        owner.interaction,
        viewportIndexRef.current,
        'governor-begin-visible-interaction',
      );
    }
    cineResourceGovernor?.beginVisibleInteraction({
      preserveSpeculativeKinds: [
        ...(linkedScrollEnabled ? ['linked-intent'] : []),
        ...(preserveProjectedBurstOnWheel ? ['burst-projected'] : []),
      ],
    });
    if (!isActiveViewport && typeof onViewportFocus === 'function') {
      if (isViewportEvidenceNetworkBridgeActive()) {
        const owner = cineWheelInteractionRef.current;
        if (owner) recordViewportCineWheelEffect(owner.interaction, viewportIndexRef.current, 'focus-callback');
      }
      onViewportFocus();
    }
    // Do not let speculative full-stack browser warmup compete with interactive
    // wheel/cine scrolling. Redis frame-cache hits are already fast; wheel input
    // should prioritize visible frames and nearby idle preload only. Keep
    // reference/linked geometry live at the bounded control-state cadence.
    if (isViewportEvidenceNetworkBridgeActive()) {
      const owner = cineWheelInteractionRef.current;
      if (owner) recordViewportCineWheelEffect(owner.interaction, viewportIndexRef.current, 'warmup-pause');
    }
    pauseActiveSeriesBrowserWarmup();
    if (isViewportEvidenceNetworkBridgeActive()) {
      const owner = cineWheelInteractionRef.current;
      if (owner) recordViewportCineWheelEffect(owner.interaction, viewportIndexRef.current, 'scroll-pending-mutation');
    }
    scrollPendingRapidBurstRef.current = scrollPendingRapidBurstRef.current || rapidBurst;
    scrollPendingRef.current += deltaPixels;
    scrollDirectionRef.current = deltaPixels > 0 ? 1 : -1; // track direction for asymmetric preload
    scheduleWheelScroll();
    if (isViewportEvidenceNetworkBridgeActive()) {
      const owner = cineWheelInteractionRef.current;
      if (owner) recordViewportCineWheelEffect(owner.interaction, viewportIndexRef.current, 'idle-preload-schedule');
    }
    scheduleScrollIdlePreload(sliceRef.current ?? 0, wlRef.current, seriesRef.current, scrollDirectionRef.current);
  }, [cineResourceGovernor, invalidateSpeculativeClaimAuthority, isActiveViewport, linkedAuthorityEpoch, linkedScrollEnabled, observeCurrentWheelOwnerActivity, onViewportFocus, pauseActiveSeriesBrowserWarmup, scheduleScrollIdlePreload, scheduleWheelScroll, viewportFrozen]);

  const bindViewportImageArea = useCallback((node) => {
    boxRef.current = node;
    replaceViewportNativeWheelBinding(wheelBindingRef, node, handleWheel);
  }, [handleWheel]);

  const loadSeriesIntoViewport = useCallback(async (item, options = {}) => {
    clearVisible();
    const seriesLoadGeneration = seriesLoadGenerationRef.current.begin();
    currentSeriesLoadGenerationRef.current = seriesLoadGeneration;
    interactiveRequestCoordinatorRef.current.beginVisible({ slice: null, direction: scrollDirectionRef.current || 1 });
    cancelLinkedScrollFrame();
    cancelLinkedFollowerRetry();
    linkedFollowerRetryRef.current = { key: '', attempts: 0 };
    cancelWheelScrollFrame();
    cancelScrubFrame();
    annotationDragRef.current = null;
    suppressNextCanvasClickRef.current = false;
    roiStartRef.current = null;
    const triangulationCaptureTarget = triangulationDragCaptureTargetRef.current;
    const triangulationCapturePointerId = triangulationDragPointerIdRef.current;
    triangulationDragCaptureTargetRef.current = null;
    try {
      if (triangulationCaptureTarget && triangulationCapturePointerId != null
          && (!triangulationCaptureTarget.hasPointerCapture
            || triangulationCaptureTarget.hasPointerCapture(triangulationCapturePointerId))) {
        triangulationCaptureTarget.releasePointerCapture?.(triangulationCapturePointerId);
      }
    } catch (_) {}
    triangulationDragStateRef.current = null;
    triangulationDragPointerIdRef.current = null;
    triangulationDragGestureRef.current = null;
    if (triangulationDragFrameRef.current != null) {
      window.cancelAnimationFrame(triangulationDragFrameRef.current);
      triangulationDragFrameRef.current = null;
    }
    viewportGeometryBroadcastCoordinatorRef.current.invalidate(index);
    invalidateActiveSeriesBrowserWarmup();
    if (scrubPreviewCanvasRef.current) scrubPreviewCanvasRef.current.style.display = 'none';
    interactiveRequestCoordinatorRef.current.abortVisibleImage();
    cancelScrollIdlePreload();
    presentationRestoreCoordinatorRef.current?.invalidate();
    presentationSuppressSaveRef.current = true;
    presentationRestoreKeyRef.current = '';
    setPresentationVersion(0);
    setPresentationSaveStatus('Loading annotations…');
    setPresentationSaveError('');
    setImgError(false);
    imgSrcRef.current = null;
    setImgSrc(null);
    setLoading(true);
    setSeries(null);
    setMeasurements([]);
    setPendingMeasure(null);
    setRois([]);
    setPendingRoi(null);
    setAnnotations([]);
    setPendingArrow(null);
    setSelectedMarkup(null);
    setHoveredAnnotationId(null);
    propertyEditSessionRef.current = null;
    setPropertyPopover(null);
    setPendingFreehand(null);
    setPendingRect(null);
    setPendingEllipse(null);
    setPendingAngle([]);
    setPendingCobb([]);
    setTextPopover(null);
    setPropertyPopover(null);
    setPixelSpacing(null);
    setSeriesGeometry([]);
    seriesGeometryRef.current = [];
    setNaturalSize(null);
    naturalSizeRef.current = null;
    setZoom(1.0);
    setPanOffset({ x: 0, y: 0 });
    try {
      const resolvedCount = await seriesLoadGenerationRef.current.resolve(seriesLoadGeneration, () => resolveDisplayFrameCount(item, getSeriesCount));
      if (!resolvedCount.accepted) return false;
      return seriesLoadGenerationRef.current.continue(seriesLoadGeneration, () => {
        const count = resolvedCount.value;
        const initialSlice = preferredInitialSlice(item, count);
        const max = Math.max(count - 1, 0);
        const wl = resolveEffectiveDefaultWindowLevel({ initialWL });
        setMaxSlice(max);
        requestedSliceRef.current = initialSlice;
        const effectiveWl = normalizeViewportWindowLevel(wl);
        windowLevelSourceRef.current = effectiveWl.source || 'auto';
        setWindowLevel(effectiveWl);
        wlRef.current = effectiveWl;
        setWlDraft(formatWindowLevelDraft(effectiveWl));
        seriesRef.current = item;
        setSeries(item);
        const shouldNotify = options.notify !== false;
        if (shouldNotify && typeof onSeriesLoad === 'function') onSeriesLoad(item);
        if (shouldNotify) notifyWindowLevelChange(effectiveWl);
        loadSlice(initialSlice, effectiveWl, item, {
          initialVisibleNeighbors: true,
          resolvedMaxSlice: max,
          requestOrigin: options.requestOrigin || VIEWPORT_REQUEST_ORIGIN.LOCAL,
        });

        // Pixel spacing and patient-space geometry are needed for measurements.
        // Preserve immediate loading for ordinary series, but defer large-series
        // metadata because a 2,304-frame response expands to tens of MB and its
        // XHR completion parses on the renderer main thread.
        const applySeriesGeometry = (geometry, signal = null) => {
          if (signal?.aborted) return;
          seriesLoadGenerationRef.current.continue(seriesLoadGeneration, () => {
            const safeGeometry = Array.isArray(geometry) ? geometry : [];
            setSeriesGeometry(safeGeometry);
            seriesGeometryRef.current = safeGeometry;
            const ps = safeGeometry.find(frame => Array.isArray(frame?.pixelSpacing) && frame.pixelSpacing.length >= 2)?.pixelSpacing;
            const parsedPixelSpacing = ps ? [Number(ps[0]), Number(ps[1])] : null;
            setPixelSpacing(parsedPixelSpacing?.every(value => Number.isFinite(value) && value > 0) ? parsedPixelSpacing : null);
            const parsedSliceThickness = safeGeometry
              .map(frame => Number(frame?.sliceThickness || frame?.spacingBetweenSlices || 0))
              .find(value => Number.isFinite(value) && value > 0);
            const effectiveSeries = parsedSliceThickness && !nativeSliceThicknessMm(item)
              ? { ...item, sliceThickness: parsedSliceThickness }
              : item;
            seriesRef.current = effectiveSeries;
            setSeries(effectiveSeries);
            const dicomDefaultWl = resolveEffectiveDefaultWindowLevel({ geometry: safeGeometry });
            if (dicomDefaultWl && windowLevelSourceRef.current === 'auto') {
              windowLevelSourceRef.current = dicomDefaultWl.source;
              setWindowLevel(dicomDefaultWl);
              wlRef.current = dicomDefaultWl;
              setWlDraft(formatWindowLevelDraft(dicomDefaultWl));
              notifyWindowLevelChange(dicomDefaultWl);
              loadSlice(initialSlice, dicomDefaultWl, effectiveSeries, {
                initialVisibleNeighbors: true,
                resolvedMaxSlice: max,
                requestOrigin: options.requestOrigin || VIEWPORT_REQUEST_ORIGIN.LOCAL,
              });
            }
          });
        };
        const rejectSeriesGeometry = (signal = null) => {
          if (!signal?.aborted) seriesLoadGenerationRef.current.continue(seriesLoadGeneration, () => {});
        };
        if (shouldDeferSeriesGeometry(count)) {
          // A new wheel input aborts in-flight large metadata before renderer
          // parsing and reschedules it after a fresh interaction-idle window.
          deferredSeriesGeometryCoordinatorRef.current.replace(({ signal }) =>
            getSeriesGeometry(item.studyInstanceUID, item.seriesInstanceUID, { signal })
              .then(geometry => applySeriesGeometry(geometry, signal))
              .catch(() => rejectSeriesGeometry(signal)));
        } else {
          deferredSeriesGeometryCoordinatorRef.current.cancel();
          getSeriesGeometry(item.studyInstanceUID, item.seriesInstanceUID)
            .then(geometry => applySeriesGeometry(geometry))
            .catch(() => rejectSeriesGeometry());
        }
      });
    } catch {
      return seriesLoadGenerationRef.current.continue(seriesLoadGeneration, () => {
        setLoading(false);
        setImgError(true);
        setSeries(item);
      });
    }
  }, [index, initialWL, loadSlice, notifyWindowLevelChange, onSeriesLoad, cancelLinkedFollowerRetry, cancelLinkedScrollFrame, cancelWheelScrollFrame, cancelScrubFrame, cancelScrollIdlePreload, clearVisible, invalidateActiveSeriesBrowserWarmup]);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item) => {
      onDrop && onDrop(index, item);
      loadSeriesIntoViewport(item);
      // Clear per-viewport overlays on new series
      setMeasurements([]);
      setPendingMeasure(null);
      setRois([]);
      setPendingRoi(null);
      setAnnotations([]);
      setPendingArrow(null);
      setSelectedMarkup(null);
      propertyEditSessionRef.current = null;
      setPropertyPopover(null);
      setPendingFreehand(null);
      setPendingRect(null);
      setPendingEllipse(null);
      setPendingAngle([]);
      setPendingCobb([]);
      setTextPopover(null);
    },
    collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
  });

  const bindViewportRoot = useCallback((node) => {
    viewportRootRef.current = node;
    drop(node);
  }, [drop]);

  // --- Left-drag interaction: default scroll/cine; explicit W/L still windows ---
  const handleViewportDoubleClick = useCallback((e) => {
    if (isViewportEditableTarget(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof onViewportFocus === 'function') onViewportFocus();
    if (typeof onViewportDoubleClick === 'function' && seriesRef.current && acceptedVisibleSurfaceRef.current && Number.isFinite(sliceRef.current)) {
      onViewportDoubleClick(index, { series: seriesRef.current, slice: sliceRef.current, windowLevel: wlRef.current });
      return;
    }
    setZoom(1.0);
    setPanOffset({ x: 0, y: 0 });
  }, [index, onViewportDoubleClick, onViewportFocus]);

  const handlePetSliceThicknessChange = useCallback((e) => {
    if (!acceptedVisibleSurfaceRef.current || !Number.isFinite(sliceRef.current)) return;
    e.stopPropagation();
    const next = Number(e.target.value);
    const safeNext = PET_SLICE_THICKNESS_OPTIONS_MM.includes(next) ? next : 1;
    petSliceThicknessRef.current = safeNext;
    setPetSliceThicknessMm(safeNext);
    loadSlice(sliceRef.current, wlRef.current, seriesRef.current);
  }, [loadSlice]);

  const handleMouseDown = (e) => {
    if (viewportFrozen) return;
    if (!acceptedVisibleSurfaceRef.current || !Number.isFinite(sliceRef.current)) return;
    if (e.button !== 0) return;
    if (isAnnotationTool) return; // handled separately on canvas
    e.preventDefault();
    if (activeTool === 'wl') {
      const startWl = normalizeViewportWindowLevel({
        ww: wlRef.current.ww || 400,
        wc: wlRef.current.wc || 40,
      });
      dragStartRef.current = { mode: 'wl', x: e.clientX, y: e.clientY, ww: startWl.ww, wc: startWl.wc };
      closeWindowLevelEvidenceInteraction('cancelled');
      if (isViewportEvidenceNetworkBridgeActive()) {
        const testInjection = resolveWindowLevelEvidenceTestInjection(windowLevelEvidenceTestInjection);
        if (!windowLevelEvidenceClockRef.current) {
          windowLevelEvidenceClockRef.current = testInjection?.clock
            || createViewportWindowLevelBrowserClock();
        }
        const requestViewportRoot = viewportRootRef.current;
        const seriesLoadGeneration = currentSeriesLoadGenerationRef.current;
        const currentSeries = seriesRef.current;
        const acceptedGeneration = acceptedVisibleSurfaceRef.current?.generation;
        let routeHref = null;
        try { routeHref = new URL(window.location.href).href; } catch (_) {}
        const state = {
          interaction: null,
          input: null,
          publication: null,
          renderEvidence: null,
          released: false,
          paintFrames: new Set(),
          phase: 'single-step',
          generation: acceptedGeneration,
          starting: true,
        };
        const isAuthorityCurrent = () => {
          try {
            const liveSeries = seriesRef.current;
            return isViewportEvidenceNetworkBridgeActive()
              && viewportMountedRef.current === true
              && (state.starting || windowLevelEvidenceInteractionRef.current === state)
              && viewportIndexRef.current === index
              && viewportRootRef.current === requestViewportRoot
              && requestViewportRoot?.isConnected === true
              && currentSeriesLoadGenerationRef.current === seriesLoadGeneration
              && seriesLoadGenerationRef.current.isCurrent(seriesLoadGeneration)
              && liveSeries?.studyInstanceUID === currentSeries?.studyInstanceUID
              && liveSeries?.seriesInstanceUID === currentSeries?.seriesInstanceUID
              && new URL(window.location.href).href === routeHref;
          } catch (_) {
            return false;
          }
        };
        const interaction = beginViewportWindowLevelInteraction({
          event: testInjection ? testInjection.eventForNativeEvent(e.nativeEvent) : e.nativeEvent,
          clock: windowLevelEvidenceClockRef.current,
          phase: state.phase,
          viewport: index,
          generation: state.generation,
          windowLevel: Object.freeze({ ww: startWl.ww, wc: startWl.wc }),
          isAuthorityCurrent,
        });
        state.starting = false;
        if (interaction) {
          state.interaction = interaction;
          windowLevelEvidenceInteractionRef.current = state;
        }
      }
    } else {
      const requestedSlice = requestedSliceRef.current ?? sliceRef.current ?? 0;
      dragStartRef.current = { mode: 'scroll', y: e.clientY, startSlice: requestedSlice, lastSlice: requestedSlice };
    }
    setDragging(true);
  };

  const handleMouseMove = useCallback((e) => {
    const dragStart = dragStartRef.current;
    if (!dragging || !dragStart) return;
    if (dragStart.mode === 'wl') {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      const wl = normalizeViewportWindowLevel({
        ww: Math.max(1, dragStart.ww + dx * 5),
        wc: dragStart.wc - dy * 2,
      });
      wlRef.current = wl;
      const state = windowLevelEvidenceInteractionRef.current;
      if (state) {
        const testInjection = resolveWindowLevelEvidenceTestInjection(windowLevelEvidenceTestInjection);
        const input = observeViewportWindowLevelInput(state.interaction, {
          event: testInjection ? testInjection.eventForNativeEvent(e) : e,
          windowLevel: Object.freeze({ ww: wl.ww, wc: wl.wc }),
        });
        if (!input) {
          closeWindowLevelEvidenceInteraction('failed', state);
          windowLevelDragCoordinatorRef.current.schedule(wl);
          return;
        }
        state.paintFrames.forEach(frameId => window.cancelAnimationFrame(frameId));
        state.paintFrames.clear();
        state.input = input;
        state.publication = null;
        state.renderEvidence = null;
        windowLevelDragCoordinatorRef.current.schedule(wl, input);
      } else {
        windowLevelDragCoordinatorRef.current.schedule(wl);
      }
      return;
    }

    const pixelsPerSlice = 8;
    const deltaSlices = Math.trunc((e.clientY - dragStart.y) / pixelsPerSlice);
    const next = Math.max(0, Math.min(maxSlice, dragStart.startSlice + deltaSlices));
    if (next !== dragStart.lastSlice) {
      scrollDirectionRef.current = next > dragStart.lastSlice ? 1 : -1;
      publishScrubSliceState(next);
      scheduleScrubSliceLoad(next, { allowColdDisplay: true });
      dragStartRef.current.lastSlice = next;
    }
  }, [closeWindowLevelEvidenceInteraction, dragging, maxSlice, publishScrubSliceState, scheduleScrubSliceLoad, windowLevelEvidenceTestInjection]);

  const handleMouseUp = useCallback((e) => {
    if (dragging) {
      const wasWindowing = dragStartRef.current?.mode === 'wl';
      setDragging(false);
      dragStartRef.current = null;
      if (wasWindowing) {
        const finalWl = windowLevelDragCoordinatorRef.current.snapshot().latest || { ...wlRef.current };
        const state = windowLevelEvidenceInteractionRef.current;
        if (state) {
          const testInjection = resolveWindowLevelEvidenceTestInjection(windowLevelEvidenceTestInjection);
          state.released = releaseViewportWindowLevelInteraction(state.interaction, {
            event: testInjection ? testInjection.eventForNativeEvent(e) : e,
            windowLevel: Object.freeze({ ww: finalWl.ww, wc: finalWl.wc }),
          }) === true;
          if (!state.released) closeWindowLevelEvidenceInteraction('failed', state);
          else {
            windowLevelDragCoordinatorRef.current.flushLatest();
            attemptWindowLevelEvidenceSettlement(state);
          }
        }
        wlRef.current = finalWl;
        windowLevelSourceRef.current = finalWl.source || 'manual';
        setWindowLevel(finalWl);
        setWlDraft(formatWindowLevelDraft(finalWl));
        notifyWindowLevelChange(finalWl);
      } else {
        finishScrubInteraction(requestedSliceRef.current);
      }
    }
  }, [attemptWindowLevelEvidenceSettlement, closeWindowLevelEvidenceInteraction, dragging, finishScrubInteraction, notifyWindowLevelChange, windowLevelEvidenceTestInjection]);

  const handleSliceSliderLiveChange = useCallback((safeNext) => {
    if (viewportFrozen || !hasAcceptedClinicalSurface()) return;
    scrollDirectionRef.current = safeNext > requestedSliceRef.current
      ? 1
      : safeNext < requestedSliceRef.current ? -1 : scrollDirectionRef.current;
    publishScrubSliceState(safeNext);
    scheduleScrubSliceLoad(safeNext, { allowColdDisplay: true });
  }, [hasAcceptedClinicalSurface, publishScrubSliceState, scheduleScrubSliceLoad, viewportFrozen]);

  const handleSliceSliderCommit = useCallback((safeNext) => {
    if (viewportFrozen || !hasAcceptedClinicalSurface()) return;
    const clamped = Math.max(0, Math.min(maxSlice, Number(safeNext) || 0));
    if (clamped !== requestedSliceRef.current) {
      if (Number.isFinite(requestedSliceRef.current)) sliceSliderRef.current?.setLiveSlice(requestedSliceRef.current);
      return;
    }
    finishScrubInteraction(clamped);
  }, [finishScrubInteraction, hasAcceptedClinicalSurface, maxSlice, viewportFrozen]);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  const getDomImageRect = useCallback(() => {
    const parent = boxRef.current;
    if (!parent) return null;

    // The <img> element deliberately fills the viewport so object-fit:contain can
    // upscale as the browser/window grows. getBoundingClientRect() on that element
    // would return the full viewport (including letterbox bars), not the actual
    // visible DICOM bitmap. Compute the contained bitmap rect from the parent
    // dimensions instead, then apply the same zoom/pan transform used by CSS.
    const ns = naturalSizeRef.current || naturalSize;
    const pr = parent.getBoundingClientRect();
    if (!ns || pr.width <= 0 || pr.height <= 0) {
      const img = imgRef.current;
      if (!img) return null;
      const ir = img.getBoundingClientRect();
      return {
        left: ir.left - pr.left,
        top: ir.top - pr.top,
        width: ir.width,
        height: ir.height,
      };
    }

    return computeImgRect(
      { __logicalWidth: pr.width, __logicalHeight: pr.height, width: pr.width, height: pr.height },
      ns,
      zoom,
      panOffset,
    );
  }, [naturalSize, zoom, panOffset]);

  // --- Measure tool: click on canvas ---
  // Convert mouse event to image-relative coords (0–1).
  // Uses the canvas bounding rect + naturalSize to stay in the same coordinate
  const eventToImageCoords = useCallback((e, { clamp = true } = {}) => {
    const imgRect = getDomImageRect();
    const parent = boxRef.current;
    if (!imgRect || !parent || imgRect.width <= 0 || imgRect.height <= 0) return null;
    const pr = parent.getBoundingClientRect();
    const rawX = (e.clientX - pr.left - imgRect.left) / imgRect.width;
    const rawY = (e.clientY - pr.top - imgRect.top) / imgRect.height;
    if (!clamp && (rawX < 0 || rawX > 1 || rawY < 0 || rawY > 1)) return null;
    return { x: Math.max(0, Math.min(1, rawX)), y: Math.max(0, Math.min(1, rawY)) };
  }, [getDomImageRect]);
  const shouldPreventTriangulationAuxClick = useCallback((e) => hasAcceptedClinicalSurface() && shouldHandleTriangulationMiddleClick({
    button: e.button,
    viewportFrozen: viewportFrozenRef.current,
    point: eventToImageCoords(e, { clamp: false }),
    series: seriesRef.current,
  }), [eventToImageCoords, hasAcceptedClinicalSurface]);
  const emitTriangulationPoint = useCallback((point, meta = null) => {
    if (!point || !hasAcceptedClinicalSurface() || typeof onTriangulationPoint !== 'function') return;
    const payload = createTriangulationPoint({
      sourceViewportIndex: index,
      point,
      slice: sliceRef.current,
      total: maxSlice + 1,
      series: seriesRef.current,
      sourceGeometry: seriesGeometryRef.current?.[sliceRef.current] || null,
      sourceDimensions: naturalSizeRef.current || naturalSize,
    });
    if (!payload) return false;
    try {
      onTriangulationPoint(payload, meta);
      return true;
    } catch (_) {
      const captureTarget = triangulationDragCaptureTargetRef.current;
      const pointerId = triangulationDragPointerIdRef.current;
      triangulationDragCaptureTargetRef.current = null;
      triangulationDragPointerIdRef.current = null;
      triangulationDragGestureRef.current = null;
      triangulationDragStateRef.current = null;
      if (triangulationDragFrameRef.current != null) {
        window.cancelAnimationFrame(triangulationDragFrameRef.current);
        triangulationDragFrameRef.current = null;
      }
      try {
        if (captureTarget && pointerId != null
            && (!captureTarget.hasPointerCapture || captureTarget.hasPointerCapture(pointerId))) {
          captureTarget.releasePointerCapture?.(pointerId);
        }
      } catch (_) {}
      return false;
    }
  }, [hasAcceptedClinicalSurface, index, maxSlice, naturalSize, onTriangulationPoint]);

  const scheduleTriangulationDragUpdate = useCallback((point) => {
    triangulationDragStateRef.current = point;
    if (triangulationDragFrameRef.current != null) return;
    triangulationDragFrameRef.current = window.requestAnimationFrame(() => {
      triangulationDragFrameRef.current = null;
      const nextPoint = triangulationDragStateRef.current;
      triangulationDragStateRef.current = null;
      // Mid-drag frames are transient: they travel the imperative drag channel
      // only, so no page/pane React render occurs per pointer frame. The
      // gesture-ending emission commits durable React state.
      emitTriangulationPoint(nextPoint, { transient: true });
    });
  }, [emitTriangulationPoint]);

  const releaseTriangulationPointerCapture = useCallback(() => {
    const target = triangulationDragCaptureTargetRef.current;
    const pointerId = triangulationDragPointerIdRef.current;
    triangulationDragCaptureTargetRef.current = null;
    if (!target || pointerId == null) return;
    try {
      if (!target.hasPointerCapture || target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture?.(pointerId);
      }
    } catch (_) {}
  }, []);

  const acquireTriangulationPointerCapture = useCallback((target, pointerId) => {
    if (!target || pointerId == null) return false;
    try {
      target.setPointerCapture?.(pointerId);
      triangulationDragCaptureTargetRef.current = target;
      return true;
    } catch (_) {
      try { target.releasePointerCapture?.(pointerId); } catch (_) {}
      triangulationDragCaptureTargetRef.current = null;
      triangulationDragPointerIdRef.current = null;
      triangulationDragGestureRef.current = null;
      triangulationDragStateRef.current = null;
      if (triangulationDragFrameRef.current != null) {
        window.cancelAnimationFrame(triangulationDragFrameRef.current);
        triangulationDragFrameRef.current = null;
      }
      return false;
    }
  }, []);

  const handleTriangulationMiddlePointerDown = useCallback((e) => {
    if (!hasAcceptedClinicalSurface()) return false;
    const pt = eventToImageCoords(e, { clamp: false });
    // Gesture entry reads the freeze mirror ref (flipped synchronously in
    // toggleViewportFrozen) rather than the render-lagged prop, so a click in
    // the toggle→commit window cannot start a half-activated drag whose
    // commands the scheduling path would then reject.
    if (!shouldHandleTriangulationMiddleClick({ button: e.button, viewportFrozen: viewportFrozenRef.current, point: pt, series: seriesRef.current })) return false;
    e.preventDefault();
    e.stopPropagation();
    if (typeof onViewportFocus === 'function') onViewportFocus();
    const clearOnTap = shouldClearTriangulationOnMiddleClick(triangulationPoint, index, seriesRef.current);
    if (!acquireTriangulationPointerCapture(e.currentTarget, e.pointerId)) return true;
    triangulationDragPointerIdRef.current = e.pointerId;
    triangulationDragGestureRef.current = {
      button: 1,
      startPoint: pt,
      lastPoint: pt,
      moved: false,
      clearOnTap,
    };
    triangulationDragStateRef.current = null;
    if (!clearOnTap) emitTriangulationPoint(pt);
    return true;
  }, [acquireTriangulationPointerCapture, emitTriangulationPoint, eventToImageCoords, hasAcceptedClinicalSurface, index, onViewportFocus, triangulationPoint]);

  const handleTriangulationMiddlePointerMove = useCallback((e) => {
    const gesture = triangulationDragGestureRef.current;
    if (gesture?.button !== 1 || triangulationDragPointerIdRef.current !== e.pointerId) return false;
    if (viewportFrozenRef.current || !hasAcceptedClinicalSurface() || (Number(e.buttons) & 4) !== 4) return true;
    const pt = eventToImageCoords(e, { clamp: true });
    if (!pt) return true;
    e.preventDefault();
    e.stopPropagation();
    if (Math.hypot(pt.x - gesture.startPoint.x, pt.y - gesture.startPoint.y) > 0.001) gesture.moved = true;
    gesture.lastPoint = pt;
    if (gesture.moved) scheduleTriangulationDragUpdate(pt);
    return true;
  }, [eventToImageCoords, hasAcceptedClinicalSurface, scheduleTriangulationDragUpdate]);

  const finishTriangulationMiddlePointerDrag = useCallback((e, { cancelled = false } = {}) => {
    const gesture = triangulationDragGestureRef.current;
    if (gesture?.button !== 1 || triangulationDragPointerIdRef.current !== e.pointerId) return false;
    releaseTriangulationPointerCapture();
    triangulationDragPointerIdRef.current = null;
    triangulationDragGestureRef.current = null;
    if (triangulationDragFrameRef.current != null) {
      window.cancelAnimationFrame(triangulationDragFrameRef.current);
      triangulationDragFrameRef.current = null;
    }
    triangulationDragStateRef.current = null;
    if (cancelled || viewportFrozenRef.current || !hasAcceptedClinicalSurface()) return true;
    e.preventDefault();
    e.stopPropagation();
    if (!gesture.moved && gesture.clearOnTap) {
      try { onTriangulationPoint?.(null); } catch (_) {}
      return true;
    }
    if (gesture.moved) {
      const pt = eventToImageCoords(e, { clamp: true }) || gesture.lastPoint;
      emitTriangulationPoint(pt);
    }
    return true;
  }, [emitTriangulationPoint, eventToImageCoords, hasAcceptedClinicalSurface, onTriangulationPoint, releaseTriangulationPointerCapture]);

  const handleTriangulationMarkerAuxClick = useCallback((e) => {
    if (e.button !== 1 || !hasAcceptedClinicalSurface()) return;
    e.preventDefault();
    e.stopPropagation();
  }, [hasAcceptedClinicalSurface]);

  const handleTriangulationMarkerPointerDown = useCallback((e) => {
    if (e.button === 1) {
      handleTriangulationMiddlePointerDown(e);
      return;
    }
    // Same freeze-mirror-ref read as the middle-button entry above.
    if (viewportFrozenRef.current || !hasAcceptedClinicalSurface() || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof onViewportFocus === 'function') onViewportFocus();
    if (!acquireTriangulationPointerCapture(e.currentTarget, e.pointerId)) return;
    triangulationDragPointerIdRef.current = e.pointerId;
    triangulationDragGestureRef.current = { button: 0 };
    triangulationDragStateRef.current = null;
  }, [acquireTriangulationPointerCapture, handleTriangulationMiddlePointerDown, hasAcceptedClinicalSurface, onViewportFocus]);

  const handleTriangulationMarkerPointerMove = useCallback((e) => {
    if (handleTriangulationMiddlePointerMove(e)) return;
    if (viewportFrozenRef.current || !hasAcceptedClinicalSurface()) return;
    if (triangulationDragPointerIdRef.current !== e.pointerId || triangulationDragGestureRef.current?.button !== 0) return;
    const pt = eventToImageCoords(e, { clamp: true });
    if (!pt) return;
    e.preventDefault();
    e.stopPropagation();
    scheduleTriangulationDragUpdate(pt);
  }, [eventToImageCoords, handleTriangulationMiddlePointerMove, hasAcceptedClinicalSurface, scheduleTriangulationDragUpdate]);

  const handleTriangulationMarkerPointerUp = useCallback((e) => {
    if (finishTriangulationMiddlePointerDrag(e)) return;
    if (!hasAcceptedClinicalSurface()) {
      releaseTriangulationPointerCapture();
      triangulationDragPointerIdRef.current = null;
      triangulationDragGestureRef.current = null;
      triangulationDragStateRef.current = null;
      return;
    }
    if (triangulationDragPointerIdRef.current !== e.pointerId || triangulationDragGestureRef.current?.button !== 0) return;
    releaseTriangulationPointerCapture();
    triangulationDragPointerIdRef.current = null;
    triangulationDragGestureRef.current = null;
    const pt = eventToImageCoords(e, { clamp: true });
    if (pt) {
      e.preventDefault();
      e.stopPropagation();
      if (triangulationDragFrameRef.current != null) {
        window.cancelAnimationFrame(triangulationDragFrameRef.current);
        triangulationDragFrameRef.current = null;
      }
      triangulationDragStateRef.current = null;
      emitTriangulationPoint(pt);
    }
  }, [emitTriangulationPoint, eventToImageCoords, finishTriangulationMiddlePointerDrag, hasAcceptedClinicalSurface, releaseTriangulationPointerCapture]);

  const cancelTriangulationMarkerPointerDrag = useCallback((e) => {
    if (finishTriangulationMiddlePointerDrag(e, { cancelled: true })) return true;
    if (triangulationDragPointerIdRef.current !== e.pointerId
        || triangulationDragGestureRef.current?.button !== 0) return false;
    e.preventDefault();
    e.stopPropagation();
    releaseTriangulationPointerCapture();
    if (triangulationDragFrameRef.current != null) {
      window.cancelAnimationFrame(triangulationDragFrameRef.current);
      triangulationDragFrameRef.current = null;
    }
    triangulationDragStateRef.current = null;
    triangulationDragPointerIdRef.current = null;
    triangulationDragGestureRef.current = null;
    return true;
  }, [finishTriangulationMiddlePointerDrag, releaseTriangulationPointerCapture]);

  useEffect(() => {
    if (!viewportFrozen) return;
    releaseTriangulationPointerCapture();
    if (triangulationDragFrameRef.current != null) {
      window.cancelAnimationFrame(triangulationDragFrameRef.current);
      triangulationDragFrameRef.current = null;
    }
    triangulationDragStateRef.current = null;
    triangulationDragPointerIdRef.current = null;
    triangulationDragGestureRef.current = null;
  }, [releaseTriangulationPointerCapture, viewportFrozen]);

  useEffect(() => () => {
    releaseTriangulationPointerCapture();
    if (triangulationDragFrameRef.current != null) {
      window.cancelAnimationFrame(triangulationDragFrameRef.current);
      triangulationDragFrameRef.current = null;
    }
    triangulationDragStateRef.current = null;
    triangulationDragPointerIdRef.current = null;
    triangulationDragGestureRef.current = null;
  }, [releaseTriangulationPointerCapture]);

  const annotationStyleForNew = (overrides = {}) => normalizeAnnotationAppearance({ ...annotationStyle, ...overrides });

  const applySelectedAnnotationStyle = (patch) => {
    if (!selectedAnnotationId || !hasAcceptedClinicalSurface()) return;
    setAnnotations(prev => prev.map(a => a.id === selectedAnnotationId ? { ...a, ...patch } : a));
  };

  const updateAnnotationStyle = (patch) => {
    const next = normalizeAnnotationAppearance({ ...annotationStyle, ...patch });
    setAnnotationStyle(next);
    applySelectedAnnotationStyle(patch);
    if (typeof onAnnotationAppearanceChange === 'function') onAnnotationAppearanceChange(next);
  };

  const hitTestAnnotationHandle = (pt, annotationId = null) => {
    const imgRect = getDomImageRect();
    const threshold = imgRect ? Math.max(0.01, 10 / Math.max(imgRect.width, imgRect.height)) : 0.018;
    const visible = annotationsRef.current.filter(a => a.slice === sliceRef.current && (!annotationId || a.id === annotationId));
    for (let i = visible.length - 1; i >= 0; i--) {
      const ann = visible[i];
      const handles = getAnnotationHandlePoints(ann);
      for (let j = handles.length - 1; j >= 0; j--) {
        const h = handles[j];
        if (Math.hypot(pt.x - h.pt.x, pt.y - h.pt.y) <= threshold) return { ann, handle: h };
      }
    }
    return null;
  };

  const hitTestMarkup = (pt) => hitTestViewportMarkupAt({
    annotations: visibleAnnotations,
    measurements: visibleMeasurements.filter(item => !item.pending),
    rois: visibleRois.filter(item => !item.pending),
    point: pt,
    slice: sliceRef.current,
    imageRect: getDomImageRect(),
    defaultFontSize: overlayMeasurementFontSize,
    measurementLabelFor: formatMeasureLabel,
    roiLabelLinesFor: (roi) => roiStatsLines(roi.stats),
  });

  const makeMarkupTarget = useCallback((kind, item) => {
    const currentSeries = seriesRef.current;
    const acceptedSurface = acceptedVisibleSurfaceRef.current;
    return {
      kind,
      id: item?.id,
      viewportIndex: index,
      studyInstanceUID: currentSeries?.studyInstanceUID || '',
      seriesInstanceUID: currentSeries?.seriesInstanceUID || '',
      slice: item?.slice,
      seriesLoadGeneration: currentSeriesLoadGenerationRef.current,
      acceptedGeneration: acceptedSurface?.generation ?? null,
    };
  }, [index]);

  const isMarkupTargetCurrent = useCallback((target) => {
    const currentSeries = seriesRef.current;
    const acceptedSurface = acceptedVisibleSurfaceRef.current;
    return Boolean(target)
      && target.viewportIndex === index
      && target.studyInstanceUID === currentSeries?.studyInstanceUID
      && target.seriesInstanceUID === currentSeries?.seriesInstanceUID
      && target.slice === sliceRef.current
      && target.seriesLoadGeneration === currentSeriesLoadGenerationRef.current
      && target.acceptedGeneration === (acceptedSurface?.generation ?? null)
      && hasAcceptedClinicalSurface();
  }, [hasAcceptedClinicalSurface, index]);

  const isMarkupTargetCollectionOwnerCurrent = useCallback((target) => {
    const currentSeries = seriesRef.current;
    return Boolean(target)
      && target.viewportIndex === index
      && target.studyInstanceUID === currentSeries?.studyInstanceUID
      && target.seriesInstanceUID === currentSeries?.seriesInstanceUID
      && target.seriesLoadGeneration === currentSeriesLoadGenerationRef.current;
  }, [index]);

  const selectAnnotationAt = (pt) => {
    const hit = hitTestMarkup(pt);
    setSelectedMarkup(hit ? makeMarkupTarget(hit.kind, hit.item) : null);
    return hit;
  };

  const updateMarkupItem = useCallback((target, updater) => {
    if (viewportFrozen || !isMarkupTargetCurrent(target)) return false;
    const { kind, id } = target;
    if (kind === 'annotation') setAnnotations(prev => prev.map(item => item.id === id && item.slice === target.slice ? updater(item) : item));
    if (kind === 'measurement') setMeasurements(prev => prev.map(item => item.id === id && item.slice === target.slice ? updater(item) : item));
    if (kind === 'roi') setRois(prev => prev.map(item => item.id === id && item.slice === target.slice ? updater(item) : item));
    return true;
  }, [isMarkupTargetCurrent, viewportFrozen]);

  const closePropertyPopover = () => {
    const session = propertyEditSessionRef.current;
    propertyEditSessionRef.current = null;
    setPropertyPopover(null);
    if (session?.dirty) setAnnotationCommitRevision(current => current + 1);
  };

  const cancelPropertyPopover = useCallback(() => {
    const session = propertyEditSessionRef.current;
    propertyEditSessionRef.current = null;
    setPropertyPopover(null);
    if (!session?.dirty || !isMarkupTargetCollectionOwnerCurrent(session.target)) return;
    const restore = current => restoreMarkupAppearanceSnapshot(current, session.original);
    if (session.target.kind === 'annotation') {
      setAnnotations(prev => prev.map(item => item.id === session.target.id && item.slice === session.target.slice ? restore(item) : item));
    }
    if (session.target.kind === 'measurement') {
      setMeasurements(prev => prev.map(item => item.id === session.target.id && item.slice === session.target.slice ? restore(item) : item));
    }
    if (session.target.kind === 'roi') {
      setRois(prev => prev.map(item => item.id === session.target.id && item.slice === session.target.slice ? restore(item) : item));
    }
  }, [isMarkupTargetCollectionOwnerCurrent]);

  const applyPropertyPopoverPatch = (patch) => {
    const target = propertyPopover?.target;
    if (!target || viewportFrozen || !isMarkupTargetCurrent(target)) return;
    if (propertyEditSessionRef.current) propertyEditSessionRef.current.dirty = true;
    if (!updateMarkupItem(target, current => applyMarkupAppearancePatch(current, patch))) return;
    setPropertyPopover(prev => prev ? {
      ...prev,
      target: { ...prev.target, item: applyMarkupAppearancePatch(prev.target.item, patch) },
    } : null);
  };

  const deleteMarkupTarget = useCallback((target) => {
    if (!target || viewportFrozen || !isMarkupTargetCurrent(target)) return;
    const { kind, id } = target;
    if (kind === 'annotation') setAnnotations(prev => prev.filter(item => item.id !== id || item.slice !== target.slice));
    if (kind === 'measurement') setMeasurements(prev => prev.filter(item => item.id !== id || item.slice !== target.slice));
    if (kind === 'roi') setRois(prev => prev.filter(item => item.id !== id || item.slice !== target.slice));
    propertyEditSessionRef.current = null;
    setSelectedMarkup(null);
    setPropertyPopover(null);
  }, [isMarkupTargetCurrent, viewportFrozen]);

  const deletePropertyPopoverTarget = () => {
    if (!propertyPopover?.target) return;
    deleteMarkupTarget(propertyPopover.target);
  };

  const handleCanvasClick = (e) => {
    if (suppressNextCanvasClickRef.current) {
      suppressNextCanvasClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (viewportFrozen || !hasAcceptedClinicalSurface()) return;
    if (!['measure', 'arrow', 'text', 'angle', 'cobb', 'level', 'select'].includes(activeTool)) return;
    e.stopPropagation();
    const pt = eventToImageCoords(e);
    if (!pt) return;

    if (activeTool === 'select') { selectAnnotationAt(pt); return; }

    if (activeTool === 'measure') {
      const current = pendingMeasureRef.current;
      if (!current) {
        setPendingMeasure({ a: pt, b: pt });
      } else {
        const measurement = {
          a: current.a,
          b: pt,
          id: Date.now(),
          slice: sliceRef.current,
          color: '#00e5ff',
          label: '',
          lineWidth: annotationStyle.lineWidth,
          fontFamily: annotationStyle.fontFamily,
        };
        setMeasurements(prev => [...prev, measurement]);
        setPendingMeasure(null);
        completeToolInteraction();
        if (typeof onViewportFocus === 'function') onViewportFocus();
        if (typeof onReportContextChange === 'function') {
          const measurementCtx = buildReportContext('measurement', { measurement });
          if (measurementCtx) onReportContextChange(measurementCtx);
        }
      }
      return;
    }

    if (activeTool === 'arrow') {
      const current = pendingArrowRef.current;
      if (!current) {
        setPendingArrow({ a: pt, b: pt });
      } else {
        setAnnotations(prev => [...prev, {
          id: uid(), type: 'arrow', slice: sliceRef.current, points: { a: current.a, b: pt }, ...annotationStyleForNew(),
        }]);
        setPendingArrow(null);
        completeToolInteraction();
      }
      return;
    }

    if (activeTool === 'text' || activeTool === 'level') {
      setTextPopover({ x: e.clientX, y: e.clientY, pt, mode: activeTool });
      return;
    }

    if (activeTool === 'angle') {
      const pts = [...pendingAngleRef.current, pt];
      if (pts.length >= 3) {
        setAnnotations(prev => [...prev, { id: uid(), type: 'angle', slice: sliceRef.current, points: pts.slice(0, 3), ...annotationStyleForNew({ color: annotationStyle.color || '#ffeb3b' }) }]);
        setPendingAngle([]);
        completeToolInteraction();
      } else {
        setPendingAngle(pts);
      }
      return;
    }

    if (activeTool === 'cobb') {
      const pts = [...pendingCobbRef.current, pt];
      if (pts.length >= 4) {
        setAnnotations(prev => [...prev, { id: uid(), type: 'cobb', slice: sliceRef.current, points: pts.slice(0, 4), ...annotationStyleForNew({ color: annotationStyle.color || '#66ff66' }) }]);
        setPendingCobb([]);
        completeToolInteraction();
      } else {
        setPendingCobb(pts);
      }
    }
  };

  const finalizeContextualRoiGeometry = (drag, pt) => {
    if (!drag || !pt) return drag?.original || null;
    if (drag.kind === 'roi' && drag.roiResizePart === 'radius') {
      return resizeRoiRadiusTo(drag.original, pt, getDomImageRect());
    }
    const dx = pt.x - drag.startPt.x;
    const dy = pt.y - drag.startPt.y;
    return moveViewportMarkupBy('roi', drag.original, dx, dy);
  };

  const handleCanvasMouseMove = (e) => {
    if (viewportFrozen || !hasAcceptedClinicalSurface()) return;
    const pt = eventToImageCoords(e);
    if (!pt) return;

    if (annotationDragRef.current?.id) {
      const drag = annotationDragRef.current;
      if (drag.pointerId !== e.pointerId || !isMarkupTargetCurrent(drag)) return;
      drag.lastPt = pt;
      const dx = pt.x - drag.startPt.x;
      const dy = pt.y - drag.startPt.y;
      drag.moved = drag.moved || Math.abs(dx) > 0.0001 || Math.abs(dy) > 0.0001;
      if (drag.handleKey && drag.kind === 'annotation') {
        setAnnotations(prev => prev.map(a => a.id === drag.id && a.slice === drag.slice ? resizeAnnotationHandle(drag.original, drag.handleKey, pt) : a));
      } else if (drag.kind === 'measurement' && drag.measurementEndpoint) {
        const movedItem = moveMeasurementEndpointTo(drag.original, drag.measurementEndpoint, pt);
        setMeasurements(prev => prev.map(item => item.id === drag.id && item.slice === drag.slice ? movedItem : item));
      } else if (drag.kind === 'roi' && drag.roiResizePart === 'radius') {
        const resizedItem = resizeRoiRadiusTo(drag.original, pt, getDomImageRect());
        setRois(prev => prev.map(item => item.id === drag.id && item.slice === drag.slice ? resizedItem : item));
      } else {
        const movedItem = moveViewportMarkupBy(drag.kind, drag.original, dx, dy);
        if (drag.kind === 'annotation') setAnnotations(prev => prev.map(item => item.id === drag.id && item.slice === drag.slice ? movedItem : item));
        if (drag.kind === 'measurement') setMeasurements(prev => prev.map(item => item.id === drag.id && item.slice === drag.slice ? movedItem : item));
        if (drag.kind === 'roi') setRois(prev => prev.map(item => item.id === drag.id && item.slice === drag.slice ? movedItem : item));
      }
      return;
    }

    if (activeTool === 'measure') {
      const current = pendingMeasureRef.current;
      if (!current) return;
      setPendingMeasure(prev => prev ? { ...prev, b: pt } : null);
      return;
    }

    if (activeTool === 'arrow') {
      const current = pendingArrowRef.current;
      if (!current) return;
      setPendingArrow(prev => prev ? { ...prev, b: pt } : null);
    }
  };

  const handleCanvasContextMenu = (e) => {
    if (viewportFrozen || !contextMenuEnabled || !hasAcceptedClinicalSurface()) return;
    const pt = eventToImageCoords(e);
    const hit = pt ? hitTestMarkup(pt) : null;
    if (hit) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof onViewportFocus === 'function') onViewportFocus();
      const item = applyMarkupAppearancePatch(hit.item, {
        lineWidth: hit.item.lineWidth ?? annotationStyle.lineWidth ?? 2,
        fontSize: hit.item.fontSize ?? annotationStyle.fontSize ?? overlayMeasurementFontSize,
        labelFontSize: hit.item.labelFontSize ?? overlayMeasurementFontSize,
        fontFamily: hit.item.fontFamily || annotationStyle.fontFamily,
      });
      const target = makeMarkupTarget(hit.kind, hit.item);
      setCtxMenu(null);
      setSelectedMarkup(target);
      const previousPropertySession = propertyEditSessionRef.current;
      if (previousPropertySession?.dirty) setAnnotationCommitRevision(current => current + 1);
      propertyEditSessionRef.current = {
        target,
        original: hit.item,
        dirty: false,
      };
      setPropertyPopover({
        anchor: { x: e.clientX, y: e.clientY },
        target: { ...target, item },
      });
      return;
    }
    closePropertyPopover();
    openWindowLevelContextMenu(e);
  };

  // --- ROI tool: drag to define circular ROI ---

  const handleCanvasMouseDown_ROI = (e) => {
    if (viewportFrozen || !hasAcceptedClinicalSurface() || e.button !== 0) return;
    if (activeTool !== 'roi') return;
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget?.setPointerCapture && e.pointerId !== undefined) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    }
    const pt = eventToImageCoords(e);
    if (!pt) return;
    roiStartRef.current = pt;
    setPendingRoi({ cx: pt.x, cy: pt.y, rx: 0, ry: 0 });
  };

  const makeCenteredCircleROI = (center, edge) => {
    const canvas = canvasRef.current;
    const ns = naturalSizeRef.current;
    const imgRect = getDomImageRect() || (canvas && ns ? computeImgRect(canvas, ns, zoomRef.current, panOffsetRef.current) : null);
    if (!imgRect) return null;
    const dxPx = (edge.x - center.x) * imgRect.width;
    const dyPx = (edge.y - center.y) * imgRect.height;
    const radiusPx = Math.sqrt(dxPx * dxPx + dyPx * dyPx);
    const denom = Math.min(imgRect.width, imgRect.height) || 1;
    return {
      cx: center.x,
      cy: center.y,
      // r is used for drawing so the ROI is guaranteed circular on screen.
      r: radiusPx / denom,
      // rx/ry are kept for the existing stats endpoint.
      rx: radiusPx / imgRect.width,
      ry: radiusPx / imgRect.height,
    };
  };

  const handleCanvasMouseMove_ROI = (e) => {
    if (!hasAcceptedClinicalSurface() || activeTool !== 'roi' || !roiStartRef.current) return;
    const pt = eventToImageCoords(e);
    if (!pt) return;
    const roi = makeCenteredCircleROI(roiStartRef.current, pt);
    if (roi) setPendingRoi(roi);
  };

  const currentRoiStatsOwner = () => {
    const currentSeries = seriesRef.current;
    if (!currentSeries || currentSeriesLoadGenerationRef.current == null) return null;
    return {
      seriesLoadGeneration: currentSeriesLoadGenerationRef.current,
      studyInstanceUID: currentSeries.studyInstanceUID,
      seriesInstanceUID: currentSeries.seriesInstanceUID,
    };
  };

  const settleMovedRoiStats = (requestOwner, stats) => {
    if (!sameRoiStatsRequestOwner(roiStatsSavePendingRef.current, requestOwner)) return;
    roiStatsSavePendingRef.current = null;
    setRois(prev => applyRoiStatsIfOwned({
      rois: prev,
      requestOwner,
      currentOwner: currentRoiStatsOwner(),
      stats,
    }));
  };

  const refreshMovedRoiStats = (roi) => {
    const s = seriesRef.current;
    if (!roi || !s || currentSeriesLoadGenerationRef.current == null) return;
    const finalItem = { ...roi, stats: null, statsSubjectToken: uid() };
    const requestOwner = {
      seriesLoadGeneration: currentSeriesLoadGenerationRef.current,
      studyInstanceUID: s.studyInstanceUID,
      seriesInstanceUID: s.seriesInstanceUID,
      slice: finalItem.slice,
      roiId: finalItem.id,
      roiSubjectToken: finalItem.statsSubjectToken,
    };
    roiStatsSavePendingRef.current = requestOwner;
    setRois(prev => prev.map(item => item.id === finalItem.id ? finalItem : item));
    const url = `/render/study/${s.studyInstanceUID}/series/${s.seriesInstanceUID}/roi-stats?slice=${finalItem.slice}&cx=${finalItem.cx}&cy=${finalItem.cy}&rx=${finalItem.rx}&ry=${finalItem.ry}`;
    axios.get(url, { timeout: 5000 })
      .then(res => settleMovedRoiStats(requestOwner, res.data))
      .catch(() => settleMovedRoiStats(requestOwner, { mean: '?', max: '?', min: '?', sd: '?' }));
  };

  const handleCanvasMouseUp_ROI = (e) => {
    if (!hasAcceptedClinicalSurface() || activeTool !== 'roi' || !roiStartRef.current) {
      roiStartRef.current = null;
      setPendingRoi(null);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget?.releasePointerCapture && e.pointerId !== undefined) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    const pt = eventToImageCoords(e);
    if (!pt) return;
    const start = roiStartRef.current;
    roiStartRef.current = null;
    const circle = makeCenteredCircleROI(start, pt);
    if (!circle) return;
    const roi = {
      ...finalizeRoiForSlice(circle, sliceRef.current, Date.now()),
      statsSubjectToken: uid(),
      color: '#66ff66',
      lineWidth: annotationStyle.lineWidth,
      fontFamily: annotationStyle.fontFamily,
    };
    setPendingRoi(null);
    if (roi.rx < 0.005 && roi.ry < 0.005) return; // too small, ignore
    completeToolInteraction();

    // Show the ROI immediately. Fetch HU stats asynchronously so a slow/failed
    // stats endpoint cannot make the annotation appear to be missing.
    setRois(prev => [...prev, roi]);
    if (typeof onViewportFocus === 'function') onViewportFocus();
    if (typeof onReportContextChange === 'function') {
      const roiCtx = buildReportContext('roi', { roi });
      if (roiCtx) onReportContextChange(roiCtx);
    }

    if (seriesRef.current) {
      const s = seriesRef.current;
      const requestOwner = {
        seriesLoadGeneration: currentSeriesLoadGenerationRef.current,
        studyInstanceUID: s.studyInstanceUID,
        seriesInstanceUID: s.seriesInstanceUID,
        slice: roi.slice,
        roiId: roi.id,
        roiSubjectToken: roi.statsSubjectToken,
      };
      const url = `/render/study/${s.studyInstanceUID}/series/${s.seriesInstanceUID}/roi-stats?slice=${roi.slice}&cx=${roi.cx}&cy=${roi.cy}&rx=${roi.rx}&ry=${roi.ry}`;
      roiStatsSavePendingRef.current = requestOwner;
      axios.get(url, { timeout: 5000 })
        .then(res => settleMovedRoiStats(requestOwner, res.data))
        .catch(() => settleMovedRoiStats(requestOwner, { mean: '?', max: '?', min: '?', sd: '?' }));
    }
  };

  const handleContextualAnnotationHover = (e) => {
    if (!hasAcceptedClinicalSurface()) {
      setHoveredAnnotationId(null);
      return;
    }
    if (annotationDragRef.current?.id) return;
    const pt = eventToImageCoords(e);
    const hit = pt ? hitTestMarkup(pt) : null;
    const hoverKey = hit ? `${hit.kind}:${hit.item.id}:${hit.part || 'body'}` : null;
    setHoveredAnnotationId(current => current === hoverKey ? current : hoverKey);
  };

  const beginContextualAnnotationDrag = (e) => {
    if (viewportFrozen || !hasAcceptedClinicalSurface() || e.button !== 0) return false;
    if (isAnnotationTool && activeTool !== 'select') return false;
    const pt = eventToImageCoords(e);
    if (!pt) return false;

    const handleHit = selectedAnnotationId ? hitTestAnnotationHandle(pt, selectedAnnotationId) : null;
    const hit = handleHit
      ? { kind: 'annotation', item: handleHit.ann }
      : hitTestMarkup(pt);
    if (!hit) {
      if (selectedMarkup) setSelectedMarkup(null);
      return false;
    }

    if (e.pointerId === undefined || !e.currentTarget?.setPointerCapture) return false;
    let captureEstablished = false;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
      captureEstablished = typeof e.currentTarget.hasPointerCapture !== 'function'
        || e.currentTarget.hasPointerCapture(e.pointerId);
    } catch (_) {
      captureEstablished = false;
    }
    if (!captureEstablished) return false;
    e.preventDefault();
    e.stopPropagation();
    if (typeof onViewportFocus === 'function') onViewportFocus();
    closePropertyPopover();
    const prepared = prepareViewportMarkupForDrag(hit.kind, hit.item, hit.kind === 'roi' ? uid() : null);
    if (hit.kind === 'roi') {
      roiStatsSavePendingRef.current = null;
      setRois(prev => prev.map(item => item.id === hit.item.id && item.slice === hit.item.slice ? prepared.working : item));
    }
    const target = makeMarkupTarget(hit.kind, hit.item);
    setSelectedMarkup(target);
    setHoveredAnnotationId(`${hit.kind}:${hit.item.id}:${hit.part || 'body'}`);
    annotationDragRef.current = {
      ...target,
      startPt: pt,
      lastPt: pt,
      moved: false,
      original: prepared.working,
      rollbackOriginal: prepared.rollback,
      captureTarget: e.currentTarget,
      pointerId: e.pointerId,
      measurementEndpoint: hit.kind === 'measurement' && ['a', 'b'].includes(hit.part) ? hit.part : null,
      roiResizePart: hit.kind === 'roi' && hit.part === 'radius' ? hit.part : null,
      ...(handleHit ? { handleKey: handleHit.handle.key } : {}),
    };
    suppressNextCanvasClickRef.current = true;
    return true;
  };

  const handleAnnotationPointerDown = (e) => {
    if (viewportFrozen || !hasAcceptedClinicalSurface() || e.button !== 0) return;
    if (!['pen', 'rect', 'ellipse'].includes(activeTool)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget?.setPointerCapture && e.pointerId !== undefined) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    }
    const pt = eventToImageCoords(e);
    if (!pt) return;
    if (activeTool === 'pen') setPendingFreehand([pt]);
    if (activeTool === 'rect') setPendingRect({ a: pt, b: pt });
    if (activeTool === 'ellipse') setPendingEllipse({ a: pt, b: pt });
  };

  const handleAnnotationPointerMove = (e) => {
    if (viewportFrozen || !hasAcceptedClinicalSurface()) return;
    const pt = eventToImageCoords(e);
    if (!pt) return;
    if (annotationDragRef.current?.id) {
      handleCanvasMouseMove(e);
      return;
    }
    if (activeTool === 'pen' && pendingFreehand) {
      setPendingFreehand(prev => {
        if (!prev || !prev.length) return [pt];
        const last = prev[prev.length - 1];
        if (Math.hypot(pt.x - last.x, pt.y - last.y) < 0.002) return prev;
        return [...prev, pt];
      });
    }
    if (activeTool === 'rect' && pendingRect) setPendingRect(prev => prev ? { ...prev, b: pt } : null);
    if (activeTool === 'ellipse' && pendingEllipse) setPendingEllipse(prev => prev ? { ...prev, b: pt } : null);
  };

  const restoreCancelledMarkupDrag = useCallback((drag) => {
    if (!drag?.rollbackOriginal) return;
    const currentSeries = seriesRef.current;
    if (currentSeries?.studyInstanceUID !== drag.studyInstanceUID
      || currentSeries?.seriesInstanceUID !== drag.seriesInstanceUID) return;
    const restore = item => item.id === drag.id && item.slice === drag.slice ? drag.rollbackOriginal : item;
    if (drag.kind === 'annotation') setAnnotations(prev => prev.map(restore));
    if (drag.kind === 'measurement') setMeasurements(prev => prev.map(restore));
    if (drag.kind === 'roi') setRois(prev => prev.map(restore));
  }, []);

  const cancelAnnotationDrag = useCallback(({ restore = true, updateUi = true } = {}) => {
    const drag = annotationDragRef.current;
    if (!drag?.id) return false;
    annotationDragRef.current = null;
    if (drag.captureTarget?.releasePointerCapture && drag.pointerId !== undefined) {
      try {
        if (typeof drag.captureTarget.hasPointerCapture !== 'function'
          || drag.captureTarget.hasPointerCapture(drag.pointerId)) {
          drag.captureTarget.releasePointerCapture(drag.pointerId);
        }
      } catch (_) {}
    }
    if (restore) restoreCancelledMarkupDrag(drag);
    suppressNextCanvasClickRef.current = false;
    if (updateUi) setHoveredAnnotationId(null);
    return true;
  }, [restoreCancelledMarkupDrag]);

  const finishAnnotationDrag = (e) => {
    const activeDrag = annotationDragRef.current;
    if (activeDrag?.id && activeDrag.pointerId !== e.pointerId) return;
    if (viewportFrozen || !hasAcceptedClinicalSurface() || (activeDrag?.id && !isMarkupTargetCurrent(activeDrag))) {
      cancelAnnotationDrag();
      setPendingFreehand(null);
      setPendingRect(null);
      setPendingEllipse(null);
      return;
    }
    if (e.currentTarget?.releasePointerCapture && e.pointerId !== undefined) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    const completedContextualDrag = annotationDragRef.current;
    annotationDragRef.current = null;
    if (completedContextualDrag?.id) {
      if (completedContextualDrag.kind === 'roi' && !completedContextualDrag.moved) {
        restoreCancelledMarkupDrag(completedContextualDrag);
      } else if (completedContextualDrag.moved) {
        setAnnotationCommitRevision(current => current + 1);
        if (completedContextualDrag.kind === 'roi' && completedContextualDrag.lastPt) {
          const finalItem = finalizeContextualRoiGeometry(completedContextualDrag, completedContextualDrag.lastPt);
          refreshMovedRoiStats(finalItem);
        }
      }
    }
    if (activeTool === 'pen' && pendingFreehand?.length > 1) {
      setAnnotations(prev => [...prev, { id: uid(), type: 'freehand', slice: sliceRef.current, points: pendingFreehand, ...annotationStyleForNew() }]);
      setPendingFreehand(null);
      completeToolInteraction();
    }
    if (activeTool === 'rect' && pendingRect) {
      if (Math.abs(pendingRect.a.x - pendingRect.b.x) > 0.004 && Math.abs(pendingRect.a.y - pendingRect.b.y) > 0.004) {
        setAnnotations(prev => [...prev, { id: uid(), type: 'rect', slice: sliceRef.current, points: [pendingRect.a, pendingRect.b], ...annotationStyleForNew() }]);
        completeToolInteraction();
      }
      setPendingRect(null);
    }
    if (activeTool === 'ellipse' && pendingEllipse) {
      if (Math.abs(pendingEllipse.a.x - pendingEllipse.b.x) > 0.004 && Math.abs(pendingEllipse.a.y - pendingEllipse.b.y) > 0.004) {
        setAnnotations(prev => [...prev, { id: uid(), type: 'ellipse', slice: sliceRef.current, points: [pendingEllipse.a, pendingEllipse.b], ...annotationStyleForNew() }]);
        completeToolInteraction();
      }
      setPendingEllipse(null);
    }
    if (suppressNextCanvasClickRef.current) {
      window.setTimeout(() => { suppressNextCanvasClickRef.current = false; }, 0);
    }
  };

  useEffect(() => {
    const handleDeleteKey = (e) => {
      if (!isActiveViewport || isViewportEditableTarget(e.target)) return;
      if (viewportFrozen && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedMarkup) {
        e.preventDefault();
        deleteMarkupTarget(selectedMarkup);
      }
      if (e.key === 'Escape') {
        cancelAnnotationDrag();
        cancelPropertyPopover();
        setSelectedMarkup(null);
        setPendingMeasure(null); setPendingRoi(null);
        setPendingArrow(null); setPendingFreehand(null); setPendingRect(null); setPendingEllipse(null); setPendingAngle([]); setPendingCobb([]); setTextPopover(null);
        if (escapeReturnsToScroll) resetToolToScroll();
      }
    };
    window.addEventListener('keydown', handleDeleteKey);
    return () => window.removeEventListener('keydown', handleDeleteKey);
  }, [cancelAnnotationDrag, cancelPropertyPopover, deleteMarkupTarget, escapeReturnsToScroll, isActiveViewport, resetToolToScroll, selectedMarkup, viewportFrozen]);

  useEffect(() => {
    cancelAnnotationDrag();
    cancelPropertyPopover();
    setSelectedMarkup(null);
  }, [cancelAnnotationDrag, cancelPropertyPopover, series?.seriesInstanceUID, series?.studyInstanceUID, slice, viewportFrozen]);

  useEffect(() => {
    const handleWindowBlur = () => cancelAnnotationDrag();
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      cancelAnnotationDrag({ restore: false, updateUi: false });
    };
  }, [cancelAnnotationDrag]);


  const isEmpty = !series;
  const borderColor = isOver && canDrop ? '#00bcd4' : isActiveViewport ? '#6eefff' : isEmpty ? '#1e1e1e' : '#2a2a2a';

  const overlayCanvas = canvasRef.current;
  const overlayImgRect = getDomImageRect() || (overlayCanvas && naturalSize ? computeImgRect(overlayCanvas, naturalSize, zoom, panOffset) : null);
  const overlayW = canvasCssSize.width || boxRef.current?.getBoundingClientRect().width || overlayCanvas?.clientWidth || 0;
  const overlayH = canvasCssSize.height || boxRef.current?.getBoundingClientRect().height || overlayCanvas?.clientHeight || 0;

  const overlayPoint = (pt) => overlayImgRect ? ({
    x: overlayImgRect.left + pt.x * overlayImgRect.width,
    y: overlayImgRect.top + pt.y * overlayImgRect.height,
  }) : { x: 0, y: 0 };

  const formatMeasureLabel = useCallback((m) => {
    if (!m || !m.a || !m.b) return '';
    const dx = m.b.x - m.a.x;
    const dy = m.b.y - m.a.y;
    let label;
    if (pixelSpacing && naturalSize) {
      const pxDx = dx * naturalSize.width;
      const pxDy = dy * naturalSize.height;
      const mm = Math.sqrt((pxDx * pixelSpacing[1]) ** 2 + (pxDy * pixelSpacing[0]) ** 2);
      label = `${mm.toFixed(1)} mm`;
    } else if (naturalSize) {
      const pxDx = dx * naturalSize.width;
      const pxDy = dy * naturalSize.height;
      label = `${Math.sqrt(pxDx * pxDx + pxDy * pxDy).toFixed(0)} px`;
    }
    return m.label ? `${m.label}${label ? ` (${label})` : ''}` : (label || '');
  }, [naturalSize, pixelSpacing]);


  const buildReportContext = useCallback((kindOverride = null, subjectOverride = {}) => {
    if (!seriesRef.current || !acceptedVisibleSurfaceRef.current || !Number.isFinite(sliceRef.current)) return null;
    const currentSlice = sliceRef.current;
    const subjects = selectReportContextSubjects({
      measurements: measurementsRef.current,
      rois: roisRef.current,
      slice: currentSlice,
    });
    const latestMeasurement = subjectOverride.measurement || subjects.measurement || null;
    const latestRoi = subjectOverride.roi || subjects.roi || null;
    const kind = chooseReportContextKind({ kindOverride, measurement: latestMeasurement, roi: latestRoi });
    const ctx = {
      kind,
      viewportIndex: index,
      studyInstanceUID: seriesRef.current.studyInstanceUID,
      series: seriesRef.current,
      slice: currentSlice,
      maxSlice,
      windowLevel: wlRef.current,
      pixelSpacing,
      naturalSize,
    };
    if (latestMeasurement) {
      ctx.measurement = {
        ...latestMeasurement,
        lengthLabel: formatMeasureLabel(latestMeasurement),
        slice: latestMeasurement.slice ?? currentSlice,
        pixelSpacing,
        naturalSize,
      };
    }
    if (latestRoi) {
      ctx.roi = { ...latestRoi, slice: latestRoi.slice ?? currentSlice, pixelSpacing, naturalSize };
    }
    if (kind === 'measurement' && ctx.measurement) ctx.slice = ctx.measurement.slice;
    else if (kind === 'roi' && ctx.roi) ctx.slice = ctx.roi.slice;
    return ctx;
  }, [formatMeasureLabel, index, maxSlice, naturalSize, pixelSpacing]);

  useEffect(() => {
    if (!onReportContextChange || annotationDragRef.current?.id || roiStatsSavePendingRef.current) return;
    const ctx = buildReportContext();
    onReportContextChange(ctx || { viewportIndex: index, cleared: true });
  }, [annotationCommitRevision, buildReportContext, index, measurements, rois, series, slice, maxSlice, onReportContextChange]);

  const roiOverlayCircle = (roi) => {
    if (!overlayImgRect || !roi) return null;
    const cx = overlayImgRect.left + roi.cx * overlayImgRect.width;
    const cy = overlayImgRect.top + roi.cy * overlayImgRect.height;
    const rxPx = (roi.rx || 0) * overlayImgRect.width;
    const ryPx = (roi.ry || 0) * overlayImgRect.height;
    const r = roi.r != null
      ? roi.r * Math.min(overlayImgRect.width, overlayImgRect.height)
      : Math.max(Math.abs(rxPx), Math.abs(ryPx));
    return { cx, cy, r: Math.abs(r) };
  };

  const fmtHU = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : '?';
  };

  const roiStatsLines = (stats) => {
    if (!stats) return [];
    return [
      `μ ${fmtHU(stats.mean)} HU`,
      `max ${fmtHU(stats.max)} HU`,
    ];
  };

  const applyManualWindowLevel = useCallback(() => {
    if (viewportFrozen) return;
    if (!acceptedVisibleSurfaceRef.current || !Number.isFinite(sliceRef.current)) return;
    const ww = Math.max(0, Math.min(9999, Math.round(Number(wlDraft.ww))));
    const wc = Math.max(-9999, Math.min(9999, Math.round(Number(wlDraft.wc))));
    const wl = normalizeViewportWindowLevel({ ww, wc, source: 'manual' });
    windowLevelSourceRef.current = 'manual';
    setWindowLevel(wl);
    wlRef.current = wl;
    setWlDraft(formatWindowLevelDraft(wl));
    notifyWindowLevelChange(wl);
    loadSlice(sliceRef.current, wl);
  }, [loadSlice, notifyWindowLevelChange, viewportFrozen, wlDraft.wc, wlDraft.ww]);

  const handleMarkKeyObject = useCallback(() => {
    if (!seriesRef.current || !acceptedVisibleSurfaceRef.current || !Number.isFinite(sliceRef.current) || typeof onMarkKeyObject !== 'function') return;
    const annotation = annotationsRef.current.find(a => a.id === selectedAnnotationId) || null;
    onMarkKeyObject({ series: seriesRef.current, slice: sliceRef.current, windowLevel: wlRef.current, annotation, viewportIndex: index });
  }, [index, onMarkKeyObject, selectedAnnotationId]);

  const hasAcceptedClinicalSlice = Boolean(acceptedVisibleSurfaceRef.current) && Number.isFinite(slice);
  const currentKeyObjectMarked = hasAcceptedClinicalSlice && keyObjects.some(mark => mark.seriesInstanceUID === series?.seriesInstanceUID && Number(mark.slice) === slice);
  const currentSliceGeometry = seriesGeometry?.[slice] || null;
  const petThicknessEligible = hasAcceptedClinicalSlice && isPetSeries(series) && maxSlice > 0 && Boolean(nativeSliceThicknessMm(series));
  const petNativeSliceThickness = nativeSliceThicknessMm(series) || 'unknown';

  const visibleAnnotations = visibleAnnotationsForSlice(annotations, slice);
  const visibleMeasurements = visibleMeasurementsForSlice(measurements, slice, pendingMeasure);
  const visibleRois = visibleRoisForSlice(rois, slice, pendingRoi);
  const toolRailRenderRevision = JSON.stringify({
    index,
    docked: toolRailDocked,
    position: toolRailPosition ? [toolRailPosition.x, toolRailPosition.y] : null,
    dragging: toolRailDragging,
    safeInsets: [floatingToolRailSafeInsets.top, floatingToolRailSafeInsets.bottom],
    safeBand: floatingToolRailHasSafeBand,
    dockPosition: toolLayoutPrefs?.dock?.position || 'left',
    expandedSections: expandedToolRailSections,
    activeTool,
    dockTools: Array.from(dockToolIds).sort(),
    shortcuts: profiledToolShortcuts,
    counts: [rois.length, measurements.length, annotations.length],
    selectedMarkup: selectedMarkup ? [
      selectedMarkup.kind,
      selectedMarkup.id,
      selectedMarkup.viewportIndex,
      selectedMarkup.studyInstanceUID,
      selectedMarkup.seriesInstanceUID,
      selectedMarkup.slice,
      selectedMarkup.seriesLoadGeneration,
      selectedMarkup.acceptedGeneration,
    ] : null,
    selectedMarkupAcceptedSurfaceGeneration: selectedMarkup
      ? (acceptedVisibleSurfaceRef.current?.generation ?? null)
      : null,
    selectedAnnotationId,
    annotationStyle,
    viewportFrozen,
    presentationSaveStatus,
    presentationSaveAlert,
    currentKeyObjectMarked,
    hasAcceptedClinicalSlice,
    hasSeries: Boolean(series),
  });
  const toolRailExternalDependencies = [onAnnotationAppearanceChange, onMarkKeyObject];
  // Triangulation crosshair/source-marker projection moved into the memoized
  // TriangulationCrosshairLayer / TriangulationSourceMarkerLayer components,
  // which subscribe to the imperative drag channel (anchorPoint fallback:
  // triangulationPoint prop) so drag frames never re-render this component.
  // Reference-line projection likewise lives in ReferenceLineLayer so follower
  // geometry notifications cannot re-render the full diagnostic viewport.
  const pendingShapeAnnotations = [
    pendingFreehand?.length > 1 ? { id: 'pending-freehand', type: 'freehand', points: pendingFreehand, pending: true, ...annotationStyle } : null,
    pendingRect ? { id: 'pending-rect', type: 'rect', points: [pendingRect.a, pendingRect.b], pending: true, ...annotationStyle } : null,
    pendingEllipse ? { id: 'pending-ellipse', type: 'ellipse', points: [pendingEllipse.a, pendingEllipse.b], pending: true, ...annotationStyle } : null,
    pendingAngle.length ? { id: 'pending-angle', type: 'angle', points: pendingAngle, pending: true, ...annotationStyle } : null,
    pendingCobb.length ? { id: 'pending-cobb', type: 'cobb', points: pendingCobb, pending: true, ...annotationStyle } : null,
  ].filter(Boolean);
  const toolBadge = activeTool === 'measure' ? '⊢ MEASURE'
    : activeTool === 'roi' ? 'ROI'
      : activeTool === 'select' ? 'SELECT'
        : activeTool === 'arrow' ? '↗ ARROW'
          : activeTool === 'text' ? 'TXT'
            : activeTool === 'pen' ? 'PEN'
              : activeTool === 'rect' ? 'RECT'
                : activeTool === 'ellipse' ? 'ELLIPSE'
                  : activeTool === 'angle' ? 'ANGLE'
                    : activeTool === 'cobb' ? 'COBB'
                      : activeTool === 'level' ? 'LEVEL'
                        : activeTool === 'wl' ? 'W/L' : 'SCROLL';
  const toolColor = activeTool === 'measure' ? '#ffeb3b'
    : activeTool === 'roi' ? '#4caf50'
      : activeTool === 'select' ? '#00e5ff'
        : activeTool === 'arrow' ? '#ff9800'
          : activeTool === 'text' ? '#e040fb'
            : activeTool === 'pen' ? '#00e5ff'
              : activeTool === 'rect' ? '#ffeb3b'
                : activeTool === 'ellipse' ? '#66ff66'
                  : activeTool === 'angle' ? '#ffeb3b'
                    : activeTool === 'cobb' ? '#66ff66'
                      : activeTool === 'level' ? '#e040fb'
                        : activeTool === 'wl' ? '#00bcd4' : '#666';


  const overlayMeasurementFontSize = measurementLabelFontSize(effectiveOverlayConfig);
  const overlayValue = (field) => {
    if (!series) return '';
    switch (field) {
      case 'slotLabel': return slotLabel;
      case 'patientName': return studyInfo?.patientName;
      case 'patientId': return studyInfo?.patientId ? `MRN: ${studyInfo.patientId}` : '';
      case 'accessionNumber': return studyInfo?.accessionNumber ? `ACC: ${studyInfo.accessionNumber}` : '';
      case 'studyDescription': return studyInfo?.studyDescription;
      case 'studyDate': return studyInfo?.studyDate;
      case 'modality': return series.modality;
      case 'seriesDescription': return series.seriesDescription;
      case 'seriesNumber': return series.seriesNumber ? `S:${series.seriesNumber}` : '';
      case 'imageNumber': return hasAcceptedClinicalSlice ? `${slice + 1} / ${maxSlice + 1}` : '';
      case 'windowLevel': return describeWindowLevelOverlay(windowLevel);
      case 'pixelSpacing': return pixelSpacing ? `${pixelSpacing[0].toFixed(3)}×${pixelSpacing[1].toFixed(3)} mm/px` : '';
      case 'sliceThickness': return series.sliceThickness ? `Thick: ${series.sliceThickness} mm` : '';
      case 'sliceLocation': return series.sliceLocation ? `Loc: ${series.sliceLocation}` : '';
      case 'acquisitionDateTime': return series.acquisitionDateTime || series.acquisitionDate || series.acquisitionTime || '';
      case 'lateralityView': return [series.laterality, series.viewPosition].filter(Boolean).join(' ');
      default: return '';
    }
  };
  const overlayStyleByPosition = {
    topLeft: { top: 4, left: 6, textAlign: 'left' },
    topCenter: { top: 4, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' },
    topRight: { top: 4, right: 6, textAlign: 'right' },
    bottomLeft: { bottom: 28, left: 6, textAlign: 'left' },
    bottomCenter: { bottom: 28, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' },
    // Keep the bottom-right PACS text above the active-tool HUD badge and off the edge.
    bottomRight: { bottom: 48, right: 10, textAlign: 'right' },
  };
  const renderWindowLevelOverlayEditor = (position, idx) => (
    <Box
      key={`${position}-windowLevel-${idx}`}
      data-kairos-marker={VIEWPORT_WINDOW_LEVEL_INLINE_EDIT_MARKER}
      data-kairos-wl-default-marker={VIEWPORT_WL_EFFECTIVE_DEFAULT_MARKER}
      data-window-level-source={windowLevel.source || 'auto'}
      component="span"
      aria-label={`Effective window level for viewport ${index + 1}: ${describeWindowLevelOverlay(windowLevel)}`}
      title="Edit the effective WW/WC used for this viewport"
      sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: overlayStyleByPosition[position]?.textAlign === 'right' ? 'flex-end' : 'flex-start', gap: 0.35, color: idx === 0 ? '#00bcd4' : (effectiveOverlayConfig.color || '#b7dce5'), fontSize: effectiveOverlayConfig.fontSize, fontFamily: 'monospace', textShadow: '0 0 4px #000', pointerEvents: 'auto' }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {[['ww', 'WW'], ['wc', 'WC']].map(([key, label]) => (
        <Box key={key} component="label" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <Box component="span" sx={{ opacity: 0.92 }}>{label}</Box>
          <Box
            component="input"
            aria-label={`${label} effective value for viewport ${index + 1}`}
            data-kairos-wl-draft-key={key}
            value={wlDraft[key]}
            onChange={(e) => setWlDraft(prev => ({ ...prev, [key]: e.target.value }))}
            onBlur={applyManualWindowLevel}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); applyManualWindowLevel(); e.currentTarget.blur(); } }}
            onFocus={(e) => e.currentTarget.select()}
            sx={{ width: 42, height: 16, boxSizing: 'border-box', bgcolor: 'rgba(1,9,13,0.72)', color: '#d8fbff', border: '1px solid rgba(110,239,255,0.34)', borderRadius: '3px', fontSize: 9, fontFamily: 'monospace', px: 0.35, outline: 'none' }}
          />
        </Box>
      ))}
    </Box>
  );

  const renderOverlayBlock = (position) => {
    if (!effectiveOverlayConfig.enabled) return null;
    const fields = (effectiveOverlayConfig.positions?.[position] || [])
      .filter((field) => overlayFieldVisible(effectiveOverlayConfig, field))
      .map((field) => ({ field, value: overlayValue(field) }))
      .filter(({ field, value }) => field === 'windowLevel' || Boolean(value));
    if (!fields.length) return null;
    return (
      <Box key={position} sx={{ position: 'absolute', zIndex: 10, pointerEvents: 'none', maxWidth: 'calc(100% - 20px)', ...overlayStyleByPosition[position] }}>
        {fields.map(({ field, value }, idx) => field === 'windowLevel' ? renderWindowLevelOverlayEditor(position, idx) : (
          <Typography key={`${position}-${field}-${idx}`} data-viewport-live-slice-label={field === 'imageNumber' ? 'true' : undefined} sx={{ color: idx === 0 ? '#00bcd4' : (effectiveOverlayConfig.color || '#b7dce5'), fontSize: effectiveOverlayConfig.fontSize, lineHeight: effectiveOverlayConfig.density === 'comfortable' ? 1.6 : 1.35, textShadow: '0 0 4px #000', fontFamily: 'monospace', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value}
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Box
      ref={bindViewportRoot}
      data-testid={`diagnostic-viewport-drop-target-${index}`}
      data-viewport-index={index}
      data-series-instance-uid={series?.seriesInstanceUID || ''}
      data-viewport-editable-key-guard={VIEWPORT_EDITABLE_KEY_GUARD_MARKER}
      data-viewport-double-click-maximize={VIEWPORT_DOUBLE_CLICK_MAXIMIZE_MARKER}
      data-viewport-maximized={isViewportMaximized ? 'true' : 'false'}
      data-viewport-layout-mode={viewportLayoutMode}
      data-viewport-scroll-idle-preload={VIEWPORT_SCROLL_IDLE_PRELOAD_MARKER}
      data-viewport-wheel-distance-scroll={VIEWPORT_WHEEL_DISTANCE_SCROLL_MARKER}
      data-viewport-active-series-browser-cache={VIEWPORT_ACTIVE_SERIES_BROWSER_CACHE_MARKER}
      data-viewport-browser-warmup-shared-budget={VIEWPORT_BROWSER_WARMUP_SHARED_BUDGET_MARKER}
      data-viewport-geometry-cine-suppression={VIEWPORT_GEOMETRY_CINE_SUPPRESSION_MARKER}

      data-viewport-latest-wins-request={VIEWPORT_LATEST_WINS_REQUEST_MARKER}
      data-viewport-follower-frame-coordinator={VIEWPORT_FOLLOWER_FRAME_COORDINATOR_MARKER}
      data-viewport-direction-reversal-cancel={VIEWPORT_DIRECTION_REVERSAL_CANCEL_MARKER}
      data-viewport-window-level-drag={VIEWPORT_WINDOW_LEVEL_DRAG_MARKER}
      data-viewport-slice-current={sliceRef.current}
      data-viewport-slice-max={maxSlice}
      sx={{
        width: '100%', height: '100%', minWidth: 0, minHeight: 0,
        border: `1px solid ${borderColor}`,
        bgcolor: '#080808',
        display: 'flex', flexDirection: 'column',
        position: 'relative', overflow: 'hidden',
        transition: 'border-color 0.1s',
        userSelect: 'none',
        outline: isOver && canDrop ? '1px solid #00bcd4' : 'none',
      }}
      onClick={() => propertyPopover && closePropertyPopover()}
    >
      {isEmpty ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ color: '#222', fontSize: 32 }}>⊕</Typography>
          <Typography sx={{ color: '#2a2a2a', fontSize: 10, letterSpacing: 3 }}>DROP SERIES</Typography>
        </Box>
      ) : (
        <>
          {/* Active viewport sentinel — keyboard shortcuts/report context apply here without covering diagnostic pixels */}
          {isActiveViewport && (
            <Box
              data-active-viewport-sentinel="KAIROS_ACTIVE_VIEWPORT_SENTINEL_NUMBERED_TARGET"
              aria-label={`Active viewport ${index + 1}; keyboard shortcuts and report insertion target`}
              aria-live="polite"
              data-active-viewport-sentinel-placement={VIEWPORT_ACTIVE_SENTINEL_TOP_PLACEMENT_MARKER}
              sx={{ position: 'absolute', top: { xs: 32, xl: 8 }, left: '33%', transform: 'translateX(-50%)', zIndex: 30, pointerEvents: 'none', maxWidth: 'min(220px, 40%)' }}
            >
              <Typography
                sx={{
                  color: '#001418',
                  bgcolor: 'rgba(110,239,255,0.86)',
                  border: '1px solid rgba(255,255,255,0.5)',
                  borderRadius: '999px',
                  px: 0.7,
                  py: 0.18,
                  fontSize: 8,
                  fontWeight: 900,
                  letterSpacing: '0.14em',
                  fontFamily: 'monospace',
                  boxShadow: '0 0 12px rgba(110,239,255,0.28)',
                }}
              >
                {`VP ${index + 1} · KEYS/REPORT`}
              </Typography>
            </Box>
          )}

          {/* Comparison identity badge — makes prior compare tabs unambiguous even when anonymized dates match */}
          {comparisonContext?.label && (
            <Box sx={{ position: 'absolute', top: 6, left: 6, zIndex: 35, pointerEvents: 'none', maxWidth: 'calc(100% - 12px)' }}>
              <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.2, px: 0.75, py: 0.45, border: '1px solid #ffeb3b', bgcolor: 'rgba(0,0,0,0.72)', boxShadow: '0 0 8px rgba(255,235,59,0.24)' }}>
                <Typography sx={{ color: '#fff59d', fontSize: 11, fontWeight: 800, letterSpacing: 1, fontFamily: 'monospace', lineHeight: 1.1 }}>
                  {comparisonContext.label}
                </Typography>
                {comparisonContext.identity && (
                  <Typography title={comparisonContext.identity} sx={{ color: '#d8d0a0', fontSize: 9, fontFamily: 'monospace', lineHeight: 1.15, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {comparisonContext.identity}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Slot label overlay (top-center) — shown when a hanging protocol is active */}
          {slotLabel && !effectiveOverlayConfig.enabled && (
            <Box sx={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)', zIndex: 10, pointerEvents: 'none' }}>
              <Typography sx={{ color: '#444', fontSize: 8, letterSpacing: 2, textShadow: '0 0 4px #000' }}>
                {slotLabel}
              </Typography>
            </Box>
          )}

          {/* Spectral series (SBI) overlay - full screen replacement */}
          {(() => {
            const seriesDesc = (series?.seriesDescription || '').toLowerCase();
            const isSpectral = seriesDesc.includes('sbi(') || seriesDesc.startsWith('sbi') || /\bsbi\b/.test(seriesDesc);
            if (isSpectral) {
              return (
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 5, bgcolor: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#888', fontSize: 11, textAlign: 'center', p: 2 }}>
                    Spectral Series — not supported for display
                  </Typography>
                </Box>
              );
            }
            return null;
          })()}

          {/* Configurable PACS-style viewport text overlay */}
          {['topLeft', 'topCenter', 'topRight', 'bottomLeft', 'bottomCenter', 'bottomRight'].map(renderOverlayBlock)}

          {/* Per-viewport tool rail — default compact with a clear dock/undock state. */}
          <ViewportToolRailRenderBoundary
            revision={toolRailRenderRevision}
            externalDependencies={toolRailExternalDependencies}
          >
          <Box
            ref={toolRailRef}
            data-tool-layout-profile="viewer.tool_layout"
            data-viewport-tool-rail-layout="compact-grid"
            data-tool-rail-dragging={toolRailDragging ? 'true' : 'false'}
            data-tool-rail-safe-band={floatingToolRailHasSafeBand ? 'available' : 'unavailable'}
            sx={{
              position: 'absolute',
              top: toolRailDocked ? VIEWPORT_DOCKED_TOOL_RAIL_TOP : (toolRailPosition?.y ?? floatingToolRailSafeInsets.top),
              left: toolRailDocked ? 0 : (toolRailPosition ? toolRailPosition.x : (toolLayoutPrefs?.dock?.position === 'right' ? 'auto' : VIEWPORT_FLOATING_TOOL_RAIL_GUTTER)),
              right: toolRailDocked || toolRailPosition || toolLayoutPrefs?.dock?.position !== 'right' ? 'auto' : VIEWPORT_FLOATING_TOOL_RAIL_GUTTER,
              transform: 'none',
              zIndex: 31,
              display: !toolRailDocked && !floatingToolRailHasSafeBand ? 'none' : 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 0.45,
              background: 'linear-gradient(180deg, rgba(8,18,28,0.92), rgba(2,8,14,0.88))',
              maxHeight: toolRailDocked ? `calc(100% - ${VIEWPORT_DOCKED_TOOL_RAIL_TOP + VIEWPORT_DOCKED_TOOL_RAIL_BOTTOM_GUTTER}px)` : `calc(100% - ${floatingToolRailSafeInsets.top + floatingToolRailSafeInsets.bottom}px)`,
              overflowY: 'auto',
              border: '1px solid rgba(0,229,255,0.34)',
              boxShadow: '0 14px 34px rgba(0,0,0,0.48), inset 0 1px 0 rgba(255,255,255,0.08)',
              borderRadius: toolRailDocked ? '0 8px 8px 0' : '8px',
              p: 0.35,
            }}
            data-testid="viewport-tool-rail sacred-pixels-tool-rail"
            data-semantic-tool-rail-labels="KAIROS_SEMANTIC_TOOL_RAIL_ACTION_LABELS"
            data-profiled-tool-rail-shortcuts={TOOL_RAIL_PROFILED_SHORTCUT_MARKER}
            data-kairos-docked-default={VIEWPORT_TOOL_RAIL_DOCKED_DEFAULT_MARKER}
            data-kairos-docked-tool-rail-placement={VIEWPORT_TOOL_RAIL_BELOW_ATTRIBUTES_MARKER}
            data-viewport-tool-rail-docked={toolRailDocked ? 'true' : 'false'}
          >
            {toolRailDocked ? (
              <Tooltip title="Undock viewport tools" placement="right">
                <ButtonBase
                  type="button"
                  aria-label="Undock viewport tools"
                  aria-pressed={toolRailDocked}
                  data-kairos-marker={VIEWPORT_TOOL_RAIL_DOCK_TOGGLE_MARKER}
                  onClick={() => { setToolRailPosition(null); setToolRailDocked(false); }}
                  sx={{ width: 26, minHeight: 64, borderRadius: '0 6px 6px 0', border: '1px solid rgba(110,239,255,0.32)', color: '#6eefff', bgcolor: 'rgba(0,229,255,0.16)', fontSize: 8, fontWeight: 900, fontFamily: 'monospace', letterSpacing: 0.8, writingMode: 'vertical-rl', textOrientation: 'mixed', '&:focus-visible': { outline: '2px solid rgba(110,239,255,0.85)', outlineOffset: 1 } }}
                >
                  TOOLS
                </ButtonBase>
              </Tooltip>
            ) : (
              <Box sx={{ width: 80, display: 'flex', alignItems: 'stretch', gap: '3px' }}>
                <ButtonBase
                  type="button"
                  aria-label="Move undocked viewport tools"
                  aria-describedby={`viewport-tool-rail-move-help-${index}`}
                  onPointerDown={beginToolRailDrag}
                  onPointerMove={moveToolRailDrag}
                  onPointerUp={endToolRailDrag}
                  onPointerCancel={endToolRailDrag}
                  onLostPointerCapture={endToolRailDrag}
                  onKeyDown={moveToolRailWithKeyboard}
                  sx={{ flex: 1, minHeight: 22, borderRadius: '5px', border: '1px solid rgba(110,239,255,0.38)', color: '#b9f7ff', bgcolor: toolRailDragging ? 'rgba(0,229,255,0.20)' : 'rgba(0,229,255,0.10)', fontSize: 8, fontWeight: 900, fontFamily: 'monospace', letterSpacing: 0.7, cursor: toolRailDragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none', '&:focus-visible': { outline: '2px solid rgba(110,239,255,0.85)', outlineOffset: 1 } }}
                >
                  ⠿ MOVE
                </ButtonBase>
                <Typography id={`viewport-tool-rail-move-help-${index}`} sx={{ position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}>Drag to move. Arrow keys move eight pixels; hold Shift for twenty-four.</Typography>
                <Tooltip title="Dock viewport tools" placement="right">
                  <ButtonBase
                    type="button"
                    aria-label="Dock viewport tools"
                    aria-pressed={false}
                    data-kairos-marker={VIEWPORT_TOOL_RAIL_DOCK_TOGGLE_MARKER}
                    onClick={() => setToolRailDocked(true)}
                    sx={{ minWidth: 30, minHeight: 22, borderRadius: '5px', border: '1px solid rgba(110,239,255,0.32)', color: '#6eefff', bgcolor: 'rgba(0,229,255,0.08)', fontSize: 7.5, fontWeight: 900, fontFamily: 'monospace', '&:focus-visible': { outline: '2px solid rgba(110,239,255,0.85)', outlineOffset: 1 } }}
                  >
                    DOCK
                  </ButtonBase>
                </Tooltip>
              </Box>
            )}
            {!toolRailDocked && <>
            <ToolRailSectionHeader id="roi" label="ROI" color="#6eefff" expanded={expandedToolRailSections.roi} onToggle={toggleToolRailSection} statusLabel={activeTool === 'roi' ? 'ACTIVE' : (rois.length ? `${rois.length}` : '')} />
            <ToolRailSectionBody id="roi" expanded={expandedToolRailSections.roi}>
              <Tooltip title="ROI (circular)" placement="right">
                <IconButton
                  size="small"
                  aria-label={activeTool === 'roi' ? 'Switch to scroll tool from ROI' : 'Activate circular ROI tool'}
                  aria-pressed={activeTool === 'roi'}
                  aria-keyshortcuts={profiledToolShortcut('roi') || undefined}
                  aria-disabled={!hasAcceptedClinicalSlice}
                  tabIndex={hasAcceptedClinicalSlice ? 0 : -1}
                  onClick={() => { if (hasAcceptedClinicalSurface()) setLocalTool(t => t === 'roi' ? null : 'roi'); }}
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    display: showDockTool('roi') ? 'inline-flex' : 'none',
                    color: activeTool === 'roi' ? '#4caf50' : '#91a7af',
                    bgcolor: activeTool === 'roi' ? 'rgba(76,175,80,0.1)' : 'transparent',
                    p: 0.4,
                    '&:hover': { bgcolor: 'rgba(76,175,80,0.2)' },
                  }}
                >
                  <RadioButtonUncheckedIcon sx={{ fontSize: 14 }} />
                  <ToolRailHotkeyBadge hotkey={profiledToolShortcut('roi')} active={activeTool === 'roi'} color="#4caf50" />
                </IconButton>
              </Tooltip>
            </ToolRailSectionBody>

            <ToolRailSectionHeader id="measure" label="MEASURE" color="#d8c85a" expanded={expandedToolRailSections.measure} onToggle={toggleToolRailSection} mt={0.2} statusLabel={activeTool === 'measure' ? 'ACTIVE' : (measurements.length ? `${measurements.length}` : '')} />
            <ToolRailSectionBody id="measure" expanded={expandedToolRailSections.measure}>
              <Tooltip title={activeTool === 'measure' ? 'Switch to Scroll' : 'Measure (one-shot)'} placement="right">
                <IconButton
                  size="small"
                  aria-label={activeTool === 'measure' ? 'Switch to scroll tool from measurement' : 'Activate distance measurement tool'}
                  aria-pressed={activeTool === 'measure'}
                  aria-keyshortcuts={profiledToolShortcut('measure') || undefined}
                  aria-disabled={!hasAcceptedClinicalSlice}
                  tabIndex={hasAcceptedClinicalSlice ? 0 : -1}
                  onClick={() => { if (hasAcceptedClinicalSurface()) setLocalTool(t => t === 'measure' ? null : 'measure'); }}
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    display: showDockTool('measure') ? 'inline-flex' : 'none',
                    color: activeTool === 'measure' ? '#ffeb3b' : '#91a7af',
                    bgcolor: activeTool === 'measure' ? 'rgba(255,235,59,0.1)' : 'transparent',
                    p: 0.4,
                    '&:hover': { bgcolor: 'rgba(255,235,59,0.2)' },
                  }}
                >
                  <StraightenIcon sx={{ fontSize: 14 }} />
                  <ToolRailHotkeyBadge hotkey={profiledToolShortcut('measure')} active={activeTool === 'measure'} color="#ffeb3b" />
                </IconButton>
              </Tooltip>
            </ToolRailSectionBody>

            <ToolRailSectionHeader id="annotate" label="ANNOTATE" color="#ffb260" expanded={expandedToolRailSections.annotate} onToggle={toggleToolRailSection} mt={0.2} statusLabel={['arrow', 'text'].includes(activeTool) ? 'ACTIVE' : (annotations.length ? `${annotations.length}` : '')} />
            <ToolRailSectionBody id="annotate" expanded={expandedToolRailSections.annotate}>
              <Tooltip title={activeTool === 'arrow' ? 'Switch to Scroll' : 'Arrow annotation (one-shot)'} placement="right">
                <IconButton
                  size="small"
                  aria-label={activeTool === 'arrow' ? 'Switch to scroll tool from arrow annotation' : 'Activate arrow annotation tool'}
                  aria-pressed={activeTool === 'arrow'}
                  aria-keyshortcuts={profiledToolShortcut('arrow') || undefined}
                  aria-disabled={!hasAcceptedClinicalSlice}
                  tabIndex={hasAcceptedClinicalSlice ? 0 : -1}
                  onClick={() => { if (hasAcceptedClinicalSurface()) setLocalTool(t => t === 'arrow' ? null : 'arrow'); }}
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    display: showDockTool('arrow') ? 'inline-flex' : 'none',
                    color: activeTool === 'arrow' ? '#ff9800' : '#91a7af',
                    bgcolor: activeTool === 'arrow' ? 'rgba(255,152,0,0.1)' : 'transparent',
                    p: 0.4,
                    '&:hover': { bgcolor: 'rgba(255,152,0,0.2)' },
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  <ToolRailHotkeyBadge hotkey={profiledToolShortcut('arrow')} active={activeTool === 'arrow'} color="#ff9800" />
                </IconButton>
              </Tooltip>
              <Tooltip title={activeTool === 'text' ? 'Switch to Scroll' : 'Text label annotation (one-shot)'} placement="right">
                <IconButton
                  size="small"
                  aria-label={activeTool === 'text' ? 'Switch to scroll tool from text annotation' : 'Activate text annotation tool'}
                  aria-pressed={activeTool === 'text'}
                  aria-keyshortcuts={profiledToolShortcut('text') || undefined}
                  aria-disabled={!hasAcceptedClinicalSlice}
                  tabIndex={hasAcceptedClinicalSlice ? 0 : -1}
                  onClick={() => { if (hasAcceptedClinicalSurface()) setLocalTool(t => t === 'text' ? null : 'text'); }}
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    display: showDockTool('text') ? 'inline-flex' : 'none',
                    color: activeTool === 'text' ? '#e040fb' : '#91a7af',
                    bgcolor: activeTool === 'text' ? 'rgba(224,64,251,0.1)' : 'transparent',
                    p: 0.4,
                    '&:hover': { bgcolor: 'rgba(224,64,251,0.2)' },
                  }}
                >
                  <TextFieldsIcon sx={{ fontSize: 14 }} />
                  <ToolRailHotkeyBadge hotkey={profiledToolShortcut('text')} active={activeTool === 'text'} color="#e040fb" />
                </IconButton>
              </Tooltip>
            </ToolRailSectionBody>

            <ToolRailSectionHeader id="draw" label="DRAW" color="#7dd6ff" expanded={expandedToolRailSections.draw} onToggle={toggleToolRailSection} mt={0.2} statusLabel={['select', 'pen', 'rect', 'ellipse', 'angle', 'cobb', 'level'].includes(activeTool) ? 'ACTIVE' : ''} />
            <ToolRailSectionBody id="draw" expanded={expandedToolRailSections.draw}>
              {[
                ['select', 'Select/move annotations; Delete removes selected', 'SEL', '#00e5ff', 'select'],
                ['pen', 'Freehand pen annotation (drag)', 'PEN', '#00e5ff', 'pen'],
                ['rect', 'Rectangle annotation (drag)', '□', '#ffeb3b', 'rect'],
                ['ellipse', 'Ellipse annotation (drag)', '◯', '#66ff66', 'ellipse'],
                ['angle', '3-point angle measurement', '∠', '#ffeb3b', null],
                ['cobb', '4-point Cobb angle skeleton', 'COBB', '#66ff66', null],
                ['level', 'Vertebral level marker', 'LVL', '#e040fb', null],
              ].filter(([tool]) => showDockTool(tool)).map(([tool, title, label, color, shortcutTool]) => {
                const hotkey = shortcutTool ? profiledToolShortcut(shortcutTool) : '';
                return (
                <Tooltip key={tool} title={activeTool === tool ? 'Switch to Scroll' : title} placement="right">
                  <ButtonBase
                    type="button"
                    aria-label={title}
                    aria-pressed={activeTool === tool}
                    aria-keyshortcuts={hotkey || undefined}
                    aria-disabled={!hasAcceptedClinicalSlice}
                    tabIndex={hasAcceptedClinicalSlice ? 0 : -1}
                    className="toolRailDrawButton semantic-draw-tool-button"
                    onClick={() => { if (hasAcceptedClinicalSurface()) setLocalTool(t => t === tool ? null : tool); }}
                    sx={{
                      width: 'var(--kairos-toolrail-button-size, 24px)', height: 'var(--kairos-toolrail-button-size, 24px)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'visible',
                      color: activeTool === tool ? color : '#91a7af', bgcolor: activeTool === tool ? 'rgba(0,188,212,0.10)' : 'transparent',
                      border: '1px solid transparent',
                      fontSize: label.length > 2 ? 'var(--kairos-toolbar-cluster-label-font-size, 7px)' : 'var(--kairos-toolrail-icon-size, 12px)', fontFamily: 'monospace', cursor: 'pointer', borderRadius: '2px',
                      '&:hover': { bgcolor: 'rgba(0,188,212,0.14)', color },
                      '&:focus-visible': { outline: '2px solid rgba(110,239,255,0.85)', outlineOffset: 1, borderColor: 'rgba(110,239,255,0.55)' },
                    }}
                  >
                    {label}
                    {hotkey ? <ToolRailHotkeyBadge hotkey={hotkey} active={activeTool === tool} color={color} /> : null}
                  </ButtonBase>
                </Tooltip>
              );
              })}
              <Box sx={{ display: 'flex', gap: '2px', flexWrap: 'wrap', width: 80, mt: 0.25 }}>
                {ANNOTATION_COLORS.map(color => (
                  <AnnotationAppearanceButton
                    key={color}
                    label={`Use ${color} annotation color`}
                    pressed={annotationStyle.color === color}
                    onClick={() => updateAnnotationStyle({ color })}
                    sx={{ width: 11, height: 11, bgcolor: color, border: annotationStyle.color === color ? '1px solid #fff' : '1px solid #111' }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <AnnotationAppearanceButton label="Decrease annotation line width" onClick={() => updateAnnotationStyle({ lineWidth: Math.max(1, annotationStyle.lineWidth - 1) })} sx={{ color: '#9fb6bd', fontSize: 10 }}>−</AnnotationAppearanceButton>
                <Typography aria-live="polite" sx={{ color: '#9fb6bd', fontSize: 9, fontFamily: 'monospace' }}>W{annotationStyle.lineWidth}</Typography>
                <AnnotationAppearanceButton label="Increase annotation line width" onClick={() => updateAnnotationStyle({ lineWidth: Math.min(6, annotationStyle.lineWidth + 1) })} sx={{ color: '#9fb6bd', fontSize: 10 }}>＋</AnnotationAppearanceButton>
              </Box>
              <Box className="annotationAppearanceControls profile-backed-annotation-appearance" sx={{ display: 'flex', alignItems: 'center', gap: 0.45, width: 80, justifyContent: 'space-between' }}>
                <AnnotationAppearanceButton label="Decrease annotation font size" onClick={() => updateAnnotationStyle({ fontSize: Math.max(8, annotationStyle.fontSize - 1) })} sx={{ color: '#9fb6bd', fontSize: 10 }}>A−</AnnotationAppearanceButton>
                <Typography aria-live="polite" sx={{ color: '#d8fbff', fontSize: 8, fontFamily: cssFontFamilyForAnnotation(annotationStyle.fontFamily) }}>F{annotationStyle.fontSize}</Typography>
                <AnnotationAppearanceButton label="Increase annotation font size" onClick={() => updateAnnotationStyle({ fontSize: Math.min(24, annotationStyle.fontSize + 1) })} sx={{ color: '#9fb6bd', fontSize: 10 }}>A＋</AnnotationAppearanceButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.35, width: 80 }}>
                {['monospace', 'sans', 'serif'].map(family => (
                  <AnnotationAppearanceButton key={family} label={`Use ${family} annotation font`} pressed={annotationStyle.fontFamily === family} onClick={() => updateAnnotationStyle({ fontFamily: family })} sx={{ color: annotationStyle.fontFamily === family ? '#00e5ff' : '#607d8b', fontSize: 7, fontFamily: cssFontFamilyForAnnotation(family) }}>{family === 'monospace' ? 'MONO' : family.toUpperCase()}</AnnotationAppearanceButton>
                ))}
              </Box>
              <AnnotationAppearanceButton label="Toggle annotation labels" pressed={annotationStyle.showLabels} onClick={() => updateAnnotationStyle({ showLabels: !annotationStyle.showLabels })} sx={{ color: annotationStyle.showLabels ? '#00e5ff' : '#9fb6bd', fontSize: 8 }}>LABELS</AnnotationAppearanceButton>
            </ToolRailSectionBody>

            <ToolRailSectionHeader id="status" label="STATUS" color="#607d8b" expanded={expandedToolRailSections.status} onToggle={toggleToolRailSection} mt={0.2} statusLabel={viewportFrozen ? 'LOCK' : (presentationSaveStatus ? 'SAVE' : '')} />
            <ToolRailSectionBody id="status" expanded={expandedToolRailSections.status}>
              <Tooltip title={viewportFrozen ? `Unfreeze active viewport interactions (${profiledToolShortcut('freezeViewport') || 'F'})` : `Freeze active viewport interactions (${profiledToolShortcut('freezeViewport') || 'F'})`} placement="right">
                <IconButton
                  size="small"
                  aria-label={viewportFrozen ? 'Unfreeze viewport' : 'Freeze viewport'}
                  aria-pressed={viewportFrozen}
                  aria-keyshortcuts={profiledToolShortcut('freezeViewport') || undefined}
                  onClick={toggleViewportFrozen}
                  sx={{ display: showDockTool('freezeViewport') ? 'inline-flex' : 'none', color: viewportFrozen ? '#b6ff7a' : '#607d8b', p: 0.4, border: viewportFrozen ? '1px solid rgba(182,255,122,0.45)' : '1px solid rgba(96,125,139,0.18)', bgcolor: viewportFrozen ? 'rgba(182,255,122,0.08)' : 'transparent' }}
                >
                  {viewportFrozen ? <LockIcon sx={{ fontSize: 14 }} /> : <LockOpenIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
              {selectedAnnotationId && (
                <Typography sx={{ color: '#00e5ff', fontSize: 8, fontFamily: 'monospace', width: 74, lineHeight: 1.1 }}>SELECTED: drag handles or style</Typography>
              )}

              <Tooltip title={selectedAnnotationId ? 'Mark selected annotation as an internal key object' : 'Mark current image as an internal key image'} placement="right">
                <ButtonBase
                  type="button"
                  aria-label={selectedAnnotationId ? 'Mark selected object as key object' : 'Mark current image as key image'}
                  aria-pressed={currentKeyObjectMarked}
                  onClick={handleMarkKeyObject}
                  aria-disabled={!series || !hasAcceptedClinicalSlice}
                  tabIndex={series && hasAcceptedClinicalSlice ? 0 : -1}
                  data-kairos-marker="KAIROS_QOL_KEY_IMAGE_MARK_BUTTON"
                  sx={{ width: 80, minHeight: 18, borderRadius: '4px', border: currentKeyObjectMarked ? '1px solid #fff59d' : '1px solid rgba(255,235,59,0.28)', color: currentKeyObjectMarked ? '#1b1500' : '#fff59d', bgcolor: currentKeyObjectMarked ? 'rgba(255,235,59,0.82)' : 'rgba(255,235,59,0.08)', fontSize: 7.5, fontWeight: 900, fontFamily: 'monospace', letterSpacing: 0.8, '&:focus-visible': { outline: '1px solid #fff59d', outlineOffset: 1 } }}
                >
                  {selectedAnnotationId ? 'KEY OBJ' : 'KEY IMG'}
                </ButtonBase>
              </Tooltip>

              {presentationSaveStatus && !presentationSaveAlert && (
                <Typography sx={{ fontSize: 8, color: '#607d8b', px: 0.25, width: 80, lineHeight: 1.1 }}>
                  {presentationSaveStatus}
                </Typography>
              )}
            </ToolRailSectionBody>

            {presentationSaveAlert && (
              <Box
                className="presentationSaveClinicalAlert"
                role="alert"
                aria-live="assertive"
                data-testid="presentation-save-clinical-alert"
                sx={{ width: 80, border: '1px solid rgba(255,138,128,0.46)', borderRadius: '6px', bgcolor: 'rgba(58,8,8,0.78)', boxShadow: '0 0 12px rgba(255,82,82,0.18)', px: 0.35, py: 0.25 }}
              >
                <Typography sx={{ color: '#ffb3ab', fontSize: 8, fontFamily: 'monospace', fontWeight: 800, lineHeight: 1.1 }}>
                  SAVE ISSUE
                </Typography>
                <Typography title={presentationSaveAlert} sx={{ color: '#ff8a80', fontSize: 7.5, lineHeight: 1.1, maxHeight: 28, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {presentationSaveAlert}
                </Typography>
              </Box>
            )}

            {(selectedMarkup || annotations.length > 0 || measurements.length > 0 || rois.length > 0) && (
              <Box data-testid="tool-rail-clear-actions" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pt: 0.25, borderTop: '1px solid rgba(96,125,139,0.24)' }}>
                {selectedMarkup && (
                  <Tooltip title="Delete selected markup" placement="right">
                    <IconButton size="small" aria-label="Delete selected markup" aria-disabled={!hasAcceptedClinicalSlice} tabIndex={hasAcceptedClinicalSlice ? 0 : -1} onClick={() => deleteMarkupTarget(selectedMarkup)} sx={{ color: '#f44336', p: 0.4 }}>
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {annotations.length > 0 && (
                  <Tooltip title="Clear arrows/text" placement="right">
                    <IconButton
                      size="small"
                      aria-label="Clear arrow and text annotations"
                      aria-disabled={!hasAcceptedClinicalSlice}
                      tabIndex={hasAcceptedClinicalSlice ? 0 : -1}
                      onClick={() => { if (!hasAcceptedClinicalSurface()) return; setAnnotations([]); setPendingArrow(null); setTextPopover(null); }}
                      sx={{ color: '#555', p: 0.4, '&:hover': { color: '#f44336' } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {measurements.length > 0 && (
                  <Tooltip title="Clear measurements" placement="right">
                    <IconButton
                      size="small"
                      aria-label="Clear distance measurements"
                      aria-disabled={!hasAcceptedClinicalSlice}
                      tabIndex={hasAcceptedClinicalSlice ? 0 : -1}
                      onClick={() => { if (!hasAcceptedClinicalSurface()) return; setMeasurements([]); setPendingMeasure(null); }}
                      sx={{ color: '#555', p: 0.4, '&:hover': { color: '#f44336' } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {rois.length > 0 && (
                  <Tooltip title="Clear ROIs" placement="right">
                    <IconButton
                      size="small"
                      aria-label="Clear ROI measurements"
                      aria-disabled={!hasAcceptedClinicalSlice}
                      tabIndex={hasAcceptedClinicalSlice ? 0 : -1}
                      onClick={() => { if (!hasAcceptedClinicalSurface()) return; setRois([]); setPendingRoi(null); }}
                      sx={{ color: '#555', p: 0.4, '&:hover': { color: '#f44336' } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            </>}
          </Box>
          </ViewportToolRailRenderBoundary>

          {viewportFrozen && (
            <Box className="viewportFreezeSentinel" sx={{ position: 'absolute', top: 6, right: 6, zIndex: 36, pointerEvents: 'none', px: 0.75, py: 0.35, border: '1px solid rgba(182,255,122,0.42)', borderRadius: 1.5, bgcolor: 'rgba(4,12,10,0.76)', boxShadow: '0 0 14px rgba(182,255,122,0.16)' }}>
              <Typography sx={{ color: '#b6ff7a', fontSize: 9, fontFamily: 'monospace', fontWeight: 800, letterSpacing: 1.1 }}>FROZEN · F</Typography>
            </Box>
          )}

          {isViewportMaximized && (
            <Box
              data-full-viewport-restore-badge={VIEWPORT_FULL_RESTORE_RIGHT_PLACEMENT_MARKER}
              sx={{
                position: 'absolute',
                top: { xs: 34, xl: 10 },
                left: { xs: '65%', md: '67%', xl: '69%' },
                transform: 'translateX(-50%)',
                zIndex: 36,
                pointerEvents: 'none',
                maxWidth: 'min(250px, 28%)',
                px: 0.85,
                py: 0.3,
                border: '1px solid rgba(110,239,255,0.38)',
                borderRadius: 1.5,
                bgcolor: 'rgba(3,16,24,0.72)',
                boxShadow: '0 0 14px rgba(0,188,212,0.16)',
              }}
            >
              <Typography sx={{ color: '#9befff', fontSize: 9, fontFamily: 'monospace', fontWeight: 900, letterSpacing: 1.2 }}>FULL VP{index + 1} · DOUBLE-CLICK RESTORE</Typography>
            </Box>
          )}

          {petThicknessEligible && (
            <Box data-kairos-marker={PET_SLICE_THICKNESS_MARKER} sx={{ position: 'absolute', top: 30, right: 6, zIndex: 36, pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 0.45, px: 0.55, py: 0.25, border: '1px solid rgba(255,204,128,0.36)', borderRadius: 1, bgcolor: 'rgba(16,10,2,0.72)', boxShadow: '0 0 12px rgba(255,152,0,0.12)' }} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
              <Typography component="label" htmlFor={`pet-slice-thickness-${index}`} sx={{ color: '#ffcc80', fontSize: 8, fontFamily: 'monospace', fontWeight: 900, letterSpacing: 0.8, whiteSpace: 'nowrap' }}>PT AVG SLAB</Typography>
              <Tooltip title={PET_SLICE_THICKNESS_HELP_TEXT} placement="bottom" arrow>
                <Typography
                  component="button"
                  type="button"
                  data-kairos-marker={PET_SLICE_THICKNESS_HELP_MARKER}
                  aria-label={PET_SLICE_THICKNESS_HELP_TEXT}
                  sx={{
                    appearance: 'none',
                    p: 0,
                    m: 0,
                    width: 13,
                    height: 13,
                    lineHeight: '13px',
                    borderRadius: '50%',
                    textAlign: 'center',
                    color: '#2a1500',
                    bgcolor: 'rgba(255,204,128,0.92)',
                    border: '1px solid rgba(255,236,179,0.92)',
                    fontSize: 9,
                    fontFamily: 'monospace',
                    fontWeight: 900,
                    cursor: 'help',
                    boxShadow: '0 0 8px rgba(255,152,0,0.22)',
                    userSelect: 'none',
                    flex: '0 0 auto',
                    '&:focus-visible': {
                      outline: '1px solid #fff3c4',
                      outlineOffset: 1,
                    },
                  }}
                >
                  ?
                </Typography>
              </Tooltip>
              <Box
                component="select"
                id={`pet-slice-thickness-${index}`}
                aria-label={`PET average-slab display thickness for viewport ${index + 1}; native ${petNativeSliceThickness} millimeters`}
                value={petSliceThicknessMm}
                onChange={handlePetSliceThicknessChange}
                sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: '#ffe0b2', border: '1px solid rgba(255,204,128,0.42)', borderRadius: '3px', fontSize: 9, fontFamily: 'monospace', height: 18, outline: 'none' }}
              >
                {PET_SLICE_THICKNESS_OPTIONS_MM.map(mm => <option key={mm} value={mm}>{mm} mm</option>)}
              </Box>
            </Box>
          )}

          {/* Active tool badge */}
          <Box sx={{ position: 'absolute', bottom: 32, right: 4, zIndex: 30, pointerEvents: 'none' }}>
            <Typography sx={{
              fontSize: 8, letterSpacing: 1, color: toolColor,
              textShadow: '0 0 4px #000',
            }}>
              {toolBadge}
            </Typography>
          </Box>

          {/* Main image area */}
          <Box
            ref={bindViewportImageArea}
            data-testid={`diagnostic-viewport-image-area-${index}`}
            data-viewport-interactive-projected-preload={VIEWPORT_INTERACTIVE_PROJECTED_PRELOAD_MARKER}
            sx={{
              flex: 1, minWidth: 0, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: activeTool === 'measure'
                ? (pendingMeasure ? 'crosshair' : 'cell')
                : activeTool === 'arrow'
                  ? (pendingArrow ? 'crosshair' : 'cell')
                  : activeTool === 'text' || activeTool === 'level'
                    ? 'text'
                    : activeTool === 'select'
                      ? 'move'
                      : ['pen', 'rect', 'ellipse', 'angle', 'cobb'].includes(activeTool)
                        ? 'crosshair'
                        : activeTool === 'roi'
                      ? 'crosshair'
                      : activeTool === 'wl' ? (dragging ? 'ew-resize' : 'crosshair') : (dragging ? 'ns-resize' : 'ns-resize'),
              overflow: 'hidden', position: 'relative', isolation: 'isolate',
            }}
            onMouseDown={(e) => { if (typeof onViewportFocus === 'function') onViewportFocus(); handleMouseDown(e); }}
            onPointerDown={handleTriangulationMiddlePointerDown}
            onPointerMove={handleTriangulationMiddlePointerMove}
            onPointerUp={finishTriangulationMiddlePointerDrag}
            onPointerCancel={(e) => finishTriangulationMiddlePointerDrag(e, { cancelled: true })}
            onDoubleClick={handleViewportDoubleClick}
            onAuxClick={(e) => { if (shouldPreventTriangulationAuxClick(e)) e.preventDefault(); }}
            onContextMenu={contextMenuEnabled ? openWindowLevelContextMenu : undefined}
          >
            {loading && (
              <Box sx={{ position: 'absolute', zIndex: 5 }}>
                <CircularProgress size={16} thickness={2} sx={{ color: '#00bcd4', opacity: 0.5 }} />
              </Box>
            )}
            {imgError ? (
              <Typography sx={{ color: '#444', fontSize: 11 }}>Failed to load image</Typography>
            ) : (imgSrcRef.current || imgSrc) && (
              <img
                ref={imgRef}
                src={imgSrcRef.current || imgSrc}
                alt="DICOM"
                draggable={false}
                onLoad={(e) => {
                  if (!isExpectedViewportImageSource(imgSrcRef.current || imgSrc, e.currentTarget?.src, window.location.href)) return;
                  if (settlePendingVisibleDom(true, e.currentTarget?.src) !== true) return;
                  const { naturalWidth, naturalHeight } = e.target;
                  const ns = { width: naturalWidth, height: naturalHeight };
                  setNaturalSize(previous => previous?.width === ns.width && previous?.height === ns.height ? previous : ns);
                  naturalSizeRef.current = ns;  // sync update so measure clicks work immediately
                  scrubDisplayPendingRef.current = false;
                  hideScrubPreview();
                  setLoading(false);
                  window.requestAnimationFrame(refreshOverlayLayout);
                  if (scrubActiveRef.current && scrubPendingSliceRef.current != null) {
                    scheduleScrubSliceLoad(scrubPendingSliceRef.current, { allowColdDisplay: true });
                  }
                }}
                onError={(e) => {
                  if (!isExpectedViewportImageSource(imgSrcRef.current || imgSrc, e.currentTarget?.src, window.location.href)) return;
                  const settlement = settlePendingVisibleDom(false, e.currentTarget?.src);
                  if (!settlement) return;
                  scrubDisplayPendingRef.current = false;
                  setLoading(false);
                  if (settlement === 'failed-empty') {
                    hideScrubPreview();
                    setImgError(true);
                  }
                  if (scrubActiveRef.current && scrubPendingSliceRef.current != null) scheduleScrubSliceLoad(scrubPendingSliceRef.current, { allowColdDisplay: true });
                }}
                style={{
                  // Drive the image with the same ResizeObserver-backed CSS size as
                  // the annotation canvas. The browser normally handles width/height
                  // 100%, but explicit pixels force a repaint/re-fit when any viewport
                  // grows/shrinks (browser maximize/fullscreen, grid/protocol changes)
                  // for every modality, not just the CT layouts where this was first
                  // noticed.
                  width: canvasCssSize.width ? `${canvasCssSize.width}px` : '100%',
                  height: canvasCssSize.height ? `${canvasCssSize.height}px` : '100%',
                  maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block',
                  position: 'relative', zIndex: 1, pointerEvents: 'none',
                  transform: zoom !== 1.0 ? `scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)` : undefined,
                  transformOrigin: 'center center',
                }}
              onDoubleClick={() => { setZoom(1.0); setPanOffset({ x: 0, y: 0 }); }}
              />
            )}
            <canvas
              ref={scrubPreviewCanvasRef}
              data-viewport-scrub-preview-cache="true"
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 4,
                display: 'none',
                pointerEvents: 'none',
              }}
            />

            {/* DOM/SVG annotation overlay (visible annotations live here, not on canvas) */}
            {overlayImgRect && overlayW > 0 && overlayH > 0 && (
              <Box
                sx={{
                  position: 'absolute', top: 0, left: 0,
                  width: `${overlayW}px`, height: `${overlayH}px`,
                  pointerEvents: 'none', zIndex: 110,
                  overflow: 'hidden',
                }}
              >
                <svg width={overlayW} height={overlayH} style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible' }}>
                  <defs>
                    <marker id={`arrow-head-${index}`} markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
                      <path d="M0,0 L0,6 L9,3 z" fill="#ff9800" />
                    </marker>
                    <marker id={`arrow-head-pending-${index}`} markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
                      <path d="M0,0 L0,6 L9,3 z" fill="#ffeb3b" />
                    </marker>
                  </defs>
                  {[...visibleAnnotations.filter(a => a.type === 'arrow'), ...(pendingArrow ? [{ id: 'pending-arrow', type: 'arrow', points: pendingArrow, pending: true, color: '#ffeb3b' }] : [])].map((ann) => {
                    const a = overlayPoint(ann.points.a);
                    const b = overlayPoint(ann.points.b);
                    const color = ann.pending ? '#ffeb3b' : (ann.color || '#ff9800');
                    return (
                      <g key={ann.id}>
                        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="rgba(0,0,0,0.8)" strokeWidth="4" markerEnd={`url(#arrow-head-${ann.pending ? 'pending-' : ''}${index})`} />
                        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={color} strokeWidth={ann.id === selectedAnnotationId ? 3.5 : (ann.lineWidth || 2)} strokeDasharray={ann.pending || ann.id === selectedAnnotationId ? '4 2' : undefined} markerEnd={`url(#arrow-head-${ann.pending ? 'pending-' : ''}${index})`} />
                      </g>
                    );
                  })}

                  {[...visibleAnnotations.filter(a => ['freehand', 'rect', 'ellipse', 'angle', 'cobb'].includes(a.type)), ...pendingShapeAnnotations].map((ann) => {
                    const color = ann.pending ? '#ffeb3b' : (ann.color || annotationStyle.color || '#ff9800');
                    const sw = (ann.lineWidth || 2) + (ann.id === selectedAnnotationId ? 1.5 : 0);
                    const pts = Array.isArray(ann.points) ? ann.points : [];
                    const screenPts = pts.map(overlayPoint);
                    const selected = ann.id === selectedAnnotationId;
                    if (ann.type === 'freehand') {
                      const d = screenPts.map((p, i) => `${i ? 'L' : 'M'} ${p.x} ${p.y}`).join(' ');
                      return <path key={ann.id} d={d} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={ann.pending ? '4 2' : undefined} />;
                    }
                    if ((ann.type === 'rect' || ann.type === 'ellipse') && screenPts.length >= 2) {
                      const [a, b] = screenPts;
                      const x = Math.min(a.x, b.x); const y = Math.min(a.y, b.y); const w = Math.abs(b.x - a.x); const h = Math.abs(b.y - a.y);
                      return ann.type === 'rect'
                        ? <rect key={ann.id} x={x} y={y} width={w} height={h} fill="none" stroke={color} strokeWidth={sw} strokeDasharray={ann.pending || selected ? '4 2' : undefined} />
                        : <ellipse key={ann.id} cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} fill="none" stroke={color} strokeWidth={sw} strokeDasharray={ann.pending || selected ? '4 2' : undefined} />;
                    }
                    if (ann.type === 'angle' && screenPts.length >= 1) {
                      const label = formatAngleMeasurementLabel(ann);
                      const angleLabelLayout = label && screenPts.length >= 3 ? angleMeasurementLabelLayout({
                        points: screenPts,
                        label,
                        fontSize: ann.labelFontSize || overlayMeasurementFontSize,
                        viewportWidth: overlayW,
                        viewportHeight: overlayH,
                      }) : null;
                      return (
                        <g key={ann.id}>
                          {screenPts.length >= 2 && <line x1={screenPts[1].x} y1={screenPts[1].y} x2={screenPts[0].x} y2={screenPts[0].y} stroke={color} strokeWidth={sw} strokeDasharray={ann.pending ? '4 2' : undefined} />}
                          {screenPts.length >= 3 && <line x1={screenPts[1].x} y1={screenPts[1].y} x2={screenPts[2].x} y2={screenPts[2].y} stroke={color} strokeWidth={sw} />}
                          {screenPts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="2.5" fill={color} />)}
                          {angleLabelLayout && (
                            <g data-kairos-marker="KAIROS_ANGLE_LABEL_CLEARANCE_PILL">
                              <rect
                                x={angleLabelLayout.boxX}
                                y={angleLabelLayout.boxY}
                                width={angleLabelLayout.boxWidth}
                                height={angleLabelLayout.boxHeight}
                                rx="2"
                                fill={measurementCalculationBackdrop}
                              />
                              <text
                                x={angleLabelLayout.textX}
                                y={angleLabelLayout.textY}
                                fontSize={ann.labelFontSize || overlayMeasurementFontSize}
                                fontFamily={cssFontFamilyForAnnotation(ann.fontFamily || annotationStyle.fontFamily)}
                                fill={color}
                                stroke="rgba(0,0,0,0.85)"
                                strokeWidth="1.3"
                                paintOrder="stroke"
                              >{label}</text>
                            </g>
                          )}
                        </g>
                      );
                    }
                    if (ann.type === 'cobb' && screenPts.length >= 1) {
                      const label = pts.length >= 4 ? cobbAngle(pts) : null;
                      const lx = screenPts.reduce((sum, p) => sum + p.x, 0) / screenPts.length;
                      const ly = screenPts.reduce((sum, p) => sum + p.y, 0) / screenPts.length;
                      return (
                        <g key={ann.id}>
                          {screenPts.length >= 2 && <line x1={screenPts[0].x} y1={screenPts[0].y} x2={screenPts[1].x} y2={screenPts[1].y} stroke={color} strokeWidth={sw} strokeDasharray={ann.pending ? '4 2' : undefined} />}
                          {screenPts.length >= 4 && <line x1={screenPts[2].x} y1={screenPts[2].y} x2={screenPts[3].x} y2={screenPts[3].y} stroke={color} strokeWidth={sw} />}
                          {screenPts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="2.5" fill={color} />)}
                          {label != null && (ann.showLabels ?? true) && <text x={lx + 6} y={ly - 6} fontSize={ann.labelFontSize || overlayMeasurementFontSize} fontFamily={cssFontFamilyForAnnotation(ann.fontFamily || annotationStyle.fontFamily)} fill={color} stroke="rgba(0,0,0,0.85)" strokeWidth="1.3" paintOrder="stroke">Cobb {label.toFixed(1)}°</text>}
                        </g>
                      );
                    }
                    return null;
                  })}
                  {hasAcceptedClinicalSlice && (
                    <ReferenceLineLayer
                      channel={referenceGeometryChannel}
                      enabled={showRefLines}
                      viewportIndex={index}
                      studyInstanceUID={studyInstanceUID}
                      currentSliceGeometry={currentSliceGeometry}
                      naturalSize={naturalSize}
                      overlayImgRect={overlayImgRect}
                      overlayW={overlayW}
                      overlayH={overlayH}
                    />
                  )}
                  {hasAcceptedClinicalSlice && (
                    <TriangulationCrosshairLayer
                      channel={triangulationDragChannel}
                      viewportIndex={index}
                      series={series}
                      overlayImgRect={overlayImgRect}
                      overlayW={overlayW}
                      overlayH={overlayH}
                      currentSliceGeometry={currentSliceGeometry}
                      naturalSize={naturalSize}
                      anchorPoint={triangulationPoint}
                    />
                  )}
                  {visibleMeasurements.map((m) => {
                    const a = overlayPoint(m.a);
                    const b = overlayPoint(m.b);
                    const label = m.pending ? '' : formatMeasureLabel(m);
                    const fontSize = m.labelFontSize || overlayMeasurementFontSize;
                    const lineWidth = m.lineWidth || 1.4;
                    const selected = selectedMarkup?.kind === 'measurement' && selectedMarkup.id === m.id;
                    const labelLayout = label ? linearMeasurementLabelLayout({
                      a,
                      b,
                      label,
                      fontSize,
                      viewportWidth: overlayW,
                      viewportHeight: overlayH,
                    }) : null;
                    const color = m.pending ? '#ffeb3b' : (m.color || '#00e5ff');
                    return (
                      <g key={m.id || `${a.x}-${a.y}-${b.x}-${b.y}`} data-markup-kind="measurement" data-markup-selected={selected ? 'true' : 'false'}>
                        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="rgba(0,0,0,0.75)" strokeWidth={lineWidth + 1.6} />
                        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={color} strokeWidth={lineWidth + (selected ? 1 : 0)} strokeDasharray={m.pending || selected ? '4 2' : undefined} />
                        <circle cx={a.x} cy={a.y} r={selected ? 3.4 : 2.3} fill={color} />
                        <circle cx={b.x} cy={b.y} r={selected ? 3.4 : 2.3} fill={color} />
                        {labelLayout && (
                          <g data-kairos-marker="KAIROS_MEASUREMENT_LABEL_CLEARANCE_PILL">
                            <rect
                              x={labelLayout.boxX}
                              y={labelLayout.boxY}
                              width={labelLayout.boxWidth}
                              height={labelLayout.boxHeight}
                              rx="2"
                              fill={measurementCalculationBackdrop}
                            />
                            <text
                              x={labelLayout.textX}
                              y={labelLayout.textY}
                              fontSize={fontSize}
                              fontFamily={cssFontFamilyForAnnotation(m.fontFamily || annotationStyle.fontFamily)}
                              fill={color}
                              stroke="rgba(0,0,0,0.85)"
                              strokeWidth="1.1"
                              paintOrder="stroke"
                            >{label}</text>
                          </g>
                        )}
                      </g>
                    );
                  })}
                  {visibleAnnotations.filter(a => a.type === 'text' || a.type === 'level').map((ann) => {
                    const p = overlayPoint(ann.position);
                    return (
                      <text
                        key={ann.id}
                        x={p.x + 4}
                        y={p.y - 4}
                        fontSize={ann.fontSize || overlayMeasurementFontSize}
                        fontFamily={cssFontFamilyForAnnotation(ann.fontFamily || annotationStyle.fontFamily)}
                        fill={ann.color || (ann.type === 'level' ? '#ffeb3b' : '#e040fb')}
                        stroke="rgba(0,0,0,0.9)"
                        strokeWidth={ann.lineWidth || 2}
                        paintOrder="stroke"
                      >{ann.type === 'level' ? `⬥ ${ann.text}` : ann.text}</text>
                    );
                  })}
                  {visibleAnnotations.filter(a => a.id === selectedAnnotationId).flatMap((ann) =>
                    getAnnotationHandlePoints(ann).map((h) => {
                      const p = overlayPoint(h.pt);
                      const isRectangularHandle = ['rect', 'ellipse'].includes(ann.type);
                      return isRectangularHandle ? (
                        <rect
                          key={`${ann.id}-${h.key}-handle`}
                          x={p.x - 4}
                          y={p.y - 4}
                          width="8"
                          height="8"
                          fill="#00e5ff"
                          stroke="rgba(0,0,0,0.9)"
                          strokeWidth="1.5"
                        />
                      ) : (
                        <circle
                          key={`${ann.id}-${h.key}-handle`}
                          cx={p.x}
                          cy={p.y}
                          r="4"
                          fill="#00e5ff"
                          stroke="rgba(0,0,0,0.9)"
                          strokeWidth="1.5"
                        />
                      );
                    })
                  )}
                </svg>
                {visibleRois.map((roi, i) => {
                  const c = roiOverlayCircle(roi);
                  if (!c || c.r <= 0) return null;
                  const labelLines = !roi.pending ? roiStatsLines(roi.stats) : [];
                  const selected = selectedMarkup?.kind === 'roi' && selectedMarkup.id === roi.id;
                  const lineWidth = roi.lineWidth || 1.5;
                  const color = roi.color || '#66ff66';
                  const fontSize = roi.labelFontSize || Math.max(9, overlayMeasurementFontSize - 1);
                  return (
                    <Box key={roi.id || i} data-markup-kind="roi" data-markup-selected={selected ? 'true' : 'false'}>
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${c.cx - c.r}px`, top: `${c.cy - c.r}px`,
                          width: `${c.r * 2}px`, height: `${c.r * 2}px`,
                          border: `${lineWidth + (selected ? 1 : 0)}px ${roi.pending || selected ? 'dashed' : 'solid'} ${color}`,
                          borderRadius: '50%',
                          boxShadow: selected ? `0 0 0 1px rgba(0,0,0,0.65), 0 0 8px ${color}` : '0 0 0 1px rgba(0,0,0,0.45)',
                          boxSizing: 'border-box',
                        }}
                      />
                      {selected && ['east', 'west', 'north', 'south'].map(direction => {
                        const handle = direction === 'east' ? { x: c.cx + c.r, y: c.cy }
                          : direction === 'west' ? { x: c.cx - c.r, y: c.cy }
                            : direction === 'north' ? { x: c.cx, y: c.cy - c.r }
                              : { x: c.cx, y: c.cy + c.r };
                        return (
                          <Box
                            key={`${roi.id}-${direction}-radius-handle`}
                            data-roi-resize-handles="cardinal"
                            data-roi-resize-handle={direction}
                            sx={{
                              position: 'absolute',
                              left: `${handle.x - 4}px`, top: `${handle.y - 4}px`,
                              width: '8px', height: '8px',
                              borderRadius: '50%',
                              backgroundColor: color,
                              border: '1.5px solid rgba(0,0,0,0.9)',
                              boxShadow: `0 0 5px ${color}`,
                              boxSizing: 'border-box',
                            }}
                          />
                        );
                      })}
                      {labelLines.length > 0 && (
                        <Box
                          component="span"
                          sx={{
                            position: 'absolute',
                            left: `${Math.min(c.cx + c.r + 2, overlayW - 44)}px`,
                            top: `${Math.max(c.cy - 9, 0)}px`,
                            px: '2px', py: '1px',
                            fontFamily: cssFontFamilyForAnnotation(roi.fontFamily || annotationStyle.fontFamily), fontSize: `${fontSize}px`,
                            lineHeight: 1.05, color,
                            backgroundColor: measurementCalculationBackdrop,
                            borderRadius: '2px',
                            textShadow: '0 0 2px #000, 0 0 2px #000',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {labelLines.map((line, idx) => <Box key={idx} component="span" sx={{ display: 'block' }}>{line}</Box>)}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {hasAcceptedClinicalSlice && (
              <TriangulationSourceMarkerLayer
                channel={triangulationDragChannel}
                viewportIndex={index}
                series={series}
                overlayImgRect={overlayImgRect}
                overlayW={overlayW}
                currentSliceGeometry={currentSliceGeometry}
                naturalSize={naturalSize}
                anchorPoint={triangulationPoint}
                viewportFrozen={viewportFrozen}
                hasAcceptedClinicalSlice={hasAcceptedClinicalSlice}
                onPointerDown={handleTriangulationMarkerPointerDown}
                onAuxClick={handleTriangulationMarkerAuxClick}
                onPointerMove={handleTriangulationMarkerPointerMove}
                onPointerUp={handleTriangulationMarkerPointerUp}
                onPointerCancel={cancelTriangulationMarkerPointerDrag}
              />
            )}

            {/* Canvas overlay for measurements */}
            <canvas
              ref={canvasRef}
              data-contextual-annotation-selection="KAIROS_CONTEXTUAL_ANNOTATION_SELECTION"
              data-markup-kind={selectedMarkup?.kind || ''}
              style={{
                position: 'absolute', top: 0, left: 0,
                display: 'block',
                width: canvasCssSize.width ? `${canvasCssSize.width}px` : '100%',
                height: canvasCssSize.height ? `${canvasCssSize.height}px` : '100%',
                pointerEvents: hasAcceptedClinicalSlice && (isAnnotationTool || visibleAnnotations.length > 0 || visibleMeasurements.length > 0 || visibleRois.length > 0) ? 'auto' : 'none',
                touchAction: 'none',
                zIndex: 100,
                cursor: hoveredAnnotationId?.endsWith(':radius') ? 'nwse-resize' : hoveredAnnotationId ? 'move' : activeTool === 'measure'
                  ? (pendingMeasure ? 'crosshair' : 'cell')
                  : activeTool === 'arrow'
                    ? (pendingArrow ? 'crosshair' : 'cell')
                    : activeTool === 'text' || activeTool === 'level'
                      ? 'text'
                      : activeTool === 'select'
                        ? 'move'
                        : ['pen', 'rect', 'ellipse', 'angle', 'cobb'].includes(activeTool)
                          ? 'crosshair'
                          : activeTool === 'roi'
                        ? 'crosshair'
                        : 'default',
              }}
              onClick={handleCanvasClick}
              onAuxClick={(e) => { if (shouldPreventTriangulationAuxClick(e)) e.preventDefault(); }}
              onContextMenu={handleCanvasContextMenu}
              onPointerDown={(e) => {
                if (handleTriangulationMiddlePointerDown(e)) return;
                if (e.button !== 0) return;
                if (beginContextualAnnotationDrag(e)) return;
                if (activeTool === 'measure' || activeTool === 'arrow' || activeTool === 'text' || activeTool === 'level' || activeTool === 'angle' || activeTool === 'cobb') e.stopPropagation();
                if (activeTool === 'roi') handleCanvasMouseDown_ROI(e);
                if (['pen', 'rect', 'ellipse'].includes(activeTool)) handleAnnotationPointerDown(e);
              }}
              onPointerMove={(e) => {
                if (handleTriangulationMiddlePointerMove(e)) return;
                if (annotationDragRef.current?.id) {
                  handleCanvasMouseMove(e);
                  return;
                }
                handleContextualAnnotationHover(e);
                if (activeTool === 'measure' || activeTool === 'arrow') handleCanvasMouseMove(e);
                if (activeTool === 'roi') handleCanvasMouseMove_ROI(e);
                if (['pen', 'rect', 'ellipse'].includes(activeTool)) handleAnnotationPointerMove(e);
              }}
              onPointerLeave={() => setHoveredAnnotationId(null)}
              onPointerUp={(e) => {
                if (finishTriangulationMiddlePointerDrag(e)) return;
                if (annotationDragRef.current?.id) {
                  finishAnnotationDrag(e);
                  return;
                }
                if (activeTool === 'roi') handleCanvasMouseUp_ROI(e);
                if (['pen', 'rect', 'ellipse'].includes(activeTool)) finishAnnotationDrag(e);
              }}
              onPointerCancel={(e) => {
                if (finishTriangulationMiddlePointerDrag(e, { cancelled: true })) return;
                if (activeTool === 'roi') { roiStartRef.current = null; setPendingRoi(null); }
                cancelAnnotationDrag();
                suppressNextCanvasClickRef.current = false;
                setHoveredAnnotationId(null);
                if (activeTool === 'pen') setPendingFreehand(null);
                if (activeTool === 'rect') setPendingRect(null);
                if (activeTool === 'ellipse') setPendingEllipse(null);
                if (activeTool === 'arrow') setPendingArrow(null);
              }}
              onLostPointerCapture={() => cancelAnnotationDrag()}
            />
          </Box>
            {/* Text annotation popover */}
            {textPopover && (
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: 'fixed', top: textPopover.y, left: textPopover.x, zIndex: 120,
                  bgcolor: '#1a1a1a', border: '1px solid #333', borderRadius: 1, p: 1,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.8)', minWidth: 180,
                }}
              >
                <Typography sx={{ color: '#888', fontSize: 10, mb: 0.75, letterSpacing: 1 }}>{textPopover.mode === 'level' ? 'VERTEBRAL LEVEL' : 'TEXT LABEL'}</Typography>
                <input
                  autoFocus
                  placeholder={textPopover.mode === 'level' ? 'e.g. L4 / T12' : 'Label text'}
                  style={{ width: '100%', background: '#111', border: '1px solid #333', color: '#e0e0e0', padding: '4px 8px', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (!hasAcceptedClinicalSurface()) { setTextPopover(null); return; }
                      const text = e.target.value.trim();
                      if (text) {
                        setAnnotations(prev => [...prev, {
                          id: uid(),
                          type: textPopover.mode === 'level' ? 'level' : 'text',
                          slice: sliceRef.current,
                          position: textPopover.pt,
                          text,
                          color: textPopover.mode === 'level' ? '#ffeb3b' : (annotationStyle.color || '#e040fb'),
                          lineWidth: annotationStyle.lineWidth,
                          fontSize: annotationStyle.fontSize,
                          fontFamily: annotationStyle.fontFamily,
                          showLabels: annotationStyle.showLabels,
                        }]);
                      }
                      setTextPopover(null);
                      if (text) completeToolInteraction();
                    }
                    if (e.key === 'Escape') setTextPopover(null);
                  }}
                />
              </Box>
            )}
            {/* Per-item annotation, measurement, and ROI property editor */}
            <ClinicalMarkupPropertiesPopover
              anchor={propertyPopover?.anchor || null}
              target={propertyPopover?.target || null}
              onPreview={applyPropertyPopoverPatch}
              onApply={closePropertyPopover}
              onCancel={cancelPropertyPopover}
              onDelete={deletePropertyPopoverTarget}
            />

            {/* W/L Preset context menu */}
            <WLContextMenu
              anchorPos={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : null}
              onClose={() => setCtxMenu(null)}
              onContextMenu={openWindowLevelContextMenu}
              contextTools={contextTools}
              onToolSelect={(toolId) => { if (!viewportFrozen && hasAcceptedClinicalSurface()) setLocalTool(toolId); }}
              onSelect={(preset) => {
                if (viewportFrozen || !acceptedVisibleSurfaceRef.current || !Number.isFinite(sliceRef.current)) return;
                const wl = normalizeViewportWindowLevel({ ...preset, source: 'manual' });
                windowLevelSourceRef.current = 'manual';
                setWindowLevel(wl);
                wlRef.current = wl;
                setWlDraft(formatWindowLevelDraft(wl));
                notifyWindowLevelChange(wl);
                loadSlice(sliceRef.current, wl);
              }}
              onZoom={(z) => {
                if (viewportFrozen) return;
                setZoom(z);
                setPanOffset({ x: 0, y: 0 });
              }}
            />

            {zoom !== 1.0 && (
              <Box sx={{ position: 'absolute', bottom: 56, right: 6, zIndex: 30, pointerEvents: 'none' }}>
                <Typography sx={{ color: '#00bcd4', fontSize: 9, fontFamily: 'monospace' }}>
                  {Math.round(zoom * 100)}%
                </Typography>
              </Box>
            )}

          {/* Slice slider */}
          <Box
            data-kairos-marker={VIEWPORT_SCRUB_LATEST_ONLY_MARKER}
            data-viewport-scrub-latest-only="true"
            sx={{ px: 1.5, pb: 0.5, pt: 0, bgcolor: '#050505' }}
          >
            <ViewportSliceSlider
              ref={sliceSliderRef}
              value={slice}
              maxSlice={maxSlice}
              disabled={viewportFrozen || !hasAcceptedClinicalSlice}
              onLiveChange={handleSliceSliderLiveChange}
              onCommit={handleSliceSliderCommit}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export { ITEM_TYPE };
export default Viewport;
